import axios from 'axios'
import { getStoredPreviewAccessKey, PREVIEW_ACCESS_HEADER } from '../api/client'
import { getStoredMarketingVisitorId } from '../analytics/marketingVisitorStorage'
import { resolveMediaUrlAgainstOrigin } from './mediaUrlCore'
import { attachExpectedSessionUser, reportSessionIdentityChanged } from './sessionIdentity'
import type {
  CanvasEdgeDto,
  CanvasNodeDto,
  CanvasViewportDto,
  ProductionCanvasDto,
} from './workspace/contentProduction/canvas/types'
import type {
  AdminCreditAdjustmentResult,
  AdminCreditReversalResult,
  AdminOverview,
  AiUsageResponse,
  PaymentHistoryResponse,
  TenantAnalytics,
  TenantListQueryParams,
  TenantListResponse,
  AiPricingEntryRow,
  AiPricingListResponse,
  AiPricingReferenceConfig,
  MarketingAnalyticsOverview,
  AdminPlanUpgradeResult,
  PlanCatalogRow,
  TenantMembershipCatalog,
  RateCatalogEntryRow,
  RateRuleRow,
  WorkspaceDetail,
  BillingPlan,
  BillingPlanCreateRequest,
  BillingPlanPrice,
  BillingPlanUpdateRequest,
  PlanCreditsOverview,
  TopUpPackage,
  TopUpPackageCreateRequest,
  TopUpPackageUpdateRequest,
  CustomTopUpPolicy,
  CustomTopUpPolicyUpsertRequest,
  CustomTopUpQuote,
  TopUpBonusRule,
  TopUpBonusRuleCreateRequest,
  TopUpBonusRuleUpdateRequest,
  PromotionCampaign,
  PromotionCampaignCreateRequest,
  PromotionCampaignUpdateRequest,
  DailyRewardRule,
  DailyRewardRuleCreateRequest,
  DailyRewardRuleUpdateRequest,
  PaygPolicy,
  PaygPolicyUpdateRequest,
  TopupPaygOverview,
  ManualTopUpGrantRequest,
  ManualTopUpGrantResponse,
  CreditLedgerListOut,
  AuthCatalogResponse,
  AuthMemberMatrixResponse,
  AuthMemberGrantBody,
  AuthMemberGrantResult,
  AuthTeamSwitchBody,
  AuthTeamSwitchResult,
  AuthMemberResetResult,
  AuthPlanMatrixResponse,
  AuthPlanGrantBody,
  AuthPlanGrantResult,
  AuthWorkspaceMatrixResponse,
  AuthWorkspaceOverrideBody,
  AuthWorkspaceOverrideResult,
  AIChatAdminConversationAggregateResponse,
  AIChatAdminConversationDetail,
  AIChatAdminExecutionDetail,
  AIChatAdminExecutionListResponse,
  AIChatAdminMetrics,
  AssetStorageDetail,
  ContentEntry,
  ContentTypeOut,
  DomainBinding,
  DomainWithSite,
  EntitlementsMap,
  EntitlementsOut,
  ExternalServiceRow,
  ExternalServicesListResponse,
  SkillCatalogPayload,
  SkillDetail,
  SkillLibraryListResponse,
  SkillLibraryMetrics,
  SkillVersionDetail,
  FieldDefinitionOut,
  FieldOption,
  FieldType,
  MeResponse,
  PlatformAuditLogResponse,
  PlatformEnvironmentIdentity,
  PlatformSessionInfoOut,
  PlatformTaskBatchDeleteResult,
  PlatformTaskDeleteResult,
  PlatformTaskDetail,
  PlatformTaskListResponse,
  PlatformTaskBatchPauseResult,
  PlatformTaskBatchResumeResult,
  PlatformTaskPauseResult,
  PlatformTaskPinResult,
  PlatformTaskRedoResult,
  PlatformTaskReorderResult,
  PlatformTaskResult,
  PublishStatus,
  PublishTriggerResult,
  RenderProviderDetail,
  SaasSite,
  SaasWorkspace,
  SiteAsset,
  SiteSettingsOut,
  SiteWithWorkspace,
  StorageCleanupDryRun,
  StorageDeepzoomSummary,
  StorageLifecycleSnapshot,
  StorageOrphanAudit,
  StorageTrendResponse,
  StorageUsageBreakdown,
  TenantOverview,
  OverviewUsageTrendResponse,
  OverviewTeamDirectory,
  TenantCreditLedgerPage,
  Taxonomy,
  TaxonomyTerm,
  TenantFeatureEntitlementMatrixResponse,
  WorkerControlActionResult,
  WorkerDirectTestCreated,
  EnrollmentTokenCreateResult,
  EnrollmentTokenRow,
  WorkerDetail,
  WorkerCapabilityMatrixResponse,
  WorkerEnginesResponse,
  EngineProfilePolicyRow,
  WorkersPageResponse,
  WorkspaceMember,
  ProviderListResponse,
  ProviderDefinitionRow,
  CompatibleFunctionNodeRow,
  ProviderCredentialListResponse,
  ProviderCredentialRow,
  TestConnectionResponse,
  ResolveCheckResponse,
  ProviderCredentialEnvironment,
  ProviderModelListResponse,
  ProviderModelFacetsResponse,
  ProviderModelRow,
  VolcengineArkSyncResult,
  KlingSyncResult,
  ProviderOnboardingStatus,
  NodeModelBindingRow,
  BindableModelRow,
  ModelCapabilityListResponse,
  ModelCapabilityDetail,
  ModelCapabilitySnapshotRow,
  ModelCapabilitySyncAllResult,
  ModelCapabilityOverrideRow,
  CapabilityOverrideType,
  CapabilityStatus,
  NormalizedCapability,
  EligibleModelRow,
} from './types'
import type { CropRect, GraphicPage, GraphicPageBlock, GraphicPageModule, GraphicPageTree } from './graphicPageTypes'

/**
 * Deliberately a *separate* axios instance from `../api/client` (the
 * legacy Pic2Content pipeline client) — different baseURL (configurable
 * per Phase 2B-0 spec §33/§34, since the Cloud Admin Frontend and the
 * Pic2Content pipeline API may end up on different hosts) and, crucially,
 * `withCredentials: true` so the httpOnly session cookie
 * (`vistoera_session`) is sent cross-origin to `api.vistoera.com` when
 * this is deployed to `app.vistoera.com` (Cloudflare Pages).
 */
// Same normalization as ../api/client.ts's STANDALONE_BASE_URL, and for
// the identical reason: every call site below is relative (`/me`, never
// `/api/saas/me`), so this must always end in `/api/saas` regardless of
// whether the deployment's env var was set with or without that suffix.
function withApiSaasSuffix(raw: string): string {
  const trimmed = raw.replace(/\/+$/, '')
  return trimmed.endsWith('/api/saas') ? trimmed : `${trimmed}/api/saas`
}
const RAW_SAAS_API_BASE_URL = (import.meta.env.VITE_SAAS_API_BASE_URL as string | undefined)?.trim()

// Phase S1 incident (2026-08-10) — a Cloudflare Pages deployment that set
// VITE_API_BASE_URL but never separately set VITE_SAAS_API_BASE_URL fell
// through to the '/api/saas' relative default below, which resolves to
// the *Pages* origin (a static host, real POSTs there get 405 Method Not
// Allowed) instead of the API origin. A relative path only ever works
// when something (Vite's dev proxy, nginx.conf) sits in front of both
// origins — never true for static hosting. So: derive from
// VITE_API_BASE_URL (same backend host, just a different router prefix)
// before ever falling back to a page-relative path, so one missing env
// var can no longer silently break every SaaS API call on a static host.
const RAW_API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim()
const API_ORIGIN_FROM_PIPELINE_BASE_URL = RAW_API_BASE_URL?.replace(/\/+$/, '').replace(/\/api$/, '')

const SAAS_API_BASE_URL = RAW_SAAS_API_BASE_URL
  ? withApiSaasSuffix(RAW_SAAS_API_BASE_URL)
  : API_ORIGIN_FROM_PIPELINE_BASE_URL
    ? withApiSaasSuffix(API_ORIGIN_FROM_PIPELINE_BASE_URL)
    : '/api/saas'

/** Exposed so `<img>` tags (Website Builder cover images, Asset variant
 * delivery — spec §10/§26) can build a direct URL without routing an
 * image byte stream through this module's axios instance. */
export const SAAS_API_BASE_URL_EXPORT = SAAS_API_BASE_URL

/** Phase 2B-1 — the base URL the SaaS Images page points the shared
 * pipeline API client (`../api/client`'s `setPipelineApiScope`) at, so the
 * exact same standalone pipeline components hit this Workspace's own
 * `/images` scope instead of `/api`. */
export const workspaceImagesBaseUrl = (workspaceId: string) => `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/images`

/** VISTOERA_FLAT_IMAGE_OPTIMIZATION_V1 — the 平面图像优化 workbench's own,
 * entirely independent scope (`app.api.routes.flat_image`). Passed to the
 * exact same `setPipelineApiScope` the 物品标准图 page already uses (see
 * `workspaceImagesBaseUrl` above) so `UploadModal`/`getUploadConfig` are
 * reused unmodified — never a second upload widget (spec §九). */
export const workspaceFlatImagesBaseUrl = (workspaceId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/flat-images`

// Task spec §22 — the one header `saas_auth.py::signup` reads to merge
// an anonymous visitor's acquisition history into the new user.
const MARKETING_VISITOR_ID_HEADER = 'X-Vst-Visitor-Id'

export const saasApi = axios.create({
  baseURL: SAAS_API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
})

// P0 fix (2026-08) — this instance was the one axios client in the app that
// never attached the Preview Access header (`../api/client`'s `api`/
// `mediaApi` already did). Once a user unlocked `PreviewAccessGate`, every
// pipeline/media call worked but every SaaS call (signup/login/verify/
// workspace/...) kept hitting the backend's global `preview_access_gate`
// middleware and coming back 401 PREVIEW_ACCESS_REQUIRED before ever
// reaching the actual handler. Same stored key, same header name, same
// backend contract — no second credential/storage scheme, and this is a
// pure request decoration: it sends nothing when the gate is off (key never
// gets stored — see `PreviewAccessGate.tsx`), which is exactly the case on
// Production, so this never sends the header there.
saasApi.interceptors.request.use((config) => {
  const key = getStoredPreviewAccessKey()
  if (key) {
    config.headers = config.headers ?? {}
    ;(config.headers as Record<string, string>)[PREVIEW_ACCESS_HEADER] = key
  }
  // VISTOERA_PLATFORM_ADMIN_MARKETING_ANALYTICS_HOME_AND_TRACKING_
  // FOUNDATION_V1 (task spec §22) — attach the anonymous Marketing
  // Analytics visitor id (set by `../analytics/marketingTracking.ts`'s
  // page-view beacon) to every SaaS API call. Harmless on every route
  // other than `/auth/signup` (the backend only reads this header there
  // — see `saas_auth.py::signup`); reading directly from `localStorage`
  // here (rather than importing `marketingTracking.ts`) keeps this
  // module free of any dependency on that one.
  const visitorId = getStoredMarketingVisitorId()
  if (visitorId) {
    config.headers = config.headers ?? {}
    ;(config.headers as Record<string, string>)[MARKETING_VISITOR_ID_HEADER] = visitorId
  }
  return attachExpectedSessionUser(config)
})

export interface SaasApiError extends Error {
  code?: string
  status?: number
  // VistoeraTASK-00156 §七/§八/§十 — Structured Validation Error Contract.
  // Populated ONLY when the backend's `detail` carries these extra keys
  // (currently: `VideoParameterValidationError`, code
  // `VIDEO_PARAMETER_INVALID` — see `app/saas/errors.py`). Every other
  // existing error path leaves these `undefined`, so no pre-existing
  // caller that only reads `.code`/`.message` is affected. `field`
  // identifies WHICH form control to show an error state on (Canvas
  // maps this to duration/resolution/ratio/audio/reference-asset
  // sections); `constraint`/`received`/`allowed` are additive
  // diagnostic context, not required for the minimum-bar UI.
  field?: string
  constraint?: string
  received?: unknown
  allowed?: unknown
}

saasApi.interceptors.response.use(
  (r) => r,
  (err) => {
    const data = err.response?.data
    const detail = data?.detail
    const topMessage = typeof data?.message === 'string' ? data.message : undefined
    let code: string | undefined =
      (detail && typeof detail === 'object' ? detail.code : undefined) ||
      (typeof data?.code === 'string' ? data.code : undefined)
    let message: string
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
      message = detail.message
    } else if (typeof detail === 'string' && detail.trim()) {
      // Prefer human SaaS messages; keep opaque INTERNAL_ERROR detail out of toasts.
      message = topMessage && code === 'INTERNAL_ERROR' ? topMessage : detail
    } else if (topMessage) {
      message = topMessage
    } else if (!err.response) {
      // True transport / no-response cases (timeout, offline, CORS-masked
      // 500). Keep this AUTH/TRANSPORT-neutral — never reuse CCA/business
      // "syncStale" copy here (that leaked onto Login as a fake auth error).
      if (err.code === 'ECONNABORTED' || /timeout/i.test(String(err.message))) {
        message = '网络连接超时，请稍后重试'
      } else {
        message = '暂时无法连接服务，请稍后重试'
      }
      if (!code) code = 'TRANSPORT_ERROR'
    } else if (err.response.status === 401 || err.response.status === 403) {
      // Do NOT auto-logout here — permission/business 403s must not clear
      // session. Auth surfaces map codes themselves.
      const detailMessage =
        detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string'
          ? (detail as { message: string }).message
          : undefined
      message =
        err.response.status === 403
          ? code === 'email_not_verified'
            ? detailMessage || 'Please verify your email first.'
            : '没有权限执行此操作'
          : code === 'invalid_credentials'
            ? '邮箱或密码不正确'
            : '登录已失效，请重新登录'
    } else if (err.response.status === 404) {
      message = '资产不存在'
    } else if (err.response.status === 409 || err.response.status === 422) {
      message = typeof detail === 'string' && detail.trim() ? detail : '资产不可用于当前任务'
    } else if (err.response.status === 413) {
      message = '上传文件过大'
    } else if (err.response.status === 503 && code === 'auth_service_unavailable') {
      message = '暂时无法连接登录服务，请稍后重试'
    } else if (
      err.response.status === 503 &&
      detail &&
      typeof detail === 'object' &&
      typeof detail.message === 'string' &&
      detail.message.trim()
    ) {
      message = detail.message
    } else if (err.response.status >= 500) {
      message = '服务异常，请稍后重试'
    } else {
      message = typeof err.message === 'string' ? err.message : '请求失败'
    }
    const wrapped = new Error(typeof message === 'string' ? message : JSON.stringify(message)) as SaasApiError
    wrapped.code = code
    wrapped.status = err.response?.status
    reportSessionIdentityChanged(wrapped.status, code)
    // VistoeraTASK-00156 §七/§八 — surface the Structured Validation
    // Error Contract's extra keys (see the `SaasApiError` interface
    // above) additively; `detail.field` only exists on the new
    // `VideoParameterValidationError` responses, so this is a no-op for
    // every other existing error shape.
    if (detail && typeof detail === 'object' && typeof (detail as { field?: unknown }).field === 'string') {
      const structuredDetail = detail as { field: string; constraint?: string; received?: unknown; allowed?: unknown }
      wrapped.field = structuredDetail.field
      wrapped.constraint = structuredDetail.constraint
      wrapped.received = structuredDetail.received
      wrapped.allowed = structuredDetail.allowed
    }
    return Promise.reject(wrapped)
  },
)

// --------------------------------------------------------------- Auth
export const login = (email: string, password: string) =>
  saasApi.post<MeResponse>('/auth/login', { email, password })
export const logout = () => saasApi.post('/auth/logout')
export const fetchMe = () => saasApi.get<MeResponse>('/me')

/** VistoeraTASK-00586 — whitelist: only `display_name`. Email is immutable. */
export const patchMyProfile = (body: { display_name: string }) =>
  saasApi.patch<MeResponse>('/me/profile', { display_name: body.display_name })

/** VistoeraTASK-00590 — per-user image-editor resize presets (never team-shared). */
export type ImageEditorPresetType = 'DIMENSION' | 'FILE_SIZE'
export interface ImageEditorPreset {
  id: string
  preset_type: ImageEditorPresetType
  name: string
  value: { max_width?: number; max_height?: number; max_bytes?: number }
  created_at: string | null
  updated_at: string | null
}
export const listImageEditorPresets = (presetType?: ImageEditorPresetType) =>
  saasApi.get<{ items: ImageEditorPreset[] }>('/me/image-editor-presets', {
    params: presetType ? { preset_type: presetType } : undefined,
  })
export const createImageEditorPreset = (body: {
  preset_type: ImageEditorPresetType
  name: string
  value: ImageEditorPreset['value']
}) => saasApi.post<ImageEditorPreset>('/me/image-editor-presets', body)
export const updateImageEditorPreset = (
  presetId: string,
  body: { name?: string; value?: ImageEditorPreset['value'] },
) => saasApi.patch<ImageEditorPreset>(`/me/image-editor-presets/${encodeURIComponent(presetId)}`, body)
export const deleteImageEditorPreset = (presetId: string) =>
  saasApi.delete<{ status: string }>(`/me/image-editor-presets/${encodeURIComponent(presetId)}`)

export interface DeviceAccountOut {
  user_id: string
  email: string
  display_name: string | null
  is_current: boolean
  can_switch: boolean
  needs_relogin: boolean
}

export interface DeviceAccountListResponse {
  accounts: DeviceAccountOut[]
}

export const fetchDeviceAccounts = () => saasApi.get<DeviceAccountListResponse>('/auth/device-accounts')
export const addDeviceAccount = (email: string, password: string) =>
  saasApi.post<DeviceAccountListResponse>('/auth/device-accounts/add', { email, password })
export const switchAccount = (userId: string) =>
  saasApi.post<MeResponse>('/auth/switch-account', { user_id: userId })
export const removeDeviceAccount = (userId: string) => saasApi.delete(`/auth/device-accounts/${userId}`)

// Phase S1 — self-serve signup. `signup` never returns a session (the
// backend never sets the cookie until the email is verified); `verifyEmail`
// / `resendVerification` are likewise unauthenticated.
//
// LOCAL-AUTH-A1: `development_verification_url` is present ONLY when the
// backend APP_ENV is local/development. Preview/Production omit the field
// (response_model_exclude_none). Frontend must not invent Local by hostname —
// it only renders the link when the API actually returned it.
export type SignupResponse = {
  email: string
  message: string
  development_verification_url?: string | null
}

export type ResendVerificationResponse = {
  ok: boolean
  development_verification_url?: string | null
}

export const signup = (email: string, password: string) =>
  saasApi.post<SignupResponse>('/auth/signup', { email, password })
export const verifyEmail = (token: string) => saasApi.post('/auth/verify', { token })
export const resendVerification = (email: string) =>
  saasApi.post<ResendVerificationResponse>('/auth/resend-verification', { email })

// Password Recovery / Reset. `forgotPassword` always resolves the same
// generic {ok, message} shape regardless of whether the email exists —
// see backend app/api/routes/saas_auth.py's docstring (email enumeration
// protection) — never branch UI copy on any other field here.
//
// `development_password_reset_url` follows the exact same Local-only gate
// as SignupResponse.development_verification_url above: present ONLY
// when the backend APP_ENV is local/development.
export type ForgotPasswordResponse = {
  ok: boolean
  message: string
  development_password_reset_url?: string | null
}

export type ResetPasswordResponse = {
  ok: boolean
  message: string
}

export const forgotPassword = (email: string) =>
  saasApi.post<ForgotPasswordResponse>('/auth/forgot-password', { email })
export const resetPassword = (token: string, password: string, passwordConfirmation: string) =>
  saasApi.post<ResetPasswordResponse>('/auth/reset-password', {
    token,
    password,
    password_confirmation: passwordConfirmation,
  })

// ---------------------------------------------------------- Platform
// VISTOERA_TENANT_ADMIN_360_..._P0_V1 (task spec §5-§18/§53-§55) — the
// canonical Tenant Operations Overview list call: search + filter +
// server-side sort + server-side pagination, all in ONE request (never
// a client-side JS sort/filter of an unpaginated dump — task spec §53's
// hard rule). Replaces the old unpaginated bare-list shape.
export const platformListWorkspaces = (params?: TenantListQueryParams) =>
  saasApi.get<TenantListResponse>('/platform/workspaces', { params })
/** VistoeraTASK-00566 — create Workspace + Owner login (password is write-only). */
export const platformCreateWorkspace = (data: {
  name: string
  slug?: string
  owner_email: string
  owner_password: string
  owner_display_name?: string
}) => saasApi.post<SaasWorkspace>('/platform/workspaces', data)
/** VistoeraTASK-00546 — Platform-Admin-only manual Team entitlement grant /
 * revoke (``source`` is always ``platform_admin``; the server refuses to
 * override an entitlement that comes from a plan / points rule with 409). */
export const platformSetTeamMode = (workspaceId: string, data: { enabled: boolean; reason?: string }) =>
  saasApi.put<{
    workspace_id: string
    team_mode_enabled: boolean
    team_entitlement_sources: string[]
  }>(`/platform/workspaces/${workspaceId}/team-mode`, { ...data, source: 'platform_admin' })

/** VistoeraTASK-00567/00569 — tenant Owner account lifecycle (Platform Admin only). */
export type TenantDeleteJobStatus = {
  purge_job_id: string | null
  workspace_id: string
  target_user_id?: string | null
  target_email?: string | null
  delete_type?: string
  phase: string | null
  total_assets?: number
  processed_assets?: number
  remaining_assets?: number
  failed_assets?: number
  skipped_referenced?: number
  last_error?: string | null
  detail?: Record<string, unknown>
  worker_job_id?: string | null
  created_at?: string | null
  updated_at?: string | null
}
export type TenantAccountDetail = {
  workspace_id: string
  workspace_name: string
  workspace_slug: string
  workspace_status: string
  workspace_type: 'personal' | 'team' | string
  team_mode_enabled: boolean
  team_formed: boolean
  active_member_count: number
  plan_key: string | null
  credit_balance: string
  user_id: string | null
  email: string | null
  display_name: string | null
  user_status: string | null
  role: string | null
  membership_status: string | null
  created_at: string | null
  last_login_at: string | null
  is_platform_admin: boolean
  asset_counts: {
    image_assets: number
    video_assets: number
    audio_assets: number
    text_assets: number
    canvas_count: number
    project_count: number
    running_jobs: number
  }
  running_jobs: number
}
export type TenantDeletePreview = {
  mode: string
  workspace_id: string
  workspace_name: string
  email: string | null
  user_id?: string | null
  affected_members: number
  asset_counts: TenantAccountDetail['asset_counts']
  running_jobs: number
  irreversible: boolean
  confirmation_token_hint: string | null
  confirmation_kind: 'email' | 'workspace_name' | string
  workspace_type?: string
  is_last_owner_with_peers?: boolean
  can_proceed?: boolean
  block_reason?: string | null
  reference_check?: {
    blocked_count?: number
    safe_to_delete_count?: number
    total_member_assets?: number
    blocked_reason_summary?: Record<string, number>
    referenced_count?: number
    unreferenced_count?: number
  } | null
  existing_delete_job?: TenantDeleteJobStatus | null
  default_mode_recommendation?: string
}
export const platformGetTenantAccount = (workspaceId: string) =>
  saasApi.get<TenantAccountDetail>(`/platform/workspaces/${workspaceId}/account`)
export const platformPreviewTenantDelete = (
  workspaceId: string,
  params?: { mode?: string; user_id?: string },
) =>
  saasApi.get<TenantDeletePreview>(`/platform/workspaces/${workspaceId}/account/delete-preview`, {
    params,
  })
export const platformResetTenantPassword = (
  workspaceId: string,
  data: { new_password: string; new_password_confirm: string; must_change_password?: boolean },
) => saasApi.post<{ ok: boolean; workspace_id: string; user_id: string; email: string }>(
  `/platform/workspaces/${workspaceId}/account/reset-password`,
  data,
)
export const platformForceLogoutTenant = (workspaceId: string) =>
  saasApi.post<{ ok: boolean }>(`/platform/workspaces/${workspaceId}/account/force-logout`, {})
export const platformSetTenantAccountActive = (workspaceId: string, active: boolean) =>
  saasApi.post<{ ok: boolean; user_status: string; membership_status: string }>(
    `/platform/workspaces/${workspaceId}/account/set-active`,
    { active },
  )
export const platformDeleteTenantAccount = (
  workspaceId: string,
  data: { mode: 'keep_assets' | 'with_assets'; confirmation: string; user_id?: string },
) =>
  saasApi.post<{ ok: boolean; phase: string; purge_job_id: string }>(
    `/platform/workspaces/${workspaceId}/account/delete`,
    data,
  )
export const platformDeleteTenantTeam = (workspaceId: string, data: { confirmation: string }) =>
  saasApi.post<{ ok: boolean; phase: string; purge_job_id: string }>(
    `/platform/workspaces/${workspaceId}/team/delete`,
    data,
  )
export const platformGetTenantDeleteJob = (workspaceId: string, purgeJobId: string) =>
  saasApi.get<TenantDeleteJobStatus>(
    `/platform/workspaces/${workspaceId}/account/delete-jobs/${purgeJobId}`,
  )
export const platformGetLatestTenantDeleteJob = (workspaceId: string) =>
  saasApi.get<TenantDeleteJobStatus>(`/platform/workspaces/${workspaceId}/account/delete-jobs/latest`)
export const platformRetryTenantDeletePurge = (workspaceId: string, purgeJobId: string) =>
  saasApi.post<TenantDeleteJobStatus>(
    `/platform/workspaces/${workspaceId}/account/delete-jobs/${purgeJobId}/retry`,
    {},
  )

export const platformGetWorkspace = (id: string) => saasApi.get<WorkspaceDetail>(`/platform/workspaces/${id}`)
export const platformListPlanCatalog = () => saasApi.get<PlanCatalogRow[]>('/platform/plans')
export const platformUpgradeWorkspacePlan = (
  workspaceId: string,
  data: { target_plan_key: string; upgrade_attempt_id: string },
) => saasApi.post<AdminPlanUpgradeResult>(`/platform/workspaces/${workspaceId}/plan-upgrade`, data)
export const platformAdjustWorkspaceCredit = (
  workspaceId: string,
  data: { amount: string | number; note: string; adjustment_request_id: string },
) => saasApi.post<AdminCreditAdjustmentResult>(`/platform/workspaces/${workspaceId}/credit-adjustment`, data)
// Tenant 360 — task spec §24-§27/§39-§44/§36-§38/§65/§66.
export const platformGetWorkspaceUsageAnalytics = (workspaceId: string, days: number = 30) =>
  saasApi.get<TenantAnalytics>(`/platform/workspaces/${workspaceId}/usage-analytics`, { params: { days } })
export const platformGetWorkspaceAiUsage = (
  workspaceId: string,
  params?: { days?: number; page?: number; page_size?: number },
) => saasApi.get<AiUsageResponse>(`/platform/workspaces/${workspaceId}/ai-usage`, { params })
export const platformGetWorkspacePaymentHistory = (workspaceId: string) =>
  saasApi.get<PaymentHistoryResponse>(`/platform/workspaces/${workspaceId}/payment-history`)
export const platformReverseCreditAdjustment = (
  workspaceId: string,
  ledgerEntryId: string,
  data: { note: string; reversal_request_id: string },
) =>
  saasApi.post<AdminCreditReversalResult>(
    `/platform/workspaces/${workspaceId}/credit-adjustments/${ledgerEntryId}/reverse`,
    data,
  )
export const platformListRateRules = () => saasApi.get<RateRuleRow[]>('/platform/rate-rules')
export const platformUpdateRateRule = (ruleKey: string, data: { rate?: string | number; enabled?: boolean }) =>
  saasApi.patch<RateRuleRow>(`/platform/rate-rules/${encodeURIComponent(ruleKey)}`, data)
// VISTOERA_AI_BILLING_PHASE2_FULL_RATE_CATALOG_USAGE_LEDGER_AND_PRICING_V1
// (task spec §25) — the fine-grained AI Rate Catalog V2, sibling to (never
// replacing) the 4-row legacy Rate Rules helpers above.
export const platformListRateCatalog = (params?: {
  category?: string
  provider?: string
  model?: string
  enabled?: boolean
}) => saasApi.get<RateCatalogEntryRow[]>('/platform/rate-catalog', { params })
export const platformUpdateRateCatalogEntry = (
  entryId: string,
  data: {
    points_per_unit?: string | number
    estimated_cost_per_unit?: string | number | null
    cost_currency?: string | null
    enabled?: boolean
    pricing_status?: string
    display_name?: string
    notes?: string | null
  },
) => saasApi.patch<RateCatalogEntryRow>(`/platform/rate-catalog/${encodeURIComponent(entryId)}`, data)

// ===========================================================================
// VISTOERA_BILLING_PLAN_CREDITS_TOPUP_PAYG_REWARDS_AND_PROMOTIONS_
// FOUNDATION_P0_V1 — Billing Domain admin endpoints, all under
// `/platform/billing/...` (backend router `saas_platform_billing.py`,
// mounted at `/api/saas/platform/billing`). Every write endpoint is
// Platform-Admin-only (router-level `require_platform_admin`).
// ===========================================================================
export const platformGetPlanCreditsOverview = () =>
  saasApi.get<PlanCreditsOverview>('/platform/billing/plan-credits/overview')
export const platformListBillingPlans = () => saasApi.get<BillingPlan[]>('/platform/billing/plans')

// VistoeraTASK-00630 — Plan auth matrix (Phase 1 backend contract).
export const platformGetAuthCatalog = () => saasApi.get<AuthCatalogResponse>('/platform/auth-matrix/catalog')
export const platformGetAuthPlanMatrix = (planKey: string) =>
  saasApi.get<AuthPlanMatrixResponse>(`/platform/auth-matrix/plans/${encodeURIComponent(planKey)}/matrix`)
export const platformPutAuthPlanGrant = (planKey: string, data: AuthPlanGrantBody) =>
  saasApi.put<AuthPlanGrantResult>(
    `/platform/auth-matrix/plans/${encodeURIComponent(planKey)}/grants`,
    data,
  )

// VistoeraTASK-00637 — Workspace override matrix (Platform Admin).
export const platformGetAuthWorkspaceMatrix = (workspaceId: string) =>
  saasApi.get<AuthWorkspaceMatrixResponse>(
    `/platform/auth-matrix/workspaces/${encodeURIComponent(workspaceId)}/matrix`,
  )
export const platformPutAuthWorkspaceOverride = (workspaceId: string, data: AuthWorkspaceOverrideBody) =>
  saasApi.put<AuthWorkspaceOverrideResult>(
    `/platform/auth-matrix/workspaces/${encodeURIComponent(workspaceId)}/overrides`,
    data,
  )

// VistoeraTASK-00634 — current member effective auth (Canvas visibility).
export const workspaceGetMyAuthMatrix = (workspaceId: string) =>
  saasApi.get<AuthMemberMatrixResponse>(
    `/workspaces/${encodeURIComponent(workspaceId)}/auth-matrix/me`,
  )

// VistoeraTASK-00638 — Member grant matrix (team Owner/Admin).
export const workspaceGetMemberAuthMatrix = (workspaceId: string, userId: string) =>
  saasApi.get<AuthMemberMatrixResponse>(
    `/workspaces/${encodeURIComponent(workspaceId)}/auth-matrix/members/${encodeURIComponent(userId)}`,
  )
export const workspacePutMemberAuthGrant = (
  workspaceId: string,
  userId: string,
  data: AuthMemberGrantBody,
) =>
  saasApi.put<AuthMemberGrantResult>(
    `/workspaces/${encodeURIComponent(workspaceId)}/auth-matrix/members/${encodeURIComponent(userId)}/grants`,
    data,
  )

/** VistoeraTASK-00671 — Team Feature Switch (Owner): INHERIT | DENY. */
export const workspacePutTeamAuthSwitch = (workspaceId: string, data: AuthTeamSwitchBody) =>
  saasApi.put<AuthTeamSwitchResult>(
    `/workspaces/${encodeURIComponent(workspaceId)}/auth-matrix/team-switches`,
    data,
  )
export const workspaceResetMemberAuthGrants = (workspaceId: string, userId: string) =>
  saasApi.post<AuthMemberResetResult>(
    `/workspaces/${encodeURIComponent(workspaceId)}/auth-matrix/members/${encodeURIComponent(userId)}/grants/reset-inherit`,
  )

// VistoeraTASK-00638 — same Member Grant storage via Platform Admin.
export const platformGetMemberAuthMatrix = (workspaceId: string, userId: string) =>
  saasApi.get<AuthMemberMatrixResponse>(
    `/platform/auth-matrix/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}/matrix`,
  )
export const platformPutMemberAuthGrant = (
  workspaceId: string,
  userId: string,
  data: AuthMemberGrantBody,
) =>
  saasApi.put<AuthMemberGrantResult>(
    `/platform/auth-matrix/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}/grants`,
    data,
  )
export const platformResetMemberAuthGrants = (workspaceId: string, userId: string) =>
  saasApi.post<AuthMemberResetResult>(
    `/platform/auth-matrix/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}/grants/reset-inherit`,
  )
export const platformCreateBillingPlan = (data: BillingPlanCreateRequest) =>
  saasApi.post<BillingPlan>('/platform/billing/plans', data)
export const platformUpdateBillingPlan = (planKey: string, data: BillingPlanUpdateRequest) =>
  saasApi.patch<BillingPlan>(`/platform/billing/plans/${encodeURIComponent(planKey)}`, data)
export const platformUpsertBillingPlanPrice = (
  planKey: string,
  data: { currency: string; amount_minor: number; period?: string; enabled?: boolean; sort_order?: number },
) => saasApi.put<BillingPlanPrice>(`/platform/billing/plans/${encodeURIComponent(planKey)}/prices`, data)
export const platformDeleteBillingPlanPrice = (planKey: string, priceId: string) =>
  saasApi.delete<{ ok: boolean }>(
    `/platform/billing/plans/${encodeURIComponent(planKey)}/prices/${encodeURIComponent(priceId)}`,
  )

export const platformListDailyRewardRules = () => saasApi.get<DailyRewardRule[]>('/platform/billing/daily-reward-rules')
export const platformCreateDailyRewardRule = (data: DailyRewardRuleCreateRequest) =>
  saasApi.post<DailyRewardRule>('/platform/billing/daily-reward-rules', data)
export const platformUpdateDailyRewardRule = (ruleId: string, data: DailyRewardRuleUpdateRequest) =>
  saasApi.patch<DailyRewardRule>(`/platform/billing/daily-reward-rules/${encodeURIComponent(ruleId)}`, data)
export const platformDeleteDailyRewardRule = (ruleId: string) =>
  saasApi.delete<{ ok: boolean }>(`/platform/billing/daily-reward-rules/${encodeURIComponent(ruleId)}`)

export const platformListTopupBonusRules = (currency?: string) =>
  saasApi.get<TopUpBonusRule[]>('/platform/billing/topup-bonus-rules', { params: currency ? { currency } : undefined })
export const platformCreateTopupBonusRule = (data: TopUpBonusRuleCreateRequest) =>
  saasApi.post<TopUpBonusRule>('/platform/billing/topup-bonus-rules', data)
export const platformUpdateTopupBonusRule = (ruleId: string, data: TopUpBonusRuleUpdateRequest) =>
  saasApi.patch<TopUpBonusRule>(`/platform/billing/topup-bonus-rules/${encodeURIComponent(ruleId)}`, data)
export const platformDeleteTopupBonusRule = (ruleId: string) =>
  saasApi.delete<{ ok: boolean }>(`/platform/billing/topup-bonus-rules/${encodeURIComponent(ruleId)}`)

export const platformListPromotionCampaigns = () => saasApi.get<PromotionCampaign[]>('/platform/billing/promotion-campaigns')
export const platformCreatePromotionCampaign = (data: PromotionCampaignCreateRequest) =>
  saasApi.post<PromotionCampaign>('/platform/billing/promotion-campaigns', data)
export const platformUpdatePromotionCampaign = (campaignId: string, data: PromotionCampaignUpdateRequest) =>
  saasApi.patch<PromotionCampaign>(`/platform/billing/promotion-campaigns/${encodeURIComponent(campaignId)}`, data)

export const platformGetTopupPaygOverview = () => saasApi.get<TopupPaygOverview>('/platform/billing/topup-payg/overview')
export const platformListTopupPackages = () => saasApi.get<TopUpPackage[]>('/platform/billing/topup-packages')
export const platformCreateTopupPackage = (data: TopUpPackageCreateRequest) =>
  saasApi.post<TopUpPackage>('/platform/billing/topup-packages', data)
export const platformUpdateTopupPackage = (packageId: string, data: TopUpPackageUpdateRequest) =>
  saasApi.patch<TopUpPackage>(`/platform/billing/topup-packages/${encodeURIComponent(packageId)}`, data)
export const platformArchiveTopupPackage = (packageId: string) =>
  saasApi.delete<{ ok: boolean }>(`/platform/billing/topup-packages/${encodeURIComponent(packageId)}`)

export const platformListCustomTopupPolicies = () =>
  saasApi.get<CustomTopUpPolicy[]>('/platform/billing/custom-topup-policies')
export const platformUpsertCustomTopupPolicy = (currency: string, data: CustomTopUpPolicyUpsertRequest) =>
  saasApi.put<CustomTopUpPolicy>(`/platform/billing/custom-topup-policies/${encodeURIComponent(currency)}`, data)
export const platformQuoteCustomTopup = (data: { currency: string; amount_minor: number }) =>
  saasApi.post<CustomTopUpQuote>('/platform/billing/custom-topup-policies/quote', data)

export const platformGetPaygPolicy = () => saasApi.get<PaygPolicy>('/platform/billing/payg-policy')
export const platformUpdatePaygPolicy = (data: PaygPolicyUpdateRequest) =>
  saasApi.put<PaygPolicy>('/platform/billing/payg-policy', data)

export const platformManualTopupGrant = (workspaceId: string, data: ManualTopUpGrantRequest) =>
  saasApi.post<ManualTopUpGrantResponse>(`/platform/billing/workspaces/${encodeURIComponent(workspaceId)}/topup-grants`, data)

export const platformListCreditLedgerAdmin = (params?: { workspace_id?: string; limit?: number; offset?: number }) =>
  saasApi.get<CreditLedgerListOut>('/platform/billing/credit-ledger', { params })

// VISTOERA_AI_COST_PRICING_PROVIDER_COST_POINTS_REVENUE_MARGIN_CONTROL_
// PLANE_P0_V1 — joined Admin list + write paths for "AI 成本与定价".
// Sibling to (never replacing) the rate-catalog helpers above; same
// underlying ``saas_rate_catalog`` table, enriched with FunctionNode /
// ExecutionOption / Provider / Model / Credential resolution + margin
// math. Decimal fields travel as strings (same convention as Rate Catalog).
export const platformListAiPricing = (params?: {
  function_node?: string
  provider?: string
  model?: string
  status?: string
  search?: string
  loss_making_only?: boolean
}) => saasApi.get<AiPricingListResponse>('/platform/ai-pricing', { params })

export const platformCreateAiPricingEntry = (data: {
  category_key: string
  display_name: string
  operation_key: string
  provider_key: string
  model_key: string
  billing_unit: string
  provider_option_id?: string | null
  provider_model_id?: string | null
  provider_credential_id?: string | null
  provider_tier?: string | null
  points_per_unit?: string | number | null
  estimated_cost_per_unit?: string | number | null
  cost_currency?: string | null
  enabled?: boolean
  pricing_status?: string
  notes?: string | null
}) => saasApi.post<AiPricingEntryRow>('/platform/ai-pricing', data)

export const platformUpdateAiPricingEntry = (
  entryId: string,
  data: {
    provider_option_id?: string | null
    provider_model_id?: string | null
    provider_credential_id?: string | null
    points_per_unit?: string | number | null
    estimated_cost_per_unit?: string | number | null
    cost_currency?: string | null
    enabled?: boolean
    pricing_status?: string
    display_name?: string
    notes?: string | null
  },
) => saasApi.patch<AiPricingEntryRow>(`/platform/ai-pricing/${encodeURIComponent(entryId)}`, data)

export const platformGetAiPricingReferenceConfig = () =>
  saasApi.get<AiPricingReferenceConfig>('/platform/ai-pricing/reference-config')

export const platformUpdateAiPricingReferenceConfig = (data: {
  reference_point_value?: string | number | null
  reference_currency?: string | null
  notes?: string | null
}) => saasApi.put<AiPricingReferenceConfig>('/platform/ai-pricing/reference-config', data)

export const platformListMembers = (workspaceId: string) =>
  saasApi.get<WorkspaceMember[]>(`/platform/workspaces/${workspaceId}/members`)
export const platformGetEntitlements = (workspaceId: string) =>
  saasApi.get<EntitlementsOut>(`/platform/workspaces/${workspaceId}/entitlements`)
export const platformUpdateEntitlements = (workspaceId: string, entitlements: EntitlementsMap) =>
  saasApi.put<EntitlementsOut>(`/platform/workspaces/${workspaceId}/entitlements`, { entitlements })
// VISTOERA_SAAS_TENANT_FEATURE_ENTITLEMENT_MATRIX_V1 — cross-tenant read;
// mutations reuse `platformUpdateEntitlements` above (single write path).
export const platformGetTenantEntitlementMatrix = (params: { page?: number; page_size?: number; search?: string }) =>
  saasApi.get<TenantFeatureEntitlementMatrixResponse>('/platform/tenant-entitlements/matrix', { params })
export const platformListAllSites = () => saasApi.get<SiteWithWorkspace[]>('/platform/sites')
export const platformListAllDomains = () => saasApi.get<DomainWithSite[]>('/platform/domains')
// Platform Admin Shell V0.2 — the one aggregate GET the `/platform/overview`
// page polls every 10s. Same `require_platform_admin` boundary as every
// other /platform/* call above.
export const platformGetAdminOverview = () => saasApi.get<AdminOverview>('/platform/overview')
// Platform Admin Resource & Task Control V2 spec §四 — cheap, DB-free,
// fetched once at Shell mount (see `PlatformLayout.tsx`), never polled.
export const platformGetEnvironment = () => saasApi.get<PlatformEnvironmentIdentity>('/platform/environment')
// Platform Admin Session P0 — Security/System Settings "Session" panel's
// one data source (item 5). Cheap (no DB query), safe to call on demand
// or on a light poll; never returns a raw token/secret.
export const platformGetSessionInfo = () => saasApi.get<PlatformSessionInfoOut>('/platform/session')

export const platformGetBrandingSettings = () =>
  saasApi.get<import('./types').PlatformBrandingSettingsOut>('/platform/settings/branding')

export const platformUploadBrandingFavicon = (file: File) => {
  const form = new FormData()
  form.append('file', file)
  return saasApi.post<import('./types').PlatformBrandingSettingsOut>('/platform/settings/branding/favicon', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// VISTOERA_PLATFORM_ADMIN_MARKETING_ANALYTICS_HOME_AND_TRACKING_FOUNDATION_V1
// (task spec §42/§43) — the ONE aggregate the Marketing Analytics page's
// Date Range control re-fetches on every change (task spec §76); never
// more than this single GET per render, no polling (task spec §75).
export const platformGetMarketingAnalyticsOverview = (range: '7d' | '30d' | '90d') =>
  saasApi.get<MarketingAnalyticsOverview>('/platform/marketing-analytics/overview', { params: { range } })
// Worker Runtime V1 — dedicated "Worker 节点" page (spec §53-§64). Same
// `require_platform_admin` boundary as every other /platform/* call.
export const platformListWorkers = () => saasApi.get<WorkersPageResponse>('/platform/workers')
export const platformGetWorkerCapabilityMatrix = () =>
  saasApi.get<WorkerCapabilityMatrixResponse>('/platform/workers/capability-matrix')
export const platformSetWorkerCapabilityAssignment = (workerId: string, capabilityId: string, enabled: boolean) =>
  saasApi.patch<{ worker_id: string; capability_id: string; enabled: boolean; changed: boolean }>(
    `/platform/workers/${encodeURIComponent(workerId)}/capabilities/${encodeURIComponent(capabilityId)}`,
    { enabled },
  )
export const platformGetWorker = (workerId: string) => saasApi.get<WorkerDetail>(`/platform/workers/${encodeURIComponent(workerId)}`)
export const platformGetWorkerDeletionEligibility = (workerId: string) =>
  saasApi.get<import('./types').WorkerDeletionEligibility>(`/platform/workers/${encodeURIComponent(workerId)}/deletion-eligibility`)
export const platformArchiveWorker = (workerId: string) =>
  saasApi.post<{ worker_id: string; archived: boolean; archived_at: string }>(
    `/platform/workers/${encodeURIComponent(workerId)}/archive`,
    {},
  )
export const platformCreateWorkerTestJob = (workerId: string, body: { job_type: string; prompt: string }) =>
  saasApi.post<WorkerDirectTestCreated>(`/platform/workers/${encodeURIComponent(workerId)}/test-jobs`, body)
export const platformEnableWorker = (workerId: string) =>
  saasApi.post<WorkerControlActionResult>(`/platform/workers/${encodeURIComponent(workerId)}/enable`, {})
export const platformDrainWorker = (workerId: string) =>
  saasApi.post<WorkerControlActionResult>(`/platform/workers/${encodeURIComponent(workerId)}/drain`, {})
export const platformDisableWorker = (workerId: string) =>
  saasApi.post<WorkerControlActionResult>(`/platform/workers/${encodeURIComponent(workerId)}/disable`, {})
export const platformSetWorkerDesiredState = (workerId: string, desiredState: 'ONLINE' | 'AUTO' | 'DRAINING' | 'OFFLINE') =>
  saasApi.patch<{
    worker_id: string
    desired_state: string
    changed: boolean
    control_state: string
    runtime_state: string
  }>(`/platform/workers/${encodeURIComponent(workerId)}/desired-state`, { desired_state: desiredState })
export const platformGetWorkerLifecycle = (workerId: string) =>
  saasApi.get<import('./types').WorkerProcessLifecycleOverlay>(`/platform/workers/${encodeURIComponent(workerId)}/lifecycle`)

// Worker Engine Lifecycle Control Plane P0 — the second, independent axis
// from capability assignment above ("是否允许承接" vs "现在是否真的在
// 跑"). `set_worker_engine_desired_state` may reject with 409
// RESOURCE_CONFLICT / 400 ENGINE_NOT_INSTALLED — surfaced to the caller
// via the thrown AxiosError, same pattern as every other platform mutation
// in this file (never swallowed here).
export const platformListWorkerEngines = (workerId: string) =>
  saasApi.get<WorkerEnginesResponse>(`/platform/workers/${encodeURIComponent(workerId)}/engines`)
export const platformListEngineProfiles = () =>
  saasApi.get<{ engines: EngineProfilePolicyRow[] }>('/platform/engine-profiles')
export const platformUpdateEngineProfilePolicy = (
  engineId: string,
  body: { prompt_language_policy: string; translation_failure_policy: string },
) => saasApi.put<EngineProfilePolicyRow>(`/platform/engine-profiles/${encodeURIComponent(engineId)}`, body)
export const platformSetWorkerEngineDesiredState = (workerId: string, engineId: string, desiredState: 'OFFLINE' | 'READY') =>
  saasApi.post<{ worker_id: string; engine_id: string; desired_state: string; changed: boolean }>(
    `/platform/workers/${encodeURIComponent(workerId)}/engines/${encodeURIComponent(engineId)}/desired-state`,
    { desired_state: desiredState },
  )

// Worker Enrollment Security (Worker Runtime V2, spec §21-§26) — issue/
// list/revoke enrollment tokens, and independently revoke one Worker's
// long-lived credential. Same `require_platform_admin` boundary.
export const platformCreateEnrollmentToken = (body: {
  label?: string
  ttl_seconds?: number
  max_uses?: number
  environment?: string
}) => saasApi.post<EnrollmentTokenCreateResult>('/platform/workers/enrollment-tokens', body)
export const platformListEnrollmentTokens = () =>
  saasApi.get<{ tokens: EnrollmentTokenRow[] }>('/platform/workers/enrollment-tokens')
export const platformRevokeEnrollmentToken = (tokenId: string) =>
  saasApi.post<{ id: string; revoked: boolean }>(`/platform/workers/enrollment-tokens/${encodeURIComponent(tokenId)}/revoke`, {})
export const platformRevokeWorkerCredential = (workerId: string) =>
  saasApi.post<{ worker_id: string; revoked: boolean }>(`/platform/workers/${encodeURIComponent(workerId)}/credential/revoke`, {})

// ---------------------------------------------------------------------
// VISTOERA_PLATFORM_NODE_PROVIDER_MAPPING_AND_EXECUTION_TARGET_
// MANAGEMENT_V1 — Platform Admin "Provider 管理" page. Thin wrappers
// over `/platform/provider-options/*` (require_platform_admin).
// User-facing workspace provider-options (Canvas) live further below
// under Virtual Try-On / capability consumers.
// ---------------------------------------------------------------------

export type ProviderCapableNodeRow = {
  capability: string
  canvas_node_type: string | null
  fallback_label_zh: string
  fallback_label_en: string
  sort_order: number
  option_count: number
  enabled_option_count: number
  has_default: boolean
  warning: boolean
  visibility?: string
  icon_target?: string
  icon_key?: string | null
}

export type ProviderOptionCredentialStatus = {
  resolved: boolean
  source: 'explicit' | 'platform_default' | 'workspace_default' | null
  credential_id: string | null
  credential_name: string | null
  health: string | null
  status: string | null
  enabled: boolean
  reason_code: string | null
}

export type ProviderOptionMappingAdmin = {
  id: string
  version: number
  execution_target_type: 'LOCAL_WORKER' | 'EXTERNAL_API' | 'BROWSER_PROVIDER' | 'FUTURE' | string
  worker_id: string | null
  worker_name: string | null
  /** Legacy raw `Worker.status` — never feed into a status badge directly
   * (see `worker_display_status` below); kept only for debugging. */
  worker_status: string | null
  worker_online: boolean | null
  /** Canonical (VISTOERA_PLATFORM_WORKER_STICKY_HEADER_CAPABILITY_MATRIX_
   * VISIBILITY_AND_PROVIDER_RUNTIME_STATE_UNIFICATION_P0_V1) — the exact
   * same `display_status` value the Worker 节点 page's own Worker list
   * renders (`online|busy|draining|disabled|offline`). This is what the
   * Function Node Provider table's status badge must render. */
  worker_display_status: string | null
  /** Item 十五 — set only when the pinned Worker is not currently usable
   * and another online Worker advertises the same capability. */
  suggested_worker: { worker_id: string; worker_name: string; candidate_count: number } | null
  provider_definition_id: string | null
  provider_key: string | null
  provider_display_name: string | null
  provider_type: string | null
  /** ProviderDefinition.status ('active' | 'disabled') — the existing
   * Provider Registry global operational toggle (item 六/八/十一). */
  provider_operational_status: string | null
  /** Explicit mapping FK only — null does NOT mean unbound if credential_status.resolved. */
  credential_id: string | null
  credential_name: string | null
  credential_health_status: string | null
  /** Canonical resolver summary (Admin status source of truth). */
  credential_status?: ProviderOptionCredentialStatus | null
  adapter_key: string | null
  /** VISTOERA_PROVIDER_MODEL_EXECUTION_OPTION_CANONICAL_SEMANTICS_AND_
   * FASHN_CATALOG_RECONCILIATION_P0_V1 (task spec §5/§8/§9) — canonical
   * ExecutionOption -> ProviderModel traceability. Null is a legitimate
   * state (not-yet-backfilled EXTERNAL_API row, or a genuinely model-less
   * LOCAL_WORKER mapping) — the 执行方案 table must render "—", never a
   * fabricated model name, when these are null. */
  provider_model_id?: string | null
  model_external_id?: string | null
  model_display_name?: string | null
  /** Derived from `model_binding_status` (TASK-00183) — TRUE only for a
   * genuinely broken FIXED_MODEL binding (MISSING/DISABLED/PROVIDER_
   * MISMATCH/CAPABILITY_MISMATCH). Never TRUE for RUNTIME_SELECTION_
   * REQUIRED — that is an expected pooled-model state, not damage. */
  needs_model_repair?: boolean
  /** TASK-00183 canonical Execution Plan Model Resolution contract. */
  model_resolution_mode?: 'NOT_REQUIRED' | 'FIXED_MODEL' | 'RUNTIME_SELECTABLE_MODEL' | string
  model_binding_status?:
    | 'NOT_REQUIRED'
    | 'VALID'
    | 'RUNTIME_SELECTION_REQUIRED'
    | 'MISSING'
    | 'DISABLED'
    | 'PROVIDER_MISMATCH'
    | 'CAPABILITY_MISMATCH'
    | string
  model_binding_reason_code?: string | null
  model_binding_safe_message?: string | null
  model_candidate_count?: number
  config: Record<string, unknown> | null
  created_at: string | null
  /** Canonical unified runtime status (item 三/四/十四) — ONE object every
   * execution_target_type populates identically. Render this directly. */
  runtime_status?: { code: string; label: string; tone: 'success' | 'info' | 'warning' | 'danger' | 'neutral'; available: boolean } | null
  required_worker_capability?: string | null
  required_worker_software_id?: string | null
  dispatch_policy?: string | null
  worker_runtime_availability?: {
    capability: string
    required_worker_software_id?: string | null
    total_runtimes: number
    healthy_runtimes: number
    ready_runtimes: number
    auth_required_runtimes: number
    incompatible_runtimes: number
    status: string
    eligible_worker_count: number
  } | null
}

export type ExecutionPlanSummary = {
  total: number
  worker_backed: number
  api_backed: number
  executable: number
  default_display_name: string | null
}

export type ProviderOptionAdminRow = {
  id: string
  capability: string
  frontend_display_name: string
  /** VistoeraTASK-00332 — list primary label (Worker Registry for Worker-backed schemes). */
  admin_list_display_name?: string
  /** VistoeraTASK-00334 — Provider column (distinct from Worker registry name). */
  admin_provider_display_name?: string | null
  /** Rolling 30d submitted execution count (list API batch aggregate). */
  usage_30d?: number | null
  resolved_worker_id?: string | null
  resolved_worker_display_name?: string | null
  worker_backed_scheme?: boolean
  display_name_source?: 'worker_registry' | 'option_alias' | string
  frontend_description: string | null
  enabled: boolean
  is_default: boolean
  sort_order: number
  scope_type: string
  workspace_id: string | null
  mapping_revision: number
  mapping: ProviderOptionMappingAdmin | null
  created_at: string | null
  updated_at: string | null
}

export type WorkerExecutionCapabilityRow = {
  id: string
  label: string
  known: boolean
  currently_advertised: boolean
}

export type ProviderOptionMappingInput = {
  execution_target_type: string
  worker_id?: string | null
  provider_definition_id?: string | null
  credential_id?: string | null
  adapter_key?: string | null
  config?: Record<string, unknown> | null
  provider_model_id?: string | null
}

export const platformListProviderCapabilities = () =>
  saasApi.get<{ nodes: ProviderCapableNodeRow[] }>('/platform/provider-options/capabilities')

export const platformListProviderOptions = (capability: string) =>
  saasApi.get<{
    capability: string
    canvas_node_type: string | null
    execution_plan_summary?: ExecutionPlanSummary
    options: ProviderOptionAdminRow[]
  }>('/platform/provider-options', { params: { capability } })

export const platformListWorkerExecutionCapabilities = (functionCapability: string, targetType: string) =>
  saasApi.get<{ items: WorkerExecutionCapabilityRow[] }>('/platform/provider-options/worker-capabilities', {
    params: { function_capability: functionCapability, target_type: targetType },
  })

export type ExecutionPlanWorkerRuntimeRow = {
  worker_id: string
  display_name: string
  status: string
  online: boolean
  display_status?: string
  device_id?: string | null
  worker_software_id?: string | null
  worker_release_id?: string | null
  runtime_id?: string | null
  capability_count: number
  advertised_capabilities: string[]
  version?: string | null
}

export const platformListExecutionPlanWorkerRuntimes = () =>
  saasApi.get<{ workers: ExecutionPlanWorkerRuntimeRow[]; total: number }>(
    '/platform/provider-options/worker-runtimes',
  )

export const platformGetProviderOption = (optionId: string) =>
  saasApi.get<ProviderOptionAdminRow>(`/platform/provider-options/${encodeURIComponent(optionId)}`)

export const platformListProviderOptionCompatibleRuntimes = (optionId: string) =>
  saasApi.get<{ items: Record<string, unknown>[] }>(
    `/platform/provider-options/${encodeURIComponent(optionId)}/compatible-runtimes`,
  )

export type ProviderAdapterRow = {
  adapter_type: string
  key: string
  display_name: string
  supported_capabilities: string[]
  default_endpoint: string | null
  parameter_schema: Record<
    string,
    { type?: string; default?: unknown; enum?: unknown[]; required?: boolean; label?: string }
  >
  credential_schema: Record<string, { type?: string; required?: boolean; label?: string }> | null
  model_defaults: Record<string, unknown>
}

export type CapabilityOverview = {
  capability: string
  window_days: number
  total_calls: number
  enabled_count: number
  default_provider_name: string | null
  avg_latency_ms: number | null
  per_option: Array<{ provider_option_id: string; call_count: number }>
}

export const platformListProviderAdapters = () =>
  saasApi.get<{ adapters: ProviderAdapterRow[] }>('/platform/provider-options/adapters')

export const platformGetCapabilityOverview = (capability: string) =>
  saasApi.get<CapabilityOverview>(
    `/platform/provider-options/capabilities/${encodeURIComponent(capability)}/overview`,
  )

export const platformCreateProviderOption = (body: {
  capability: string
  frontend_display_name: string
  frontend_description?: string | null
  mapping: ProviderOptionMappingInput
  sort_order?: number | null
  display_name_zh?: string | null
  display_name_en?: string | null
  description_zh?: string | null
  description_en?: string | null
}) => saasApi.post<ProviderOptionAdminRow>('/platform/provider-options', body)

export const platformUpdateProviderOptionPresentation = (
  optionId: string,
  body: {
    display_name_zh?: string | null
    display_name_en?: string | null
    description_zh?: string | null
    description_en?: string | null
  },
) =>
  saasApi.patch<ProviderOptionAdminRow>(
    `/platform/provider-options/${encodeURIComponent(optionId)}/presentation`,
    body,
  )

export const platformUpdateProviderOptionDisplayName = (optionId: string, frontend_display_name: string) =>
  saasApi.patch<ProviderOptionAdminRow>(`/platform/provider-options/${encodeURIComponent(optionId)}/display-name`, {
    frontend_display_name,
  })

export const platformUpdateProviderOptionDescription = (optionId: string, frontend_description: string | null) =>
  saasApi.patch<ProviderOptionAdminRow>(`/platform/provider-options/${encodeURIComponent(optionId)}/description`, {
    frontend_description,
  })

export const platformEnableProviderOption = (optionId: string) =>
  saasApi.post<ProviderOptionAdminRow>(`/platform/provider-options/${encodeURIComponent(optionId)}/enable`, {})

export const platformDisableProviderOption = (optionId: string) =>
  saasApi.post<ProviderOptionAdminRow>(`/platform/provider-options/${encodeURIComponent(optionId)}/disable`, {})

export const platformSetDefaultProviderOption = (optionId: string) =>
  saasApi.post<ProviderOptionAdminRow>(`/platform/provider-options/${encodeURIComponent(optionId)}/set-default`, {})

export const platformReorderProviderOptions = (capability: string, ordered_option_ids: string[]) =>
  saasApi.post<{ capability: string; canvas_node_type: string | null; options: ProviderOptionAdminRow[] }>(
    '/platform/provider-options/reorder',
    { capability, ordered_option_ids },
  )

export const platformRemapProviderOption = (optionId: string, mapping: ProviderOptionMappingInput) =>
  saasApi.post<ProviderOptionAdminRow>(`/platform/provider-options/${encodeURIComponent(optionId)}/remap`, mapping)

// TASK-00183 — canonical Model Binding repair (distinct from remap
// above — never touches worker_id/provider_definition_id/credential_id/
// adapter_key/config, only provider_model_id).
export type ModelBindingCandidate = {
  id: string
  external_id: string
  display_name: string
  enabled: boolean
  capability_compatible: boolean
  is_current: boolean
}

export type ModelBindingCandidatesResponse = {
  option_id: string
  capability: string
  current_provider_model_id: string | null
  model_resolution_mode: string
  model_binding_status: string
  model_binding_safe_message: string | null
  candidates: ModelBindingCandidate[]
}

export const platformGetModelBindingCandidates = (optionId: string) =>
  saasApi.get<ModelBindingCandidatesResponse>(
    `/platform/provider-options/${encodeURIComponent(optionId)}/model-binding-candidates`,
  )

export const platformRepairModelBinding = (optionId: string, providerModelId: string) =>
  saasApi.post<ProviderOptionAdminRow>(
    `/platform/provider-options/${encodeURIComponent(optionId)}/repair-model-binding`,
    { provider_model_id: providerModelId },
  )

export const platformListProviderOptionTargetWorkers = (capability: string) =>
  saasApi.get<{ workers: Array<{ id: string; name: string; status: string; online: boolean }> }>(
    '/platform/provider-options/targets/workers',
    { params: { capability } },
  )

/** VISTOERA_FUNCTION_NODE_PROVIDER_OPTION_ADAPTER_AUTO_RESOLUTION_P0_V1
 * — canonical execution-adapter resolution for this Provider, derived
 * server-side from ProviderDefinition ONLY (spec §7/§9). When
 * `auto_resolved` is true, `adapter_key` is the ONE correct value and
 * the Adapter selector must be hidden (read-only `adapter_display_label`
 * instead). When false (legacy FASHN multi-variant case), the admin may
 * still choose, but ONLY among `selectable_adapters`. */
export type ProviderOptionTargetProviderRow = {
  id: string
  provider_key: string
  display_name: string
  provider_type: string
  adapter_type: string | null
  credential_required: boolean
  execution_target_type: string
  auto_resolved: boolean
  adapter_key: string | null
  adapter_display_label: string | null
  selectable_adapters: Array<{ key: string; display_name: string }>
}

export const platformListProviderOptionTargetProviders = (capability: string) =>
  saasApi.get<{
    providers: ProviderOptionTargetProviderRow[]
  }>('/platform/provider-options/targets/providers', { params: { capability } })

export const platformSetProviderOptionWorkerOnline = (workerId: string) =>
  saasApi.post<{ worker_id: string; status: string }>(
    `/platform/provider-options/workers/${encodeURIComponent(workerId)}/online`,
    {},
  )

export const platformSetProviderOptionWorkerOffline = (workerId: string) =>
  saasApi.post<{ worker_id: string; status: string }>(
    `/platform/provider-options/workers/${encodeURIComponent(workerId)}/offline`,
    {},
  )

/** Task spec section 30 — Platform Admin cleanup for disposable/E2E
 * fixture Provider Option rows. Distinct from disable (no undo). */
export const platformDeleteProviderOption = (optionId: string) =>
  saasApi.delete<{ deleted: boolean; id: string }>(`/platform/provider-options/${encodeURIComponent(optionId)}`)

// ---------------------------------------------------------------------
// VISTOERA_CANONICAL_NODE_REGISTRY_PROVIDER_INVENTORY_ICON_SYNC_AND_
// QUICK_ACTION_MANAGEMENT_V1 — canonical Node Icon override + Quick
// Action Registry admin endpoints (spec §9–§11/§15/§18). Both are
// Platform Admin only (`require_platform_admin`, same boundary as
// every other `/platform/provider-options/*` route above).
// ---------------------------------------------------------------------

export const platformGetNodeIcons = () =>
  saasApi.get<{ overrides: Record<string, string>; catalog: string[] }>('/platform/provider-options/node-icons')

export const platformSetNodeIcon = (
  nodeType: string,
  iconKey: string,
  opts?: { reassign?: boolean; displaceNodeTypes?: string[] },
) =>
  saasApi.patch<{
    node_type: string
    icon_key: string
    reassigned?: boolean
    displaced_to_fallback?: string[]
  }>(`/platform/provider-options/node-icons/${encodeURIComponent(nodeType)}`, {
    icon_key: iconKey,
    reassign: opts?.reassign ?? false,
    displace_node_types: opts?.displaceNodeTypes ?? [],
  })

export type QuickActionAdminRow = {
  action_key: string
  source_node_type: string
  source_output_type: string
  target_node_type: string
  target_capability: string | null
  label: string
  input_binding_strategy: string
  enabled_by_default: boolean
  enabled: boolean
  sort_order: number
}

export const platformListQuickActions = (sourceNodeType?: string) =>
  saasApi.get<{ actions: QuickActionAdminRow[] }>('/platform/provider-options/quick-actions', {
    params: sourceNodeType ? { source_node_type: sourceNodeType } : undefined,
  })

export const platformSetQuickAction = (actionKey: string, body: { enabled?: boolean; sort_order?: number }) =>
  saasApi.patch<QuickActionAdminRow>(`/platform/provider-options/quick-actions/${encodeURIComponent(actionKey)}`, body)

/** Read-only, workspace-scoped mirrors of the two admin registries
 * above — this is what Canvas actually consumes (spec §12/§38: fetched
 * ONCE per session/workspace bootstrap, never per-node/per-hover). */
export const getWorkspaceNodeIconOverrides = (workspaceId: string) =>
  saasApi.get<{ overrides: Record<string, string> }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/node-metadata/icon-overrides`,
  )

export const getWorkspaceQuickActions = (workspaceId: string, sourceNodeType?: string) =>
  saasApi.get<{ actions: QuickActionAdminRow[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/node-metadata/quick-actions`,
    { params: sourceNodeType ? { source_node_type: sourceNodeType } : undefined },
  )

// ---------------------------------------------------------------------
// VISTOERA_PLATFORM_ADMIN_CANVAS_NODE_CATALOG_ICON_ORDER_AVAILABILITY_
// AND_VISIBILITY_FOUNDATION_V2 — Platform Node Catalog control plane.
// Tenant GET is ONLINE+READY+creatable only; admin GET is full inventory.
// ---------------------------------------------------------------------

export type CanvasNodeCatalogEntry = {
  node_type: string
  display_name: string
  description: string | null
  category: string
  icon_ref: string
  platform_default_order: number
  availability_status: 'ONLINE' | 'OFFLINE' | string
  visibility_policy: string
  implementation_status: string
  is_enabled: boolean
  entitlement_key: string | null
  capability?: string | null
  family?: string | null
  creatable_by_tenant?: boolean
  supports_provider?: boolean
}

export type PlatformCustomIconRow = {
  id: string
  icon_ref: string
  storage_key: string
  url: string | null
  content_type: string
  original_filename: string | null
  byte_size: number
  created_by: string | null
  created_at: string | null
}

export const platformListNodeCatalog = () =>
  saasApi.get<{ entries: CanvasNodeCatalogEntry[] }>('/platform/node-catalog')

export const platformReorderNodeCatalog = (orderedNodeTypes: string[]) =>
  saasApi.post<{ entries: CanvasNodeCatalogEntry[] }>('/platform/node-catalog/reorder', {
    ordered_node_types: orderedNodeTypes,
  })

export const platformSetNodeCatalogAvailability = (
  nodeType: string,
  availabilityStatus: 'ONLINE' | 'OFFLINE',
) =>
  saasApi.patch<CanvasNodeCatalogEntry>(
    `/platform/node-catalog/${encodeURIComponent(nodeType)}/availability`,
    { availability_status: availabilityStatus },
  )

export const platformSetNodeCatalogIcon = (nodeType: string, iconRef: string) =>
  saasApi.patch<CanvasNodeCatalogEntry>(`/platform/node-catalog/${encodeURIComponent(nodeType)}/icon`, {
    icon_ref: iconRef,
  })

export const platformListNodeCatalogIcons = () =>
  saasApi.get<{ icons: PlatformCustomIconRow[]; approved_builtin_keys: string[] }>(
    '/platform/node-catalog/icons',
  )

export const platformUploadNodeCatalogIcon = (file: File) => {
  const form = new FormData()
  form.append('file', file)
  return saasApi.post<PlatformCustomIconRow>('/platform/node-catalog/icons', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const platformDeleteNodeCatalogIcon = (iconRef: string) =>
  saasApi.delete<{ deleted: boolean }>(`/platform/node-catalog/icons/${encodeURIComponent(iconRef)}`)

/** Tenant/workspace catalog — ONLINE + READY + creatable. Fetch once per Canvas session. */
export const getWorkspaceNodeCatalog = (workspaceId: string) =>
  saasApi.get<{ entries: CanvasNodeCatalogEntry[]; availability_by_node_type?: Record<string, string> }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/node-catalog`,
  )

/** Credential Center / Secret Vault list — used by Provider Option
 * EXTERNAL_API picker AND the Credentials admin page. Explicit allowlist
 * serializer; never returns plaintext / ciphertext. */
export const platformListProviderCredentials = (providerKey?: string) =>
  saasApi.get<ProviderCredentialListResponse>('/platform/provider-credentials', {
    params: providerKey ? { provider_key: providerKey } : undefined,
  })

// Agent Runtime Service Identity (separate principal from Workers)
export type AgentRuntimeRow = {
  agent_id: string
  name: string
  environment: string
  control_status: string
  status: string
  ready: boolean
  online: boolean
  credential_version: number
  credential_revoked: boolean
  runtime_version: string | null
  last_seen_at: string | null
  created_at: string | null
}
export const platformListAgentRuntimes = () =>
  saasApi.get<{ items: AgentRuntimeRow[] }>('/platform/agent-runtimes')
export const platformCreateAgentEnrollmentToken = (body: {
  label?: string
  ttl_seconds?: number
  max_uses?: number
  environment?: string
}) =>
  saasApi.post<{
    id: string
    enrollment_token: string
    label: string | null
    environment: string
    expires_at: string
    max_uses: number
  }>('/platform/agent-runtimes/enrollment-tokens', body)
export const platformListAgentEnrollmentTokens = () =>
  saasApi.get<{
    items: Array<{
      id: string
      label: string | null
      environment: string
      created_at: string | null
      expires_at: string | null
      max_uses: number
      use_count: number
      revoked: boolean
      last_used_by_agent_id: string | null
    }>
  }>('/platform/agent-runtimes/enrollment-tokens')
export const platformRevokeAgentEnrollmentToken = (tokenId: string) =>
  saasApi.post<{ id: string; revoked: boolean }>(
    `/platform/agent-runtimes/enrollment-tokens/${encodeURIComponent(tokenId)}/revoke`,
    {},
  )
export const platformEnableAgentRuntime = (agentId: string) =>
  saasApi.post<AgentRuntimeRow>(`/platform/agent-runtimes/${encodeURIComponent(agentId)}/enable`, {})
export const platformDisableAgentRuntime = (agentId: string) =>
  saasApi.post<AgentRuntimeRow>(`/platform/agent-runtimes/${encodeURIComponent(agentId)}/disable`, {})
export const platformRevokeAgentCredential = (agentId: string) =>
  saasApi.post<{ agent_id: string; revoked: boolean }>(
    `/platform/agent-runtimes/${encodeURIComponent(agentId)}/credential/revoke`,
    {},
  )

// Platform Admin Resource & Task Control V2 (spec §十七-§七十二) — the
// server-side filtered/paginated/sorted Task Control table + single/batch
// delete + manual queue reorder. Same `require_platform_admin` boundary as
// every other /platform/* call above.
export interface PlatformListTasksParams {
  page?: number
  page_size?: number
  status?: string
  job_type?: string
  worker_id?: string
  /** VistoeraTASK-00362 — canonical "类型"/"执行方" filters; supersede
   * `job_type`/`worker_id` above for the UI (which stay supported
   * server-side for any existing deep-link). */
  business_type?: string
  executor?: string
  search?: string
  date_from?: string
  date_to?: string
  sort?: string
  sort_dir?: string
}

export interface PlatformTaskFilterOptionItem {
  value: string
  label: string
}

export interface PlatformTaskFilterOptionsResponse {
  task_types: PlatformTaskFilterOptionItem[]
  api_executors: PlatformTaskFilterOptionItem[]
}

export const platformListTasks = (params: PlatformListTasksParams) =>
  saasApi.get<PlatformTaskListResponse>('/platform/tasks', { params })
export const platformGetTaskFilterOptions = () =>
  saasApi.get<PlatformTaskFilterOptionsResponse>('/platform/tasks/filter-options')
export const platformGetTaskDetail = (taskId: string) =>
  saasApi.get<PlatformTaskDetail>(`/platform/tasks/${encodeURIComponent(taskId)}`)
export const platformDeleteTask = (taskId: string) =>
  saasApi.delete<PlatformTaskDeleteResult>(`/platform/tasks/${encodeURIComponent(taskId)}`)
export const platformBatchDeleteTasks = (taskIds: string[]) =>
  saasApi.post<PlatformTaskBatchDeleteResult>('/platform/tasks/batch-delete', { task_ids: taskIds })
export const platformReorderTask = (taskId: string, newIndex: number) =>
  saasApi.post<PlatformTaskReorderResult>(`/platform/tasks/${encodeURIComponent(taskId)}/reorder`, { new_index: newIndex })
export const platformPinTask = (taskId: string) =>
  saasApi.post<PlatformTaskPinResult>(`/platform/tasks/${encodeURIComponent(taskId)}/pin`, {})
export const platformRedoTask = (taskId: string) =>
  saasApi.post<PlatformTaskRedoResult>(`/platform/tasks/${encodeURIComponent(taskId)}/redo`, {})
export const platformGetTaskResult = (taskId: string) =>
  saasApi.get<PlatformTaskResult>(`/platform/tasks/${encodeURIComponent(taskId)}/result`)
/** Platform Admin Task Result Viewer — canonical same-origin media URL for
 * `<img>`/`<video>` tags. Preview gate exempts these GET paths; platform
 * admin session cookie provides auth (same pattern as videoGenTaskOutputUrl). */
export const platformTaskMediaUrl = (taskId: string, kind: string) =>
  `${SAAS_API_ORIGIN}/api/saas/platform/tasks/${encodeURIComponent(taskId)}/media/${encodeURIComponent(kind)}`
/** Same-origin forced download (`Content-Disposition: attachment`) for
 * succeeded video_generation tasks — never fetch→Blob. */
export const platformTaskMediaDownloadUrl = (taskId: string, kind: string) =>
  `${platformTaskMediaUrl(taskId, kind)}?download=true`
export const platformPauseTask = (taskId: string) =>
  saasApi.post<PlatformTaskPauseResult>(`/platform/tasks/${encodeURIComponent(taskId)}/pause`, {})
export const platformResumeTask = (taskId: string) =>
  saasApi.post<PlatformTaskPauseResult>(`/platform/tasks/${encodeURIComponent(taskId)}/resume`, {})
export const platformBatchPauseTasks = (taskIds: string[]) =>
  saasApi.post<PlatformTaskBatchPauseResult>('/platform/tasks/batch-pause', { task_ids: taskIds })
export const platformBatchResumeTasks = (taskIds: string[]) =>
  saasApi.post<PlatformTaskBatchResumeResult>('/platform/tasks/batch-resume', { task_ids: taskIds })
export const platformListAuditLog = (params?: {
  page?: number
  page_size?: number
  action?: string
  // Tenant 360 "操作记录" tab (task spec §49).
  workspace_id?: string
}) => saasApi.get<PlatformAuditLogResponse>('/platform/audit-log', { params })

// VISTOERA_AI_CHAT_CONTROL_PLANE_P0 — Platform Admin AI Chat Control Plane.
// Reads/mutates the exact same AIChatConversation/AIChatMessage/
// AIChatExecution + Job rows the Tenant AI Chat feature and generic Task
// Contract already own; this is purely an admin-scoped observability +
// control surface, never a second task system.
export interface AIChatAdminListParams {
  page?: number
  page_size?: number
  provider?: string
  status?: string
  worker_id?: string
  workspace_id?: string
  date_from?: string
  date_to?: string
  keyword?: string
  sort?: string
  sort_dir?: string
}

export const platformGetAIChatMetrics = (params: Omit<AIChatAdminListParams, 'page' | 'page_size' | 'sort' | 'sort_dir'>) =>
  saasApi.get<AIChatAdminMetrics>('/platform/ai-chat/metrics', { params })
export const platformListAIChatExecutions = (params: AIChatAdminListParams) =>
  saasApi.get<AIChatAdminExecutionListResponse>('/platform/ai-chat/executions', { params })
export const platformGetAIChatExecutionDetail = (executionId: string) =>
  saasApi.get<AIChatAdminExecutionDetail>(`/platform/ai-chat/executions/${encodeURIComponent(executionId)}`)
export const platformCancelAIChatExecution = (executionId: string) =>
  saasApi.post<AIChatAdminExecutionDetail>(`/platform/ai-chat/executions/${encodeURIComponent(executionId)}/cancel`, {})
export const platformRetryAIChatExecution = (executionId: string) =>
  saasApi.post<AIChatAdminExecutionDetail>(`/platform/ai-chat/executions/${encodeURIComponent(executionId)}/retry`, {})
export const platformListAIChatConversationAggregate = (
  params: Pick<AIChatAdminListParams, 'page' | 'page_size' | 'workspace_id' | 'date_from' | 'date_to' | 'keyword'>
) => saasApi.get<AIChatAdminConversationAggregateResponse>('/platform/ai-chat/conversations', { params })
export const platformGetAIChatConversationDetail = (conversationId: string) =>
  saasApi.get<AIChatAdminConversationDetail>(`/platform/ai-chat/conversations/${encodeURIComponent(conversationId)}`)
/** Canonical Platform-Admin-authenticated byte delivery for a chat
 * attachment thumbnail — never a raw R2 URL (spec §53). */
export const platformAIChatAssetContentUrl = (assetId: string) =>
  `${SAAS_API_ORIGIN}/api/saas/platform/ai-chat/assets/${encodeURIComponent(assetId)}/content`

// ---------------------------------------- External Services / Infrastructure
// Registry (P0-B) — GET/POST /api/saas/platform/external-services*.
export const platformListExternalServices = () =>
  saasApi.get<ExternalServicesListResponse>('/platform/external-services')
export const platformGetExternalServiceDetail = (serviceId: string) =>
  saasApi.get<ExternalServiceRow>(`/platform/external-services/${encodeURIComponent(serviceId)}`)
export const platformRefreshExternalService = (serviceId: string) =>
  saasApi.post<ExternalServiceRow>(`/platform/external-services/${encodeURIComponent(serviceId)}/refresh`, {})

// ---------------------------------------- Provider Credential Center /
// Secret Vault Foundation V1 — Platform Admin only. Responses never
// include plaintext / ciphertext / encryption metadata (spec §9/§20).
// List lives with Provider Option helpers above (`platformListProviderCredentials`)
// so the EXTERNAL_API picker and this admin page share one client.
export const platformListCredentialProviders = () =>
  saasApi.get<ProviderListResponse>('/platform/provider-credentials/providers')
export const platformCreateProviderDefinition = (body: {
  provider_key: string
  display_name: string
  supported_capabilities?: string | string[]
  adapter_key?: string | null
  credential_required?: boolean
  credential_schema?: Record<string, unknown> | null
  provider_type?: string
}) => saasApi.post<ProviderDefinitionRow>('/platform/provider-credentials/providers', body)
export const platformCreateOpenAICompatibleProvider = (body: {
  provider_key: string
  display_name: string
  base_url: string
  // Canonical field going forward (spec §1-§5) — which Function Nodes
  // to atomically bind at create time. Default `[]` — nothing
  // auto-selected.
  function_node_capabilities?: string[]
  // Legacy/back-compat only — omit to let the backend derive the full
  // OPENAI_COMPATIBLE text-compatibility set automatically.
  supported_capabilities?: string[]
}) => saasApi.post<ProviderDefinitionRow>('/platform/provider-credentials/providers/openai-compatible', body)

// Dynamic Function Node Selector (spec §6/§7) — ALL FUNCTION NODES ∩
// PROVIDER ADAPTER COMPATIBILITY. Pass `providerId` (Edit Provider) to
// also flag which nodes are already bound to that Provider.
export const platformListCompatibleFunctionNodes = (adapterType: string, providerId?: string) =>
  saasApi.get<{ nodes: CompatibleFunctionNodeRow[] }>('/platform/provider-credentials/providers/compatible-function-nodes', {
    params: { adapter_type: adapterType, provider_id: providerId },
  })

// Edit Provider — "可用功能节点" add/remove flow (spec §17-§19). Binds/
// unbinds an ALREADY-EXISTING ProviderDefinition onto a Function Node
// without touching the Global Provider row or its Credential.
export const platformBindProviderFunctionNodes = (providerId: string, capabilities: string[]) =>
  saasApi.post<{ nodes: CompatibleFunctionNodeRow[] }>(
    `/platform/provider-credentials/providers/${encodeURIComponent(providerId)}/function-nodes`,
    { capabilities },
  )
export const platformUnbindProviderFunctionNode = (providerId: string, capability: string) =>
  saasApi.delete<{ nodes: CompatibleFunctionNodeRow[] }>(
    `/platform/provider-credentials/providers/${encodeURIComponent(providerId)}/function-nodes/${encodeURIComponent(capability)}`,
  )
export const platformUpdateProviderDefinition = (
  providerId: string,
  body: { display_name?: string; status?: string },
) => saasApi.patch<ProviderDefinitionRow>(`/platform/provider-credentials/providers/${encodeURIComponent(providerId)}`, body)
export const platformGetCredentialProvider = (providerId: string) =>
  saasApi.get<ProviderDefinitionRow>(`/platform/provider-credentials/providers/${encodeURIComponent(providerId)}`)
export const platformGetProviderCredential = (credentialId: string) =>
  saasApi.get<ProviderCredentialRow>(`/platform/provider-credentials/${encodeURIComponent(credentialId)}`)
export const platformCreateProviderCredential = (body: {
  provider_key: string
  name: string
  environment: ProviderCredentialEnvironment
  secret: Record<string, string>
  priority?: number
  account_label?: string
}) => saasApi.post<ProviderCredentialRow>('/platform/provider-credentials', body)
export const platformUpdateProviderCredentialMetadata = (
  credentialId: string,
  body: { name?: string; priority?: number; account_label?: string },
) =>
  saasApi.patch<ProviderCredentialRow>(
    `/platform/provider-credentials/${encodeURIComponent(credentialId)}/metadata`,
    body,
  )
export const platformRotateProviderCredential = (credentialId: string, secret: Record<string, string>) =>
  saasApi.post<ProviderCredentialRow>(
    `/platform/provider-credentials/${encodeURIComponent(credentialId)}/versions`,
    { secret },
  )
export const platformActivateProviderCredentialVersion = (credentialId: string, versionId: string) =>
  saasApi.post<ProviderCredentialRow>(
    `/platform/provider-credentials/${encodeURIComponent(credentialId)}/versions/${encodeURIComponent(versionId)}/activate`,
    {},
  )
export const platformRevokeProviderCredentialVersion = (credentialId: string, versionId: string) =>
  saasApi.post<ProviderCredentialRow>(
    `/platform/provider-credentials/${encodeURIComponent(credentialId)}/versions/${encodeURIComponent(versionId)}/revoke`,
    {},
  )
export const platformDisableProviderCredential = (credentialId: string) =>
  saasApi.post<ProviderCredentialRow>(
    `/platform/provider-credentials/${encodeURIComponent(credentialId)}/disable`,
    {},
  )
export const platformEnableProviderCredential = (credentialId: string) =>
  saasApi.post<ProviderCredentialRow>(
    `/platform/provider-credentials/${encodeURIComponent(credentialId)}/enable`,
    {},
  )
export const platformTestProviderCredential = (credentialId: string) =>
  saasApi.post<TestConnectionResponse>(
    `/platform/provider-credentials/${encodeURIComponent(credentialId)}/test`,
    {},
  )
export const platformUpdateProviderCredentialEnvironment = (
  credentialId: string,
  environment: ProviderCredentialEnvironment,
) =>
  saasApi.patch<ProviderCredentialRow>(
    `/platform/provider-credentials/${encodeURIComponent(credentialId)}/environment`,
    { environment },
  )
export const platformResolveCheckProviderCredential = (body: {
  provider_key: string
  environment?: ProviderCredentialEnvironment
  selection?: string
  explicit_credential_id?: string
}) => saasApi.post<ResolveCheckResponse>('/platform/provider-credentials/resolve-check', body)

// ---------------------------------------- Model Catalog (ProviderModel) +
// NodeModelBinding — VISTOERA_CANONICAL_PROVIDER_MODEL_CATALOG_NODE_MODEL_
// BINDING_USAGE_BILLING_FOUNDATION_P0_V1. Platform Admin only.
export const platformListProviderModels = (params?: {
  search?: string
  provider_id?: string
  model_type?: string
  enabled?: boolean
  catalog_status?: string
  family?: string
  limit?: number
  offset?: number
}) => saasApi.get<ProviderModelListResponse>('/platform/provider-models', { params })
export const platformListProviderModelFacets = () =>
  saasApi.get<ProviderModelFacetsResponse>('/platform/provider-models/facets')
export const platformSetProviderModelEnabled = (modelId: string, enabled: boolean) =>
  saasApi.patch<ProviderModelRow>(`/platform/provider-models/${encodeURIComponent(modelId)}/enabled`, { enabled })
export const platformBatchSetProviderModelEnabled = (modelIds: string[], enabled: boolean) =>
  saasApi.post<{ items: ProviderModelRow[] }>('/platform/provider-models/batch-enabled', {
    model_ids: modelIds,
    enabled,
  })
export const platformSetProviderModelSortOrder = (modelId: string, sortOrder: number) =>
  saasApi.patch<ProviderModelRow>(`/platform/provider-models/${encodeURIComponent(modelId)}/sort-order`, {
    sort_order: sortOrder,
  })
export const platformUpdateProviderModelOverrides = (
  modelId: string,
  body: {
    display_name_override?: string | null
    description_override?: string | null
    clear_display_name_override?: boolean
    clear_description_override?: boolean
  },
) => saasApi.patch<ProviderModelRow>(`/platform/provider-models/${encodeURIComponent(modelId)}/overrides`, body)
export const platformCreateManualProviderModel = (body: {
  provider_id: string
  provider_model_id: string
  display_name: string
  model_type?: string
  family?: string | null
  version?: string | null
}) => saasApi.post<ProviderModelRow>('/platform/provider-models/manual', body)
export const platformSyncVolcengineArkModels = () =>
  saasApi.post<VolcengineArkSyncResult>('/platform/provider-models/sync/volcengine-ark', {})
export const platformSyncKlingModels = () =>
  saasApi.post<KlingSyncResult>('/platform/provider-models/sync/kling', {})
export const platformGetProviderOnboardingStatus = (providerKey: string) =>
  saasApi.get<ProviderOnboardingStatus>(`/platform/provider-credentials/onboarding-status/${providerKey}`)

export const platformListNodeModelBindings = (capability: string) =>
  saasApi.get<{ items: NodeModelBindingRow[] }>('/platform/node-model-bindings', { params: { capability } })
export const platformListBindableModels = (capability: string) =>
  saasApi.get<{ items: BindableModelRow[] }>('/platform/node-model-bindings/bindable-models', {
    params: { capability },
  })
export const platformListExecutionOptionModels = (capability: string, providerDefinitionId: string) =>
  saasApi.get<{ items: BindableModelRow[] }>('/platform/node-model-bindings/execution-option-models', {
    params: { capability, provider_definition_id: providerDefinitionId },
  })
export const platformBulkAddNodeModelBindings = (body: {
  capability: string
  provider_model_ids: string[]
  tenant_visible?: boolean
}) => saasApi.post<{ items: NodeModelBindingRow[] }>('/platform/node-model-bindings/bulk-add', body)
export const platformSetNodeModelBindingEnabled = (bindingId: string, enabled: boolean) =>
  saasApi.patch<NodeModelBindingRow>(`/platform/node-model-bindings/${encodeURIComponent(bindingId)}/enabled`, {
    enabled,
  })
export const platformSetNodeModelBindingDefault = (bindingId: string) =>
  saasApi.post<NodeModelBindingRow>(`/platform/node-model-bindings/${encodeURIComponent(bindingId)}/default`, {})
export const platformSetNodeModelBindingSortOrder = (bindingId: string, sortOrder: number) =>
  saasApi.patch<NodeModelBindingRow>(`/platform/node-model-bindings/${encodeURIComponent(bindingId)}/sort-order`, {
    sort_order: sortOrder,
  })
export const platformDeleteNodeModelBinding = (bindingId: string) =>
  saasApi.delete<{ ok: boolean }>(`/platform/node-model-bindings/${encodeURIComponent(bindingId)}`)

// ---------------------------------------- Model Capability Registry —
// VISTOERA_MODEL_CAPABILITY_REGISTRY_OFFICIAL_SYNC_ADMIN_OVERRIDE_
// EFFECTIVE_RESOLUTION_AND_FUNCTION_NODE_MODEL_SELECTION_P0_V1. Platform
// Admin only (Tenant/Workspace foundation is a separate, much narrower
// eligible-models-only call below).
export const platformListModelCapabilities = (params?: {
  category?: string
  search?: string
  provider_id?: string
  capability_status?: string
  limit?: number
  offset?: number
}) => saasApi.get<ModelCapabilityListResponse>('/platform/model-capabilities', { params })
export const platformGetModelCapabilityDetail = (providerModelId: string) =>
  saasApi.get<ModelCapabilityDetail>(`/platform/model-capabilities/${encodeURIComponent(providerModelId)}`)
export const platformListModelCapabilitySnapshots = (providerModelId: string) =>
  saasApi.get<{ items: ModelCapabilitySnapshotRow[] }>(
    `/platform/model-capabilities/${encodeURIComponent(providerModelId)}/snapshots`,
  )
export const platformSyncModelCapability = (providerModelId: string) =>
  saasApi.post<{ synced: boolean; reason?: string; changed?: boolean; snapshot_id?: string }>(
    `/platform/model-capabilities/${encodeURIComponent(providerModelId)}/sync`,
    {},
  )
export const platformSyncAllModelCapabilities = () =>
  saasApi.post<ModelCapabilitySyncAllResult>('/platform/model-capabilities/sync-all', {})
export const platformActivateModelCapabilitySnapshot = (snapshotId: string) =>
  saasApi.post<ModelCapabilitySnapshotRow>(`/platform/model-capabilities/snapshots/${encodeURIComponent(snapshotId)}/activate`, {})
export const platformRejectModelCapabilitySnapshot = (snapshotId: string) =>
  saasApi.post<ModelCapabilitySnapshotRow>(`/platform/model-capabilities/snapshots/${encodeURIComponent(snapshotId)}/reject`, {})
export const platformSetModelCapabilityOverride = (
  providerModelId: string,
  capabilityKey: string,
  body: {
    override_type: CapabilityOverrideType
    reason?: string | null
    evidence_run_id?: string | null
    note?: string | null
    detail?: Record<string, unknown> | null
  },
) =>
  saasApi.put<ModelCapabilityOverrideRow>(
    `/platform/model-capabilities/${encodeURIComponent(providerModelId)}/overrides/${encodeURIComponent(capabilityKey)}`,
    body,
  )
export const platformRemoveModelCapabilityOverride = (providerModelId: string, capabilityKey: string) =>
  saasApi.delete<{ ok: boolean; capability_key: string }>(
    `/platform/model-capabilities/${encodeURIComponent(providerModelId)}/overrides/${encodeURIComponent(capabilityKey)}`,
  )
export const platformGetEffectiveModelCapability = (providerModelId: string) =>
  saasApi.get<{ provider_model_id: string; capability_status: CapabilityStatus; official: NormalizedCapability; effective: NormalizedCapability }>(
    `/platform/model-capabilities/${encodeURIComponent(providerModelId)}/effective`,
  )
export const platformFindEligibleModels = (body: { function_node_capability: string; requested?: Record<string, unknown> }) =>
  saasApi.post<{ items: EligibleModelRow[] }>('/platform/model-capabilities/eligible-models', body)

export const workspaceFindEligibleModels = (
  workspaceId: string,
  body: { function_node_capability: string; requested?: Record<string, unknown> },
) => saasApi.post<{ items: EligibleModelRow[] }>(`/workspaces/${encodeURIComponent(workspaceId)}/model-capabilities/eligible-models`, body)

// ---------------------------------------- Skill Library / Registry V1
export const platformListSkills = (params?: {
  domain?: string
  stage_key?: string
  status?: string
  q?: string
  view?: string
  /** VISTOERA_SKILL_PACKAGE_REGISTRY_..._P0_V1 §6 — filter to Skills that
   * declare compatibility with this capability (used by the Function Node
   * binding picker so it never offers an incompatible Skill). */
  capability?: string
}) => saasApi.get<SkillLibraryListResponse>('/platform/skills', { params })
export const platformGetSkillCatalog = () => saasApi.get<SkillCatalogPayload>('/platform/skills/catalog')
export const platformGetSkillMetrics = () => saasApi.get<SkillLibraryMetrics>('/platform/skills/metrics')
export const platformGetSkill = (skillId: string) =>
  saasApi.get<SkillDetail>(`/platform/skills/${encodeURIComponent(skillId)}`)
export const platformCreateSkill = (body: {
  skill_key: string
  display_name: string
  domain: string
  description?: string
  stage_key?: string
  category?: string
  compatible_capabilities?: string[]
}) => saasApi.post<SkillDetail>('/platform/skills', body)

/** Definition-level metadata edit (never version content) — primarily used
 * to declare/adjust `compatible_capabilities` after creation or import so a
 * Skill becomes eligible for Function Node binding (§6). */
export const platformUpdateSkillDefinition = (
  skillId: string,
  body: {
    display_name?: string
    description?: string
    category?: string
    compatible_capabilities?: string[]
    visibility?: string
  },
) => saasApi.patch<SkillDetail>(`/platform/skills/${encodeURIComponent(skillId)}`, body)

/** VISTOERA_SKILL_PACKAGE_REGISTRY_..._P0_V1 §9 — import a `vistoera.skill/v1`
 * `.zip` package. Always lands as UNTRUSTED + DRAFT (IMPORT_AUTO_PRODUCTION=NO)
 * regardless of `sourceKind`; an admin must explicitly approve-import before
 * the resulting version can ever be promoted to PRODUCTION. */
export const platformImportSkillPackage = (file: File, sourceKind: 'VISTOERA_PACKAGE' | 'EXTERNAL' = 'EXTERNAL') => {
  const form = new FormData()
  form.append('file', file)
  return saasApi.post<SkillDetail>('/platform/skills/import', form, {
    params: { source_kind: sourceKind },
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const platformApproveImportReview = (skillId: string, versionId: string) =>
  saasApi.post<SkillDetail>(
    `/platform/skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}/approve-import`,
  )

export const platformRejectImportReview = (skillId: string, versionId: string, reason?: string) =>
  saasApi.post<SkillDetail>(
    `/platform/skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}/reject-import`,
    reason ? { reason } : {},
  )
export const platformCreateSkillVersion = (
  skillId: string,
  body: { base_version_id?: string; change_summary?: string; content?: Record<string, unknown> },
) => saasApi.post<SkillVersionDetail>(`/platform/skills/${encodeURIComponent(skillId)}/versions`, body)
export const platformPatchSkillVersion = (
  skillId: string,
  versionId: string,
  body: Record<string, unknown>,
) =>
  saasApi.patch<SkillVersionDetail>(
    `/platform/skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}`,
    body,
  )
export const platformDeploySkill = (
  skillId: string,
  body: { skill_version_id: string; target_status: string; traffic_weight?: number; environment?: string },
) => saasApi.post<SkillDetail>(`/platform/skills/${encodeURIComponent(skillId)}/deploy`, body)
export const platformRollbackSkill = (
  skillId: string,
  body: { skill_version_id: string; environment?: string },
) => saasApi.post<SkillDetail>(`/platform/skills/${encodeURIComponent(skillId)}/rollback`, body)

export interface NodeSkillBindingRow {
  id: string
  capability: string
  canvas_node_type?: string | null
  skill_id: string
  skill_key?: string | null
  display_name?: string | null
  description?: string | null
  ownership_type?: string
  ownership_label?: string
  pricing_type?: string
  skill_status?: string | null
  lifecycle_status?: string | null
  version?: string | null
  version_id?: string | null
  enabled: boolean
  is_default: boolean
  sort_order: number
  calls_30d?: number | null
}

export interface NodeSkillBindingsResponse {
  capability: string
  canvas_node_type?: string | null
  stats: {
    bound_count: number
    published_count: number
    enabled_count: number
    default_skill_name: string | null
    calls_30d: number | null
    avg_latency_ms: number | null
  }
  bindings: NodeSkillBindingRow[]
}

export const platformListNodeSkillBindings = (capability: string) =>
  saasApi.get<NodeSkillBindingsResponse>('/platform/skills/node-bindings', { params: { capability } })

/** Reverse lookup for the Skill Detail page's 节点绑定 tab — every Function
 * Node capability this Skill is currently bound to (§5/§6). */
export const platformGetSkillNodeBindings = (skillId: string) =>
  saasApi.get<NodeSkillBindingRow[]>(`/platform/skills/${encodeURIComponent(skillId)}/node-bindings`)

export const platformBindNodeSkill = (
  capability: string,
  body: { skill_id: string; canvas_node_type?: string; enabled?: boolean; is_default?: boolean },
) => saasApi.post<NodeSkillBindingRow>('/platform/skills/node-bindings', body, { params: { capability } })

export const platformReorderNodeSkillBindings = (capability: string, ordered_binding_ids: string[]) =>
  saasApi.post<NodeSkillBindingsResponse>(
    '/platform/skills/node-bindings/reorder',
    { ordered_binding_ids },
    { params: { capability } },
  )

export const platformEnableNodeSkillBinding = (bindingId: string) =>
  saasApi.post<NodeSkillBindingRow>(`/platform/skills/node-bindings/${encodeURIComponent(bindingId)}/enable`)

export const platformDisableNodeSkillBinding = (bindingId: string) =>
  saasApi.post<NodeSkillBindingRow>(`/platform/skills/node-bindings/${encodeURIComponent(bindingId)}/disable`)

export const platformSetNodeSkillDefault = (bindingId: string) =>
  saasApi.post<NodeSkillBindingRow>(`/platform/skills/node-bindings/${encodeURIComponent(bindingId)}/set-default`)

export const platformClearNodeSkillDefault = (bindingId: string) =>
  saasApi.post<NodeSkillBindingRow>(`/platform/skills/node-bindings/${encodeURIComponent(bindingId)}/clear-default`)

export const platformUnbindNodeSkill = (bindingId: string) =>
  saasApi.delete<NodeSkillBindingsResponse>(`/platform/skills/node-bindings/${encodeURIComponent(bindingId)}`)

export const platformPublishSkillOnline = (skillId: string) =>
  saasApi.post<SkillDetail>(`/platform/skills/${encodeURIComponent(skillId)}/publish-online`)

export const platformOfflineSkill = (skillId: string) =>
  saasApi.post<SkillDetail>(`/platform/skills/${encodeURIComponent(skillId)}/offline`)

// ---------------------------------------- Storage Operations (VISTOERA_
// EXTERNAL_INFRA_OPERATIONS_V1) — GET/POST /api/saas/platform/storage/*.
// Same require_platform_admin boundary as External Services above; never
// calls Cloudflare/Render directly from the frontend.
export const platformGetStorageOverview = (force = false) =>
  saasApi.get<StorageUsageBreakdown>('/platform/storage/overview', { params: force ? { force: true } : undefined })
export const platformGetDeepzoomStorage = () => saasApi.get<StorageDeepzoomSummary>('/platform/storage/deepzoom')
export const platformGetStorageLifecycle = () => saasApi.get<StorageLifecycleSnapshot>('/platform/storage/lifecycle')
export const platformGetStorageOrphanAudit = (force = false) =>
  saasApi.get<StorageOrphanAudit>('/platform/storage/audit', { params: force ? { force: true } : undefined })
export const platformRunStorageAuditScan = () => saasApi.post<StorageOrphanAudit>('/platform/storage/audit/scan', {})
export const platformRunCleanupDryRun = () => saasApi.post<StorageCleanupDryRun>('/platform/storage/cleanup/dry-run', {})
export const platformGetAssetStorageDetail = (assetId: string) =>
  saasApi.get<AssetStorageDetail>(`/platform/storage/asset/${encodeURIComponent(assetId)}`)
export const platformGetRenderBackendDetail = () => saasApi.get<RenderProviderDetail>('/platform/storage/render/backend')
export const platformGetRenderDatabaseDetail = () => saasApi.get<RenderProviderDetail>('/platform/storage/render/database')

// -------------------------------------------------------- Workspace/Site
export const listMyWorkspaces = () => saasApi.get<SaasWorkspace[]>('/workspaces')
export const getMyWorkspace = (workspaceId: string) => saasApi.get<SaasWorkspace>(`/workspaces/${workspaceId}`)
export const getMyWorkspaceEntitlements = (workspaceId: string) =>
  saasApi.get<EntitlementsOut>(`/workspaces/${workspaceId}/entitlements`)
export const listWorkspaceSites = (workspaceId: string) =>
  saasApi.get<SaasSite[]>(`/workspaces/${workspaceId}/sites`)
export const createWorkspaceSite = (
  workspaceId: string,
  data: { name: string; slug?: string; default_locale?: string },
) => saasApi.post<SaasSite>(`/workspaces/${workspaceId}/sites`, data)
export const getSite = (siteId: string) => saasApi.get<SaasSite>(`/sites/${siteId}`)
export const getSitePublishStatus = (siteId: string) => saasApi.get<PublishStatus>(`/sites/${siteId}/publish-status`)

// ------------------------------------------------------- Tenant Overview
// VISTOERA_TENANT_OVERVIEW_HOME_V1 — the Overview page's one aggregate
// call (spec §19) plus the separate, lazily-loaded trend call (spec §8).
export const getTenantOverview = (workspaceId: string) =>
  saasApi.get<TenantOverview>(`/workspaces/${workspaceId}/overview`)

/** VistoeraTASK-00589 — Overview home credit spend trend (7/14/30). */
export const getOverviewUsageTrend = (workspaceId: string, days: 7 | 14 | 30) =>
  saasApi.get<OverviewUsageTrendResponse>(`/workspaces/${workspaceId}/overview/usage-trend`, { params: { days } })

/** VistoeraTASK-00589 — member-readable team roster for Overview「团队」Tab. */
export const getOverviewTeamDirectory = (workspaceId: string, params?: { q?: string }) =>
  saasApi.get<OverviewTeamDirectory>(`/workspaces/${workspaceId}/overview/team-directory`, { params })
export const getStorageTrend = (workspaceId: string, days: 7 | 30 | 90) =>
  saasApi.get<StorageTrendResponse>(`/workspaces/${workspaceId}/overview/storage-trend`, { params: { days } })
// Stage S5 (Plan + Credits + Metering V1) — Upgrade comparison drawer's
// Plan Catalog read + the tenant's own paginated Credit Ledger. Both
// scoped by `require_workspace_member()` on the backend (never
// `require_platform_admin`) — a tenant can only ever reach their OWN
// workspace's rows here.
export const getTenantPlanCatalog = (workspaceId: string) =>
  saasApi.get<PlanCatalogRow[]>(`/workspaces/${workspaceId}/overview/plans`)
export const getTenantMembershipCatalog = (workspaceId: string) =>
  saasApi.get<TenantMembershipCatalog>(`/workspaces/${workspaceId}/overview/membership-catalog`)
export const getTenantCreditLedger = (workspaceId: string, params: { limit: number; offset: number }) =>
  saasApi.get<TenantCreditLedgerPage>(`/workspaces/${workspaceId}/overview/credit-ledger`, { params })

// ------------------------------------------------------------- Domains
export const listSiteDomains = (siteId: string) => saasApi.get<DomainBinding[]>(`/sites/${siteId}/domains`)
export const addSiteDomain = (siteId: string, hostname: string) =>
  saasApi.post<DomainBinding>(`/sites/${siteId}/domains`, { hostname })
export const updateSiteDomain = (
  siteId: string,
  domainId: string,
  data: { is_primary?: boolean; status?: string },
) => saasApi.patch<DomainBinding>(`/sites/${siteId}/domains/${domainId}`, data)
export const deleteSiteDomain = (siteId: string, domainId: string) =>
  saasApi.delete(`/sites/${siteId}/domains/${domainId}`)

// ---------------------------------------------------- Website Builder
// LiangCollection -> Vistoera "Collection Website" preset (Local Visible
// Vertical Slice) — all under the Site's own
// `/workspaces/{workspaceId}/sites/{siteId}/...` scope, same contract as
// `app/api/routes/saas_website.py`.
export const listSiteEntries = (workspaceId: string, siteId: string, contentType?: string) =>
  saasApi.get<ContentEntry[]>(`/workspaces/${workspaceId}/sites/${siteId}/entries`, {
    params: contentType ? { content_type: contentType } : undefined,
  })

export interface CreateEntryPayload {
  content_type: string
  slug: string
  label?: string
  fields?: Record<string, unknown>
  blocks?: Array<Record<string, unknown>>
  taxonomy_term_ids?: string[]
  status?: string
  visibility?: string
  secondary_label?: string | null
  cover_asset_id?: string | null
  sort_order?: number
}

export const createSiteEntry = (workspaceId: string, siteId: string, data: CreateEntryPayload) =>
  saasApi.post<ContentEntry>(`/workspaces/${workspaceId}/sites/${siteId}/entries`, data)

export const getSiteEntry = (workspaceId: string, siteId: string, entryId: string) =>
  saasApi.get<ContentEntry>(`/workspaces/${workspaceId}/sites/${siteId}/entries/${entryId}`)

export interface UpdateEntryPayload {
  label?: string
  fields?: Record<string, unknown>
  blocks?: Array<Record<string, unknown>>
  taxonomy_term_ids?: string[]
  status?: string
  visibility?: string
  secondary_label?: string | null
  cover_asset_id?: string | null
  sort_order?: number
}

export const updateSiteEntry = (workspaceId: string, siteId: string, entryId: string, data: UpdateEntryPayload) =>
  saasApi.patch<ContentEntry>(`/workspaces/${workspaceId}/sites/${siteId}/entries/${entryId}`, data)

export const deleteSiteEntry = (workspaceId: string, siteId: string, entryId: string) =>
  saasApi.delete(`/workspaces/${workspaceId}/sites/${siteId}/entries/${entryId}`)

// ITEM-DETAIL-PROGRESSIVE-IMAGE-P0 — mints a short-lived, single-entry
// preview token so "本地查看（公开预览）" can open the unauthenticated
// public detail page for an entry that isn't `published`/`public` yet.
// Item existence must never be coupled to publish status any more than
// to Deep Zoom readiness — see public_site.py::_preview_bypass_entry.
export const createEntryPreviewToken = (workspaceId: string, siteId: string, entryId: string) =>
  saasApi.post<{ token: string; expires_in_seconds: number }>(
    `/workspaces/${workspaceId}/sites/${siteId}/entries/${entryId}/preview-token`,
  )

// LiangCollection Full Website Absorption (v2) — real server-side
// search/filter/sort/pagination for the Items management table.
export interface EntrySearchParams {
  content_type?: string
  search?: string
  taxonomy_term_ids?: string[]
  status?: string
  visibility?: string
  image_status?: string
  sort?: string
  page?: number
  page_size?: number
}

export interface ContentEntrySearchResult {
  items: ContentEntry[]
  total: number
  page: number
  page_size: number
}

export const searchSiteEntries = (workspaceId: string, siteId: string, params: EntrySearchParams) =>
  saasApi.get<ContentEntrySearchResult>(`/workspaces/${workspaceId}/sites/${siteId}/entries/search`, { params })

export type EntryBulkAction = 'delete' | 'publish' | 'unpublish' | 'show' | 'hide' | 'add_terms'

export const bulkActionSiteEntries = (
  workspaceId: string,
  siteId: string,
  data: { entry_ids: string[]; action: EntryBulkAction; term_ids?: string[] },
) => saasApi.post<{ action: string; affected: number }>(`/workspaces/${workspaceId}/sites/${siteId}/entries/bulk-action`, data)

// ---------------------------------------------- Taxonomies (Categories/Topics)
// LiangCollection Full Website Absorption §"Categories"/"Topics" — generic
// Taxonomy/TaxonomyTerm core (app/api/routes/saas_taxonomies.py), never a
// museum-specific "Category" table.
export const listSiteTaxonomies = (workspaceId: string, siteId: string) =>
  saasApi.get<Taxonomy[]>(`/workspaces/${workspaceId}/sites/${siteId}/taxonomies`)

export const createSiteTaxonomy = (workspaceId: string, siteId: string, data: { key: string; name: string; is_hierarchical?: boolean }) =>
  saasApi.post<Taxonomy>(`/workspaces/${workspaceId}/sites/${siteId}/taxonomies`, data)

export const listTaxonomyTerms = (workspaceId: string, siteId: string, taxonomyId: string) =>
  saasApi.get<TaxonomyTerm[]>(`/workspaces/${workspaceId}/sites/${siteId}/taxonomies/${taxonomyId}/terms`)

export interface CreateTaxonomyTermPayload {
  slug: string
  name: string
  name_secondary?: string | null
  description?: string | null
  parent_id?: string | null
  cover_asset_id?: string | null
  sort_order?: number
}

export const createTaxonomyTerm = (workspaceId: string, siteId: string, taxonomyId: string, data: CreateTaxonomyTermPayload) =>
  saasApi.post<TaxonomyTerm>(`/workspaces/${workspaceId}/sites/${siteId}/taxonomies/${taxonomyId}/terms`, data)

export const updateTaxonomyTerm = (
  workspaceId: string,
  siteId: string,
  taxonomyId: string,
  termId: string,
  data: Partial<CreateTaxonomyTermPayload>,
) => saasApi.patch<TaxonomyTerm>(`/workspaces/${workspaceId}/sites/${siteId}/taxonomies/${taxonomyId}/terms/${termId}`, data)

export const deleteTaxonomyTerm = (workspaceId: string, siteId: string, taxonomyId: string, termId: string) =>
  saasApi.delete(`/workspaces/${workspaceId}/sites/${siteId}/taxonomies/${taxonomyId}/terms/${termId}`)

// ------------------------------------------ Generic Content Schema (A2)
export const listContentTypes = (workspaceId: string, siteId: string) =>
  saasApi.get<ContentTypeOut[]>(`/workspaces/${workspaceId}/sites/${siteId}/content-types`)

export const listContentTypeFields = (workspaceId: string, siteId: string, contentTypeId: string) =>
  saasApi.get<FieldDefinitionOut[]>(`/workspaces/${workspaceId}/sites/${siteId}/content-types/${contentTypeId}/fields`)

export interface CreateFieldPayload {
  key: string
  label: string
  field_type: FieldType
  required?: boolean
  sort_order?: number
  group_name?: string | null
  help_text?: string
  options?: FieldOption[]
  config?: Record<string, unknown>
  public_visibility?: boolean
  editor_visibility?: boolean
}

export const createContentTypeField = (workspaceId: string, siteId: string, contentTypeId: string, data: CreateFieldPayload) =>
  saasApi.post<FieldDefinitionOut>(`/workspaces/${workspaceId}/sites/${siteId}/content-types/${contentTypeId}/fields`, data)

export const updateContentTypeField = (workspaceId: string, siteId: string, fieldId: string, data: Partial<CreateFieldPayload> & { status?: 'active' | 'archived' }) =>
  saasApi.patch<FieldDefinitionOut>(`/workspaces/${workspaceId}/sites/${siteId}/fields/${fieldId}`, data)

export const deleteContentTypeField = (workspaceId: string, siteId: string, fieldId: string) =>
  saasApi.delete(`/workspaces/${workspaceId}/sites/${siteId}/fields/${fieldId}`)

// -------------------------------------------------------- Site Settings
export const getSiteSettings = (workspaceId: string, siteId: string) =>
  saasApi.get<SiteSettingsOut>(`/workspaces/${workspaceId}/sites/${siteId}/settings`)

export interface UpdateSiteSettingsPayload {
  title?: string
  description?: string
  contact_email?: string | null
  social?: Record<string, string>
  footer_text?: string
  language?: string
  vocabulary?: Record<string, { singular?: string; plural?: string }>
}

export const updateSiteSettings = (workspaceId: string, siteId: string, data: UpdateSiteSettingsPayload) =>
  saasApi.put<SiteSettingsOut>(`/workspaces/${workspaceId}/sites/${siteId}/settings`, data)

/** Uploads through the same tenant-isolated Asset system Website Builder
 * Entries must reference (spec §9/§10 — never a LiangCollection local
 * path/fixed Cloudflare URL, always a stable `asset_id`). */
export const uploadSiteAsset = (
  workspaceId: string,
  siteId: string,
  file: File,
  metadata?: Record<string, unknown>,
  options?: {
    onUploadProgress?: (loaded: number, total: number) => void
    /** VistoeraTASK-00523: destination folder (canonical collection id); omitted = 未整理图片. */
    collection_id?: string | null
  },
) => {
  const form = new FormData()
  form.append('file', file)
  if (options?.collection_id) form.append('collection_id', options.collection_id)
  // Open, non-business provenance bag — e.g. Canvas "另存编辑图片" records
  // {source_asset_id, source_canvas_id, source_node_id, operation:
  // "canvas_image_edit", crop, ...} here (spec §30). Never a schema change.
  if (metadata && Object.keys(metadata).length > 0) {
    form.append('metadata', JSON.stringify(metadata))
  }
  // Do NOT set Content-Type manually — the browser must attach the multipart
  // boundary. Timeout is for original-file transfer only (DeepZoom is async
  // on the server); this is not "wait for derivatives".
  return saasApi.post<SiteAsset>(`/workspaces/${workspaceId}/assets`, form, {
    params: { site_id: siteId },
    timeout: 180000,
    onUploadProgress: (evt) => {
      if (!options?.onUploadProgress) return
      const total = evt.total ?? 0
      const loaded = evt.loaded ?? 0
      options.onUploadProgress(loaded, total)
    },
  })
}

export const rotateSiteAsset = (workspaceId: string, assetId: string, quarters: number = 1) =>
  saasApi.post<SiteAsset>(`/workspaces/${workspaceId}/assets/${assetId}/rotate`, { quarters }, { timeout: 180000 })

export const siteAssetVariantUrl = (workspaceId: string, assetId: string, role: string = 'thumbnail') =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/assets/${assetId}/variants/${role}`

/** Reusable Asset Picker (spec §25) — lists this Site's own
 * `SiteAsset` rows (never the separate workspace-level Image Asset
 * Center / `AssetChain` pipeline library: Graphic Page blocks store an
 * `asset_id` that the public renderer resolves via
 * `saas_assets`/`public_site.py`'s `SiteAsset`-only lookup, so any
 * picker offered here must draw from the same table). */
export interface SiteAssetVariantMeta {
  role: string
  content_type: string
  size_bytes: number | null
  width: number | null
  height: number | null
}
export interface SiteAssetOut {
  id: string
  original_filename: string | null
  created_at: string | null
  variants: SiteAssetVariantMeta[]
}
export const listSiteAssets = (workspaceId: string, siteId: string) =>
  saasApi.get<{ items: SiteAssetOut[] }>(`/workspaces/${workspaceId}/assets`, { params: { site_id: siteId } })

// ---------------------------------------- Unified Image Asset Picker (V1)
export type ImageAssetPickerPurpose =
  | 'graphic_insert'
  | 'graphic_replace'
  | 'item_image'
  | 'item_cover'
  | 'site_cover'
  | 'video_input'
  | 'ai_chat_input'
  | 'image_editor_input'
  | 'canvas_insert'
  | 'video_keyframe'
  | 'video_shot_reference'
  | 'object_image_processing'
  | 'generic'

export type PickerResourceType =
  | 'all'
  | 'original'
  | 'transparent_background'
  | 'color_background'
  | 'shadow'
  | 'flat_rectified'
  | 'flat_enhanced'
  | 'ai_generated'
  | 'crop'
  | 'other'

export type PickerSourceFilter = 'all' | 'upload' | 'vistoera_generated' | 'ai_generated' | 'external_import'

export interface ImagePickerListItem {
  id: string
  reference_type: 'site_asset' | 'library_item'
  asset_id: string
  site_id: string | null
  resource_type: PickerResourceType
  resource_type_label: string
  source: PickerSourceFilter
  source_label: string
  filename: string
  width: number | null
  height: number | null
  format: string
  size_bytes: number | null
  created_at: string | null
  thumbnail_url: string | null
  family_id: string | null
  status: string
  /** Team mode (VistoeraTASK-00523) - present when the list was requested with a team scope. */
  created_by_user_id?: string | null
  visibility?: 'workspace' | 'private' | string
  collection_id?: string | null
  creator?: { user_id: string; display_name: string; is_me: boolean } | null
}

export interface ImagePickerListResponse {
  team?: { enabled: boolean; user_id: string; role: string }
  items: ImagePickerListItem[]
  /** Null while facets/total are still pending on a fast first page. */
  total: number | null
  page: number
  page_size: number
  summary: { resource_types: Record<string, number>; sources: Record<string, number> } | null
  resource_types: Array<{ value: string; label: string; count: number | null }>
  sources: Array<{ value: string; label: string; count: number | null }>
  facets_pending?: boolean
  fast_page?: boolean
}

export interface ImagePickerSummaryResponse {
  total: number
  summary: { resource_types: Record<string, number>; sources: Record<string, number> }
  resource_types: Array<{ value: string; label: string; count: number }>
  sources: Array<{ value: string; label: string; count: number }>
  facets_pending?: boolean
  cache_hit?: boolean
  universe?: string
}

export interface ImagePickerDetail extends ImagePickerListItem {
  variants?: Array<{
    id: string
    picker_item_id: string
    resource_type: string
    resource_type_label: string
    filename: string | null
    thumbnail_url: string | null
  }>
  usage?: { usage_count: number; last_used_at: string | null } | null
  preview_url?: string | null
  full_url?: string | null
  download_url?: string | null
  ai_provenance?: {
    source?: string | null
    generation_method?: string | null
    provider?: string | null
    provider_label?: string | null
    conversation_id?: string | null
    execution_id?: string | null
    generated_at?: string | null
  } | null
  /** Production Canvas Node Detail Shell / Image Asset UI V2 §13/§14 —
   * additive-only fields resolved server-side from whichever generation
   * pipeline (if any) produced this `SiteAsset` (see
   * `image_picker_service._resolve_generation_provenance`). Every other
   * existing consumer of this endpoint (Image Asset Picker/Center)
   * simply ignores these three keys. */
  source_kind?: 'generated' | 'uploaded' | 'asset_library'
  prompt?: string | null
  candidates?: Array<{ id: string; thumbnail_url: string | null; is_current: boolean }>
  _timings?: Record<string, number>
}

export interface ImageAssetPickerSelection {
  assetId: string
  pickerItemId: string
  referenceType: 'site_asset' | 'video_gen_asset' | 'ai_chat_asset'
  /** Best-effort display name from the picker card at confirm time. */
  filename?: string | null
}

export const listImagePickerItems = (
  workspaceId: string,
  params: {
    site_id?: string
    q?: string
    resource_type?: PickerResourceType
    source?: PickerSourceFilter
    sort?: 'recent' | 'oldest' | 'name'
    page?: number
    page_size?: number
    /** Canonical asset offset (upload-tile page-1 consumes one grid cell). */
    offset?: number
    include_library?: boolean
    /** When false, list returns without waiting for facet counts (Media Picker fast path). */
    include_summary?: boolean
  } & ImagePickerTeamParams,
) => saasApi.get<ImagePickerListResponse>(`/workspaces/${workspaceId}/image-picker`, { params })

/** VistoeraTASK-00523 - team-mode scope (server ignores it for personal Workspaces). */
export interface ImagePickerTeamParams {
  view?: 'mine' | 'shared' | 'all'
  creator_id?: string
  exclude_me?: boolean
  /** Folder id or `'unsorted'`. A folder includes its descendants by default. */
  collection_id?: string
  include_descendants?: boolean
}

// ---- Canonical folder tree (same API the Image Asset Center uses) ----------

export interface ImageFolder {
  id: string
  workspace_id: string
  name: string
  parent_id: string | null
  child_count: number
  is_system: boolean
  system_key: string | null
  /** Images directly in this folder (viewer-accessible only). */
  count: number
  /** Folder + all descendants (viewer-accessible only) - what entering it shows. */
  accessible_asset_count: number
  created_by: { user_id: string; display_name: string } | null
  created_at: string | null
}

export interface ImageFolderTeamContext {
  enabled: boolean
  active_member_count?: number
  user_id?: string
  role?: string
  workspace_name?: string | null
}

export interface ImageFoldersResponse {
  collections: ImageFolder[]
  unsorted_collection_id: string | null
  can_manage: boolean
  team: ImageFolderTeamContext
  scope_counts: {
    all: number
    mine: number
    shared: number
    shared_by_others: number
    unsorted: number
  } | null
}

export const listImageFolders = (workspaceId: string, signal?: AbortSignal) =>
  saasApi.get<ImageFoldersResponse>(`/workspaces/${workspaceId}/images/collections`, {
    params: { view: 'all' },
    signal,
  })
export const createImageFolder = (workspaceId: string, name: string, parentId: string | null) =>
  saasApi.post<ImageFolder>(`/workspaces/${workspaceId}/images/collections`, {
    name,
    parent_id: parentId,
  })
export const renameImageFolder = (workspaceId: string, folderId: string, name: string) =>
  saasApi.patch<ImageFolder>(`/workspaces/${workspaceId}/images/collections/${folderId}`, { name })
export const deleteImageFolder = (workspaceId: string, folderId: string) =>
  saasApi.delete<{ deleted: boolean }>(`/workspaces/${workspaceId}/images/collections/${folderId}`)

export const getImagePickerSummary = (
  workspaceId: string,
  params?: { site_id?: string; include_library?: boolean } & ImagePickerTeamParams,
) =>
  saasApi.get<ImagePickerSummaryResponse>(`/workspaces/${workspaceId}/image-picker/summary`, {
    params,
    // Facets intentionally run after first-page list; cold full-feed summarize
    // can exceed the default 30s axios timeout on large libraries.
    timeout: 90000,
  })

export const getImagePickerItemDetail = (
  workspaceId: string,
  pickerItemId: string,
  params?: { include_usage?: boolean; include_variants?: boolean },
) =>
  saasApi.get<ImagePickerDetail>(`/workspaces/${workspaceId}/image-picker/items/${encodeURIComponent(pickerItemId)}`, {
    params,
  })

export const previewImagePickerItemDelete = (workspaceId: string, pickerItemId: string) =>
  saasApi.get<{
    picker_item_id: string
    can_delete: boolean
    usage: { usage_count: number; by_kind?: Record<string, number>; last_used_at?: string | null }
    block_reason: string | null
  }>(`/workspaces/${workspaceId}/image-picker/items/${encodeURIComponent(pickerItemId)}/delete-preview`)

export const deleteImagePickerItem = (workspaceId: string, pickerItemId: string) =>
  saasApi.delete<{
    deleted: boolean
    picker_item_id: string
    reference_type: string
    asset_id: string
  }>(`/workspaces/${workspaceId}/image-picker/items/${encodeURIComponent(pickerItemId)}`)

/** VistoeraTASK-00249 — ONE real batch-intent request (not N sequential
 * `deleteImagePickerItem` calls) — see `image_picker_service.
 * batch_delete_picker_items`. Explicit accepted/rejected split so a
 * partial-invalid batch never silently under-deletes. Deliberately still
 * only a durable *logical* delete server-side — physical storage cleanup
 * for zero-reference items runs later on the async Job worker, so this
 * call itself never blocks on R2/local storage I/O. */
export const batchDeleteImagePickerItems = (workspaceId: string, pickerItemIds: string[]) =>
  saasApi.post<{
    accepted: Array<{ deleted: boolean; picker_item_id: string; reference_type: string; asset_id: string }>
    rejected: Array<{ picker_item_id: string; reason: string }>
  }>(`/workspaces/${workspaceId}/image-picker/items/batch-delete`, { picker_item_ids: pickerItemIds })

export const resolveImagePickerSelection = (
  workspaceId: string,
  data: { picker_item_id: string; target: 'site_asset' | 'video_gen_asset' | 'ai_chat_asset'; site_id?: string | null },
) =>
  saasApi.post<{ asset_id: string; reference_type: string; picker_item_id: string }>(
    `/workspaces/${workspaceId}/image-picker/resolve`,
    data,
    // Materialize + register as video_gen_asset / ai_chat_asset can exceed the default 30s
    // when R2 is slow; keep site_asset resolves fast but give bridge targets room.
    { timeout: data.target === 'site_asset' ? 60000 : 180000 },
  )

// ------------------------------------------------- Graphic Page (图文编辑)
// LiangCollection "展览入口" absorption — see
// docs/migration/LIANGCOLLECTION_GRAPHIC_EDITOR_FULL_AUDIT.md. Every call
// below mirrors `app/api/routes/saas_graphic_pages.py` 1:1 (path, verb,
// payload shape) — the router's own tenant-isolation contract
// (`workspace_id` + `site_id` in the URL, re-checked server-side) is the
// only isolation boundary; nothing here duplicates it client-side.
const graphicPagesBase = (workspaceId: string, siteId: string) =>
  `/workspaces/${workspaceId}/sites/${siteId}/graphic-pages`

export const listGraphicPages = (
  workspaceId: string,
  siteId: string,
  params?: { page_type?: string; status?: string; search?: string },
) => saasApi.get<GraphicPage[]>(graphicPagesBase(workspaceId, siteId), { params })

export const createGraphicPage = (workspaceId: string, siteId: string, data: { page_type: string; title?: string }) =>
  saasApi.post<GraphicPage>(graphicPagesBase(workspaceId, siteId), data)

export const getGraphicPageTree = (workspaceId: string, siteId: string, pageId: string) =>
  saasApi.get<GraphicPageTree>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}`)

export const getGraphicPageDraftPreview = (workspaceId: string, siteId: string, pageId: string) =>
  saasApi.get<GraphicPageTree>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/preview`)

export interface UpdateGraphicPageMetaPayload {
  title?: string
  subtitle?: string
  summary?: string
  author?: string
  frontend_visible?: boolean
  linked_entry_id?: string | null
}
export const updateGraphicPageMeta = (workspaceId: string, siteId: string, pageId: string, data: UpdateGraphicPageMetaPayload) =>
  saasApi.patch<GraphicPage>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}`, data)

export interface UpdateGraphicPageCoverPayload {
  cover_source_type?: string
  cover_asset_id?: string | null
  cover_pinned_block_id?: string | null
  cover_crop_desktop?: CropRect | null
  cover_crop_mobile?: CropRect | null
  cover_alt_text?: string
}
export const updateGraphicPageCover = (workspaceId: string, siteId: string, pageId: string, data: UpdateGraphicPageCoverPayload) =>
  saasApi.patch<GraphicPage>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/cover`, data)

export const deleteGraphicPage = (workspaceId: string, siteId: string, pageId: string) =>
  saasApi.delete(`${graphicPagesBase(workspaceId, siteId)}/${pageId}`)

export const duplicateGraphicPage = (workspaceId: string, siteId: string, pageId: string) =>
  saasApi.post<GraphicPage>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/duplicate`)

export type GraphicPageStatusAction = 'submit_review' | 'publish' | 'unpublish' | 'archive' | 'back_to_draft'
export const changeGraphicPageStatus = (workspaceId: string, siteId: string, pageId: string, action: GraphicPageStatusAction) =>
  saasApi.post<GraphicPage>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/status`, { action })

export const createGraphicModule = (workspaceId: string, siteId: string, pageId: string, data: { title?: string }) =>
  saasApi.post<GraphicPageModule>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/modules`, data)

export interface UpdateGraphicModulePayload {
  title?: string
  subtitle?: string
  intro?: string
  is_visible?: boolean
  show_in_toc?: boolean
}
export const updateGraphicModule = (
  workspaceId: string,
  siteId: string,
  pageId: string,
  moduleId: string,
  data: UpdateGraphicModulePayload,
) => saasApi.patch<GraphicPageModule>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/modules/${moduleId}`, data)

export const deleteGraphicModule = (workspaceId: string, siteId: string, pageId: string, moduleId: string) =>
  saasApi.delete(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/modules/${moduleId}`)

export const duplicateGraphicModule = (workspaceId: string, siteId: string, pageId: string, moduleId: string) =>
  saasApi.post<GraphicPageModule>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/modules/${moduleId}/duplicate`)

export const reorderGraphicModules = (workspaceId: string, siteId: string, pageId: string, order: string[]) =>
  saasApi.post(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/modules/reorder`, { order })

export const createGraphicBlock = (
  workspaceId: string,
  siteId: string,
  pageId: string,
  moduleId: string,
  data: { block_type: string; after_block_id?: string | null },
) => saasApi.post<GraphicPageBlock>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/modules/${moduleId}/blocks`, data)

export interface UpdateGraphicBlockPayload {
  content?: Record<string, unknown>
  is_visible?: boolean
  asset_id?: string | null
  linked_entry_id?: string | null
  linked_page_id?: string | null
}
export const updateGraphicBlock = (
  workspaceId: string,
  siteId: string,
  pageId: string,
  blockId: string,
  data: UpdateGraphicBlockPayload,
) => saasApi.patch<GraphicPageBlock>(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/blocks/${blockId}`, data)

export const deleteGraphicBlock = (workspaceId: string, siteId: string, pageId: string, blockId: string) =>
  saasApi.delete(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/blocks/${blockId}`)

export const reorderGraphicBlocks = (
  workspaceId: string,
  siteId: string,
  pageId: string,
  order: Array<{ id: string; module_id?: string }>,
) => saasApi.post(`${graphicPagesBase(workspaceId, siteId)}/${pageId}/blocks/reorder`, { order })

/** Storage/Worker Execution Contract (A1) — the API origin (no `/api/saas`
 * suffix), for resolving the root-relative `descriptor_url`/
 * `tiles_base_url` `GET .../derivatives/deepzoom` returns (see
 * `getAssetDeepZoomDerivative` below). Same derivation convention as
 * `publicApi.ts`'s `PUBLIC_API_BASE_URL` — never re-guess the origin from
 * `window.location`, since the API can legitimately be a different origin
 * than this admin frontend (Cloudflare Pages + a separate API host). */
const SAAS_API_ORIGIN = SAAS_API_BASE_URL.replace(/\/api\/saas$/, '')
export const resolveSaasApiUrl = (rootRelativePath: string) => `${SAAS_API_ORIGIN}${rootRelativePath}`

/**
 * MEDIA_ELEMENT_CROSS_HOST_URL_AUDIT_AND_FIX_V1 — the one canonical
 * resolver every call site building a URL for a *native* media loader
 * (`<img src>`, `<audio src>`, `<video src>`, `poster`, a protected-media
 * download `<a href>`, …) should use instead of hand-rolling its own
 * `${SAAS_API_BASE_URL_EXPORT}/api/...` string concatenation. Unlike
 * `resolveSaasApiUrl` above (which assumes its input is always this API's
 * own root-relative path, and would incorrectly double-prefix a genuine
 * external/CDN/R2 URL), this is safe to call on *any* value a media field
 * might carry — see `resolveMediaUrlAgainstOrigin`'s own doc comment for
 * the exact passthrough/rewrite rules. Pure logic lives in
 * `./mediaUrlCore` so it can be unit-tested without `import.meta.env`.
 */
export const resolveSaasMediaUrl = (pathOrUrl: string | null | undefined) =>
  resolveMediaUrlAgainstOrigin(pathOrUrl, SAAS_API_ORIGIN)

/** Storage-neutral DeepZoom derivative descriptor (spec §25/§26) — the
 * one endpoint an OpenSeadragon viewer needs. Mirrors
 * `app.api.routes.saas_assets.get_asset_deepzoom_derivative`'s response
 * shape exactly; never a raw storage key, local path, or R2 URL — just a
 * `status` plus (once `ready`) plain geometry + same-origin URLs this
 * same backend already serves. */
export interface AssetDeepZoomDerivative {
  status: 'not_started' | 'pending' | 'processing' | 'ready' | 'failed'
  error_message: string | null
  width?: number | null
  height?: number | null
  tile_size?: number | null
  overlap?: number | null
  format?: string | null
  levels?: number | null
  processor_version?: string | null
  descriptor_url?: string
  tiles_base_url?: string
}

export const getAssetDeepZoomDerivative = (workspaceId: string, assetId: string) =>
  saasApi.get<AssetDeepZoomDerivative>(`/workspaces/${workspaceId}/assets/${assetId}/derivatives/deepzoom`)

/** Root-relative base URLs (no query string) for the two Preview
 * endpoints — used to rewrite the rendered theme's own root-absolute
 * `<link href="/static/...">` hrefs (see `PreviewTab.tsx`'s
 * `withNavigationBridge`) into a URL that actually resolves back through
 * this same endpoint with the right `path` query param, since a
 * `srcDoc` iframe otherwise resolves a root-absolute href against the
 * PARENT page's origin, not against this API call's own URL. */
export const siteDraftPreviewBaseUrl = (workspaceId: string, siteId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/sites/${siteId}/preview`
export const sitePublishedPreviewBaseUrl = (workspaceId: string, siteId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/sites/${siteId}/publish/preview`

/** "Draft Preview" — always a fresh render of current DB state (drafts
 * included), spec §11/§14. `transformResponse` is set to identity so a
 * 404 "nothing to preview yet" HTML body (see saas_website.py) still
 * lands in `err.response.data` as plain text instead of being run
 * through axios's default JSON.parse attempt. */
export const fetchSiteDraftPreview = (workspaceId: string, siteId: string, path: string) =>
  saasApi.get<string>(`/workspaces/${workspaceId}/sites/${siteId}/preview`, {
    params: { path },
    responseType: 'text',
    transformResponse: (data) => data,
  })

export const publishSite = (workspaceId: string, siteId: string) =>
  saasApi.post<PublishTriggerResult>(`/workspaces/${workspaceId}/sites/${siteId}/publish`)

// ------------------------------------------- Video Generation (Phase V1)
// Tenant Console native "视频生成" feature — Control-Plane only this
// phase (validate/store/queue a `video_generation` Task; no Worker, no
// LTX). Mirrors the `initiate -> PUT to R2 -> complete` presigned-upload
// contract `../api/client.ts`'s `initiateUpload`/`putFileToPresignedUrl`/
// `completeUpload` already use for AssetChain image uploads, but under
// this Workspace's own `/video-generation/assets/*` namespace (a
// different backend table — never the AssetChain pipeline, see backend
// `app.saas.services.video_generation_assets`).
export interface VideoGenerationProviderChannel {
  id: string
  label: string
  billing_hint: string
  billing_tooltip?: string | null
  route_mode: string
  // Generic engine/registry key (e.g. "local"/"minimax"/a provider_key) —
  // EXECUTION metadata only, never the Canvas Provider selector's own
  // canonical identity. See `provider_option_id` below for that (task spec
  // VISTOERA_GENERATE_VIDEO_CANONICAL_PROVIDER_ID_AND_ADMIN_DEFAULT_
  // PROVIDER_END_TO_END_P0_V1 §P2/§P3).
  provider_id: string
  // Canonical Provider identity (`ProviderOption.id`, Platform Admin
  // 功能节点管理 → generate_video → Provider Option's own stable row id).
  // THIS is what a "same Provider" comparison / Admin-default match must
  // use — `id` (the execution channel id, e.g. "LOCAL_LTX") and
  // `provider_id` above are both routing metadata, never identity.
  // Absent only for a genuinely unresolvable/misconfigured legacy channel
  // (should not happen in practice — the backend excludes such channels).
  provider_option_id?: string | null
  // Whether this channel is the Platform Admin's CURRENT canonical default
  // for generate_video (`ProviderOption.is_default && enabled`). A
  // brand-new Canvas node must select the channel where this is true —
  // never a hardcoded frontend constant (task spec §P4).
  is_default?: boolean
  model_id: string
  cost_class: 'LOCAL' | 'PAID_CLOUD'
  route_policy?: {
    route_mode?: string | null
    provider_preference?: string | null
    // Task spec §P6 — canonical Provider identity carried alongside the
    // legacy `provider_preference`/`route_mode` execution fields so the
    // server can validate the submitted channel truly belongs to this
    // Provider (`PROVIDER_CHANNEL_MISMATCH` otherwise).
    provider_option_id?: string | null
    model_preference?: string | null
    cloud_fallback_allowed?: boolean
  } | null
  readiness?: Record<string, unknown>
  supported_ratios?: string[]
  duration_seconds_choices?: number[]
  /** VistoeraTASK-00172 — backend-owned duration contract (ENUM/RANGE/DERIVED/COMBINATION). */
  duration_capability?: {
    mode?: string
    choices?: number[]
    choices_by_quality_tier?: Record<string, number[]>
    min_seconds?: number
    max_seconds?: number
    fps?: number
    frame_alignment?: string
  }
  quality_tier_choices?: { value: string; label: string }[]
  parameter_support?: Record<string, string>
  /** Effective structured input contract for this channel (tenant UI).
   * VistoeraTASK-00188 §十三 — widened from `Record<string, string>` to
   * `Record<string, string | number>`: every pre-existing key is still
   * a plain SUPPORTED/PARTIAL/UNSUPPORTED string; the new
   * `*_MAX_COUNT` keys (e.g. `REFERENCE_IMAGE_MAX_COUNT`) are `number`
   * so callers can do a genuine `connectedCount <= max_count` check
   * instead of a boolean-only comparison. */
  input_capability?: Record<string, string | number>
  // VISTOERA_FUNCTION_NODE_PROVIDER_SINGLE_SOURCE_OF_TRUTH_AND_CANVAS_
  // GENERATE_VIDEO_PROVIDER_DRIFT_FORENSIC_P0_V1 §P5/§P6 Case 4 — when a
  // dynamic (Function-Node-Provider-Binding-gated) channel exposes more
  // than one bindable model, this lists them so a future in-channel
  // model/mode picker can override `route_policy.model_preference`
  // without a second top-level "Provider" entry per model.
  model_choices?: { id: string; label: string; is_default: boolean }[]
}

export interface VideoGenConfig {
  aspect_ratio_choices: string[]
  quality_tier_choices: { value: string; label: string }[]
  duration_preset_choices: number[]
  max_duration_seconds: Record<string, number>
  default_aspect_ratio: string
  default_quality_tier: string
  default_duration_seconds: number
  seed_range: { min: number; max: number }
  prompt_max_length: number
  image_allowed_mime: string[]
  video_allowed_mime: string[]
  image_max_size_mb: number
  video_max_size_mb: number
  // P0 fix — mirrors `../api/client.ts`'s `/uploads/config` `direct_to_r2`
  // flag: false (the default, since this repo's R2 token cannot configure
  // bucket CORS) means the browser cannot PUT straight to R2 and callers
  // must use `videoGenUploadAssetDirect` instead of the initiate/PUT/
  // complete flow below, or every upload fails client-side with a bare
  // "Network Error" (browser-swallowed CORS failure, no HTTP status).
  direct_to_r2: boolean
  provider_channels?: VideoGenerationProviderChannel[]
  // Task spec VISTOERA_GENERATE_VIDEO_CANONICAL_PROVIDER_ID_AND_ADMIN_
  // DEFAULT_PROVIDER_END_TO_END_P0_V1 §P4/§P12 — canonical Admin default
  // Provider (`ProviderOption.id`), included in this SAME page-level
  // shared config fetch (never a second per-node API call). A brand-new
  // GenerateVideo node selects the `provider_channels` entry whose
  // `provider_option_id` matches this value. `null` when no ENABLED
  // ProviderOption is currently marked default — Canvas must leave the
  // node's Provider unset in that case, never guess (§P4/§P9 hard rule).
  default_provider_option_id?: string | null
}

export const getVideoGenConfig = (workspaceId: string) =>
  saasApi.get<VideoGenConfig>(`/workspaces/${workspaceId}/video-generation/config`)

export interface VideoGenInitiateAssetResponse {
  asset_id: string
  media_type: 'image' | 'video'
  storage_key: string
  upload_url: string
  expires_at: string
}

export const videoGenInitiateAssetUpload = (
  workspaceId: string,
  data: { filename: string; content_type: string; size?: number },
) => saasApi.post<VideoGenInitiateAssetResponse>(`/workspaces/${workspaceId}/video-generation/assets/initiate`, data)

export interface VideoGenAsset {
  id: string
  workspace_id: string
  media_type: 'image' | 'video'
  status: 'pending' | 'ready' | 'failed'
  original_filename: string
  content_type: string
  size_bytes: number | null
  width: number | null
  height: number | null
  duration_seconds: number | null
  created_at: string | null
}

export const videoGenCompleteAssetUpload = (workspaceId: string, assetId: string) =>
  saasApi.post<VideoGenAsset>(`/workspaces/${workspaceId}/video-generation/assets/${assetId}/complete`)

// P0 fix — proxy transport (Browser -> this backend -> R2), used instead of
// initiate/PUT-to-R2/complete whenever `VideoGenConfig.direct_to_r2` is
// false. Bytes travel through this backend's own origin only, so no R2
// bucket CORS policy is required at all (see backend
// `video_generation_assets.upload_direct` docstring for the full rationale
// — same fix already shipped for the AssetChain pipeline's `/uploads/direct`).
export const videoGenUploadAssetDirect = (workspaceId: string, file: File) => {
  const form = new FormData()
  form.append('file', file)
  return saasApi.post<VideoGenAsset>(`/workspaces/${workspaceId}/video-generation/assets/direct`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const videoGenRetryAssetPresign = (workspaceId: string, assetId: string) =>
  saasApi.post<{ asset_id: string; storage_key: string; upload_url: string }>(
    `/workspaces/${workspaceId}/video-generation/assets/${assetId}/retry-presign`,
  )

export type VideoGenTaskStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'waiting_for_review'

export interface VideoGenInputAssetRef {
  asset_id: string
  role: 'reference_image' | 'reference_video'
  media_type: 'image' | 'video'
}

// ---------------------------------------------------------------------
// Canvas Unified Video Generation Node P0 — Execution Planner / Model
// Selector / Keyframe / Canvas-materialize additions. Mirrors
// `backend/app/api/routes/saas_video_generation.py`'s `GET /engines`,
// `POST /canvas/materialize-asset`, and the `CreateTaskRequest` fields
// added this round 1:1 (spec §24/§27/§30/§40) — never a second,
// independently-invented frontend shape.
// ---------------------------------------------------------------------

export type VideoKeyframeRole = 'start_image' | 'middle_image' | 'end_image'

export interface VideoGenKeyframeItem {
  role: VideoKeyframeRole
  asset_id: string
  time_position?: number | null
}

export interface VideoGenEngineChoice {
  value: string
  label: string
  available: boolean
  unavailable_reason?: string | null
  model_name?: string
}

export type VideoCapabilityStatus = 'SUPPORTED' | 'UNSUPPORTED' | 'UNKNOWN'

export interface VideoGenEngineCapabilityProfile {
  display_name_zh: string
  capabilities: Record<string, VideoCapabilityStatus>
  aspect_ratio_status: VideoCapabilityStatus
  resolution_status: VideoCapabilityStatus
  duration_status: VideoCapabilityStatus
  citation: string
}

export interface VideoGenEnginesResponse {
  choices: VideoGenEngineChoice[]
  capability_matrix: Record<string, VideoGenEngineCapabilityProfile>
}

/** Canvas Unified Video Generation Node P0 (spec §27/§33) — the Model
 * Selector's real data source; `GET /video-generation/engines`. */
export const getVideoGenEngines = (workspaceId: string) =>
  saasApi.get<VideoGenEnginesResponse>(`/workspaces/${workspaceId}/video-generation/engines`)

export interface MaterializeCanvasVideoAssetResponse {
  asset_id: string
  media_type: 'image' | 'video'
  status: string
}

/** Canvas Materialization Bridge (spec §6/§40) — turns an `image_asset`
 * Canvas node's `SiteAsset` (`kind: 'site_asset'`) or a `generate_image`
 * Canvas node's own completed output (`kind: 'image_generation_output'`)
 * into a `VideoGenerationAsset` this module's `/tasks` endpoint actually
 * accepts references to. Idempotent — safe to call again on every
 * submit without creating duplicates. */
export const materializeCanvasVideoAsset = (workspaceId: string, kind: 'site_asset' | 'image_generation_output', id: string) =>
  saasApi.post<MaterializeCanvasVideoAssetResponse>(`/workspaces/${workspaceId}/video-generation/canvas/materialize-asset`, {
    kind,
    id,
  })

export interface VideoGenTask {
  id: string
  workspace_id: string
  status: VideoGenTaskStatus
  status_label: string
  // Vistoera Video Queue / Premature Failure P0 (spec item 七) — real
  // 1-based position in the canonical FIFO queue; `null` for every
  // non-queued status. Never guess this client-side from list order —
  // the queue can include other job types (asset.deepzoom/image.pipeline)
  // ahead of a video task that this workspace's own task list never
  // shows, so only the backend's real canonical order is trustworthy.
  queue_position: number | null
  prompt: string
  enhance_prompt: boolean
  generation: {
    mode: 'text_to_video' | 'image_to_video' | 'video_to_video'
    aspect_ratio: string
    quality_tier: string
    width: number
    height: number
    duration_seconds: number
    frame_rate?: number
    seed_mode: string
    seed: number
    gen_kf?: number
    output_quality?: string
    image_strength?: number
  }
  input_assets: VideoGenInputAssetRef[]
  has_reference_image: boolean
  has_reference_video: boolean
  // No fake progress (spec §28) — null unless a real future Worker has
  // reported one; the UI must render "—" / "等待 Worker", never invent a
  // percentage from elapsed time.
  progress: number | null
  worker_id: string | null
  error_code: string | null
  error_message: string | null
  queued_at: string | null
  started_at: string | null
  finished_at: string | null
  output_asset_id: string | null
  result_kind?: 'generated' | 'continuation_segment' | 'continuous_composite'
  composite_id?: string
  root_video_id?: string
  source_video_id?: string
  continuous_session_id?: string
  segment_ids?: string[]
  segment_count?: number
  is_composed?: boolean
  continuation_composite?: VideoGenTask
  // when this task is part of a continuation sequence:
  // - a *root* video (top-level list row) gets `is_continuation_root` +
  //   `continuation_children` (its segments, `segment_index` ASC) +
  //   `can_compose`.
  // - a *segment* (only ever seen nested inside `continuation_children`,
  //   or via `getContinuationSequence`'s `segments[]`) gets
  //   `segment_index`/`parent_video_id`/`root_video_id`/`continuation_mode`.
  // A plain, never-continued video has none of these keys.
  is_continuation_root?: boolean
  sequence_id?: string | null
  continuation_children?: VideoGenTask[]
  can_compose?: boolean
  segment_index?: number
  parent_video_id?: string
  continuation_mode?: 'last_frame_i2v' | 'native_prefix'
  // VISTOERA_CONTINUOUS_VIDEO_STATE_MACHINE_AND_LAST_FRAME_P0 (spec §3) —
  // canonical SEGMENT state, present only on a continuation segment (see
  // `getContinuationSequence`'s `segments[]`). Additive alongside the
  // existing raw `status`/`status_label` — never remove those.
  segment_state?: 'queued' | 'generating' | 'completed' | 'failed' | 'cancelled'
  // VISTOERA_CONTINUOUS_VIDEO_SEGMENT_RESULT_BINDING_P0 — output MP4
  // persisted + readable in storage (distinct from raw `status=succeeded`).
  result_asset_ready?: boolean
  final_frame_ready?: boolean
  /** UI projection — composite is the master row in the video list (P0). */
  is_master_result?: boolean
  /** Lineage row under a composite master (source or segment history). */
  is_lineage_row?: boolean
  lineage_role?: 'source' | 'segment'
  // Canvas Unified Video Generation Node P0 (spec §40/§43) — round-
  // tripped verbatim by the backend's `task_out` so a Canvas page
  // refresh can restore the routed engine + keyframe assignment +
  // originating node (`source_context.node_id`). `undefined`/`[]` for
  // every pre-existing Tenant Video Generation task (never set).
  engine?: string | null
  engine_selection?: string | null
  keyframes?: VideoGenKeyframeItem[]
  source_context?: { source?: string; node_id?: string; project_id?: string } | null
  /** Non-sensitive: generation_mode T2V/I2V/FLF2V + first_frame flags. */
  conditioning_diagnostics?: {
    generation_mode?: string
    conditioning?: {
      first_frame?: boolean
      last_frame?: boolean
      middle_frame?: boolean
      reference_count?: number
      keyframe_count?: number
    }
    provider?: string
  } | null
  waiting_for_engine?: boolean
  engine_activation?: {
    waiting_for_engine?: boolean
    activation_request_id?: string
    residency_request_id?: string
    lease_id?: string
    worker_id?: string
    engine_id?: string
    engine_state?: string | null
    readiness_code?: string
    requested_at?: string
    released?: boolean
  } | null
}

export interface CreateVideoGenTaskPayload {
  prompt: string
  aspect_ratio: string
  quality_tier: string
  duration_seconds: number
  seed_mode: 'random' | 'fixed'
  seed?: number | null
  enhance_prompt?: boolean
  reference_image_asset_id?: string | null
  // VistoeraTASK-00157 §九/§十四 (originated this field) + VistoeraTASK-
  // 00181 (re-affirmed the same field name — VistoeraTASK-00197 merged
  // the two accidental duplicate declarations back into this single
  // canonical one) — the canonical, deduplicated array of ALL structured
  // reference image asset ids (character/product/scene/other-reference)
  // that are ready to submit, already materialized into real
  // `VideoGenerationAsset` ids (same bridge as the singular scalar field
  // above). Additive/optional, backend-deduplicated against the
  // singular/keyframe fields by `asset_id` (never double-submitted,
  // never silently dropped) — see `create_task`'s own docstring in
  // `video_generation_service.py`. Every pre-existing caller that omits
  // this keeps working exactly as before (server-side model capability
  // still gates the real count).
  reference_image_asset_ids?: string[] | null
  /** VistoeraTASK-00191 — per-canonical-image Canvas semantic role
   * mapping (Picture N -> Subject/Scene/…), same array length/order as
   * `reference_image_asset_ids` above. A SEPARATE field from it, never a
   * substitute — see this file's `CreateVideoGenTaskPayload` docstring
   * convention: `reference_image_asset_ids` = canonical dedup image ids,
   * `reference_image_bindings` = role mapping for those SAME ids. */
  reference_image_bindings?: Array<{ asset_id: string; canvas_role: string; ordinal: number }> | null
  reference_video_asset_id?: string | null
  reference_video_asset_ids?: string[] | null
  // VistoeraTASK-00150 §四/§六 — structured, multi-slot audio reference
  // asset ids (already materialized into real `VideoGenerationAsset`
  // ids — see `materializeAudioSiteAssetRef` in `GenerateVideoNode.tsx`).
  // Additive/optional; every pre-existing caller omits it, unchanged
  // behavior. Server-side capability gating happens regardless of what
  // this field contains — never assume "if the UI sent it, it must be
  // supported."
  reference_audio_asset_ids?: string[] | null
  // Canvas Unified Video Generation Node P0 (spec §27/§7/§40) —
  // additive/optional; every pre-existing caller (Tenant Video
  // Generation page) never sends these, unchanged behavior.
  engine?: string | null
  keyframes?: VideoGenKeyframeItem[]
  source_context?: {
    source: string
    node_id: string
    project_id?: string
    original_prompt_snapshot?: string | null
    effective_prompt_snapshot?: string | null
    effective_prompt_source?: string | null
    prompt_enhancement_run_id?: string | null
    prompt_enhancement_skill_id?: string | null
    prompt_enhancement_skill_version?: string | null
    visual_conditioning?: {
      has_first_frame?: boolean
      reference_asset_count?: number
      reference_asset_types?: string[]
      visual_conditioning_source?: string
      visual_semantic_role?: string
      prompt_source?: string
    } | null
    // VISTOERA_CANVAS_GENERATE_VIDEO_SEMANTIC_DROP_ZONES_EDGE_PORT_ALIGNMENT_AND_COLLAPSIBLE_REFERENCE_ASSETS_P0_V1
    // §十五 "Generation Payload 必须最终验证" — the FULL structured
    // semantic breakdown (characters/products/scenes/other_references/
    // reference_video), diagnostic/JSON-safe, additive-only (every
    // pre-existing caller omits it, unchanged behavior). See
    // `buildStructuredAssetContextSnapshot` (`dataflow.ts`) for the
    // ONE builder producing this shape.
    structured_asset_context?: {
      characters: Array<{ source_label: string; entity_id?: string; generated_output_id?: string; ready: boolean }>
      products: Array<{ source_label: string; entity_id?: string; generated_output_id?: string; ready: boolean }>
      scenes: Array<{ source_label: string; entity_id?: string; generated_output_id?: string; ready: boolean }>
      other_references: Array<{ source_label: string; entity_id?: string; generated_output_id?: string; ready: boolean }>
      reference_video: { source_label: string; entity_id?: string; generated_output_id?: string; ready: boolean } | null
    } | null
  } | null
  route_policy?: {
    route_mode?: string | null
    provider_preference?: string | null
    // Task spec §P6 — canonical Provider identity (`ProviderOption.id`)
    // alongside the legacy `provider_preference`/`route_mode` execution
    // fields; server validates it belongs to the resolved channel
    // (`PROVIDER_CHANNEL_MISMATCH` otherwise). Optional so this payload
    // stays backward-compatible with anything still constructing it
    // without a resolved channel object.
    provider_option_id?: string | null
    model_preference?: string | null
    cloud_fallback_allowed?: boolean
  } | null
}

export const createVideoGenTask = (workspaceId: string, data: CreateVideoGenTaskPayload) =>
  saasApi.post<VideoGenTask>(`/workspaces/${workspaceId}/video-generation/tasks`, data, { timeout: 180000 })

/** Canvas Unified Video Generation Node P0 — single-task poll (the
 * Canvas node's own reload-recovery path mirrors `GenerateImageNode`'s
 * `getImageGenerationTask`, never the tenant list's own batched
 * `listVideoGenTasks`). Backend route already existed
 * (`GET /video-generation/tasks/{task_id}`) — only this client wrapper
 * is new. */
export const getVideoGenTask = (workspaceId: string, taskId: string) =>
  saasApi.get<VideoGenTask>(`/workspaces/${workspaceId}/video-generation/tasks/${taskId}`)

export const listVideoGenTasks = (workspaceId: string, status?: string) =>
  saasApi.get<{ items: VideoGenTask[] }>(`/workspaces/${workspaceId}/video-generation/tasks`, {
    params: status && status !== 'all' ? { status } : undefined,
  })

/** Tenant UI Batch V1 — hard-remove one task from the tenant list (trashes
 * output R2 objects when present). Running tasks are refused by the API. */
export const deleteVideoGenTask = (workspaceId: string, taskId: string) =>
  saasApi.delete<{ id: string; deleted: boolean }>(
    `/workspaces/${workspaceId}/video-generation/tasks/${taskId}`,
  )

export interface VideoGenBatchDeleteResult {
  deleted: string[]
  failed: Array<{ id: string; code: string; message: string }>
  deleted_count: number
  failed_count: number
}

export const batchDeleteVideoGenTasks = (workspaceId: string, taskIds: string[]) =>
  saasApi.post<VideoGenBatchDeleteResult>(`/workspaces/${workspaceId}/video-generation/tasks/batch-delete`, {
    task_ids: taskIds,
  })

/** @deprecated Video Delivery P0 — do not use for playback/download.
 * Whole-file `responseType: 'blob'` is the previous ARCHITECTURE_DEFECT.
 * Prefer {@link videoGenTaskOutputUrl} as a native `<video src>` /
 * `<a download>` target — the backend streams real bytes back same-origin
 * (Tenant-Branded Asset Download Delivery P0, 2026-08-25; historically
 * this 302-redirected to a short-lived presigned R2 URL, which is the
 * defect this whole helper is deprecated in favor of avoiding). */
export const getVideoGenTaskOutput = (workspaceId: string, taskId: string) =>
  saasApi.get<Blob>(`/workspaces/${workspaceId}/video-generation/tasks/${taskId}/output`, {
    responseType: 'blob',
  })

// History UI V2 — direct (non-axios) URLs for `<img>`/`<video>` tags in the
// 我的视频任务 table (first-frame poster thumbnails, reference asset
// thumbnails/playback). Safe as a bare `src` because `app.main`'s
// `preview_access_gate` exempts exactly these byte-serving GET paths
// (same "no custom headers possible" reasoning as the DeepZoom/variant
// exemption it already had) — tenant auth still runs on the route itself
// via the httpOnly session cookie (`withCredentials` is the default for
// `<img>`/`<video>` cross-origin resource requests, unlike `fetch`/XHR).
//
// Video Delivery P0: `<video src={videoGenTaskOutputUrl(...)}>` is the
// canonical playback path. Tenant-Branded Asset Download Delivery P0
// (2026-08-25): this stable authenticated endpoint now proxies real bytes
// back same-origin on every request (never a 302 to a presigned R2 URL —
// that was the real root cause of R2 leaking into the browser's Network
// panel / macOS "Where From" download metadata). Retry = bump key /
// reload the same stable URL, same as before.
export const videoGenTaskPosterUrl = (workspaceId: string, taskId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/video-generation/tasks/${taskId}/poster`

export const videoGenTaskOutputUrl = (workspaceId: string, taskId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/video-generation/tasks/${taskId}/output`

export const videoGenAssetPosterUrl = (workspaceId: string, assetId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/video-generation/assets/${assetId}/poster`

export const videoGenAssetContentUrl = (workspaceId: string, assetId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/video-generation/assets/${assetId}/content`

// ---------------------------------------------------------------------
// Video Production — Final Assembly (VISTOERA_VIDEO_PRODUCTION_ROUGH_
// CUT_TIMELINE_PREVIEW_AND_FINAL_ASSEMBLY_CARD_P0_V1 §19/§24). The
// AssemblyManifest is caller-built (from the live Storyboard + bound
// Shot video Jobs — Video Production has no backend-side Shot model of
// its own) and is only ever a *request* shape; the persisted row
// returned back is the server-verifiable source of truth.
export interface VideoProductionAssemblyManifestShotPayload {
  shot_id: string
  order: number
  expected_duration_sec: number | null
  video_job_id: string
}

export interface CreateVideoProductionAssemblyPayload {
  canvas_node_id: string
  storyboard_artifact_id?: string | null
  provider?: string
  skill?: string
  shots: VideoProductionAssemblyManifestShotPayload[]
  production_format?: {
    aspect_ratio: string
    resolution: string
    version?: number
  }
  aspect_ratio?: string
  delivery_target_width?: number
  delivery_target_height?: number
}

export type VideoProductionAssemblyStatus = 'ASSEMBLING' | 'READY' | 'FAILED'

export interface VideoProductionAssembly {
  id: string
  workspace_id: string
  canvas_node_id: string
  storyboard_artifact_id: string | null
  status: VideoProductionAssemblyStatus
  provider: string
  skill: string
  shot_count: number
  manifest: VideoProductionAssemblyManifestShotPayload[]
  duration_seconds: number | null
  size_bytes: number | null
  width: number | null
  height: number | null
  audio_dropped: boolean
  error_code: string | null
  error_message: string | null
  created_at: string | null
  updated_at: string | null
}

function videoProductionBase(workspaceId: string): string {
  return `/workspaces/${workspaceId}/video-production`
}

/** 合并生成视频 — synchronous request/response (same "one blocking click"
 * shape as Video Continuation's own 合成视频), never a queued async job. */
export const createVideoProductionAssembly = (
  workspaceId: string,
  payload: CreateVideoProductionAssemblyPayload,
) =>
  saasApi
    .post<VideoProductionAssembly>(`${videoProductionBase(workspaceId)}/assembly`, payload, { timeout: 180000 })
    .then((res) => res.data)

export const getVideoProductionAssembly = (workspaceId: string, assemblyId: string) =>
  saasApi
    .get<VideoProductionAssembly>(`${videoProductionBase(workspaceId)}/assembly/${assemblyId}`)
    .then((res) => res.data)

export const videoProductionAssemblyOutputUrl = (workspaceId: string, assemblyId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/video-production/assembly/${assemblyId}/output`

// ---------------------------------------------------------------------
// VISTOERA_PRODUCTION_CANVAS_IMAGE_TO_IMAGE_GENERATION_FLOW_V1 — the
// Canvas "生成图片" node's ONLY backend surface (spec §23/§24: Canvas
// frontend -> this ONE domain-agnostic service, never a Worker/provider
// API directly). Mirrors `app/api/routes/saas_image_generation.py` 1:1.
// ---------------------------------------------------------------------

export type ImageGenerationMode = 'FAST' | 'STANDARD'

export interface ImageGenerationConfigImageToImage {
  operation: string
  required_input_role: string
  required_input_count: number
  max_reference_images?: number
  supports_multi_reference?: boolean
  reference_limit_label?: string
}

export interface ImageGenerationPromptConstraints {
  max_characters: number | null
  unit: string
  source: string
}

export interface ImageGenerationConfig {
  capability: string
  operations: string[]
  operation: string
  default_generation_mode: ImageGenerationMode
  provider_channels?: ImageGenerationProviderChannel[]
  /** Tenant-visible default Execution Plan (remapped from Admin default when needed). */
  default_provider_option_id?: string | null
  /** Hidden ProviderOption id → Canvas channel id for persisted legacy selections. */
  provider_option_channel_aliases?: Record<string, string> | null
  image_to_image: ImageGenerationConfigImageToImage
  /** @deprecated Use provider_channels[].prompt_constraints instead. */
  prompt_max_length: number | null
  prompt_limit_unit?: string
  job_budget_seconds: number
}

export type ImageGenerationParameterGuarantee = 'best_effort' | 'supported' | 'unsupported_exact'

export type ImageGenerationParameterExecutionMode =
  | 'prompt_constraint'
  | 'api_dimensions'
  | 'local_workflow'
  | 'applied'
  | 'provider_hint'

export interface ImageGenerationParameterCapability {
  supported: boolean
  execution_mode: ImageGenerationParameterExecutionMode
  guarantee: ImageGenerationParameterGuarantee
  exact_supported?: boolean
  supported_values?: string[]
  max?: number
}

/**
 * Canonical image-generation route policy (frontend contract).
 * Source of truth: backend `RoutePolicyInput` + `ImageGenerationRoutePolicyRequest`,
 * with legacy `model_preference` allowed on provider channel config JSON only.
 */
export interface ImageGenerationRoutePolicy {
  provider_preference?: string | null
  route_mode?: string | null
  /** Canonical submit/model identity (`RoutePolicyInput.model_id`). */
  model_id?: string | null
  /** Legacy alias on `provider_channels` JSON; normalized to `model_id` on submit. */
  model_preference?: string | null
  provider_option_id?: string | null
  cloud_fallback_allowed?: boolean
}

export interface ImageGenerationProviderChannel {
  id: string
  label: string
  billing_hint: string
  billing_tooltip?: string | null
  preferred_prompt_language?: string | null
  prompt_translation_recommended?: boolean
  generation_mode: ImageGenerationMode
  route_policy?: ImageGenerationRoutePolicy | null
  supports_multi_reference: boolean
  max_reference_images: number
  provider_max_reference_images: number
  model_id: string
  provider_id: string
  supported_ratios?: string[]
  parameter_support?: Record<string, string>
  parameter_capabilities?: Record<string, ImageGenerationParameterCapability>
  prompt_constraints?: ImageGenerationPromptConstraints
  /** VISTOERA_GLOBAL_FUNCTION_NODE_EXECUTION_OPTION_TO_TENANT_CANVAS_
   * PROVIDER_DISCOVERY_CANONICAL_PIPELINE_P0_V1 — additive canonical
   * identity/readiness fields present on EVERY channel (legacy and
   * dynamic alike) now that `provider_channels` is backed by a real,
   * admin-editable `ProviderOption` row instead of a hardcoded literal.
   * Optional so no existing consumer needs to change. */
  provider_option_id?: string | null
  is_default?: boolean
  /** VistoeraTASK-00253 — canonical admin-controlled display order
   * (mirrors `ProviderOption.sort_order`). Optional so no existing
   * consumer needs to change; Canvas's own channel list is already
   * returned pre-sorted by the backend — this is exposed mainly so
   * tests/diagnostics can assert on it without re-deriving it. */
  sort_order?: number
  readiness?: {
    provider_configured?: boolean
    entitlement_granted?: boolean
    execution_supported?: boolean
    unavailable_reason?: string | null
  }
}

export const getImageGenerationConfig = (workspaceId: string) =>
  saasApi.get<ImageGenerationConfig>(`/workspaces/${workspaceId}/image-generation/config`)

export interface ImageGenerationPromptTranslatePayload {
  text: string
  source_language?: string
  target_language?: string
}

export interface ImageGenerationPromptTranslateResult {
  translated_text: string
  source_language: string
  target_language: string
  translation_applied: boolean
  already_english?: boolean
  translation_provider?: string | null
  translation_status?: string | null
}

export const translateImageGenerationPrompt = (
  workspaceId: string,
  data: ImageGenerationPromptTranslatePayload,
) =>
  saasApi.post<ImageGenerationPromptTranslateResult>(
    `/workspaces/${workspaceId}/image-generation/prompt/translate`,
    data,
  )

export interface ImageGenerationReference {
  asset_id: string
  role?: string | null
}

export interface ImageGenerationReferenceOut {
  asset_id: string
  role: string | null
  /** Present once the referenced `SiteAsset` resolves to a real
   * thumbnail-able asset (spec §11 reference thumbnail chip). */
  thumbnail_url?: string | null
}

export interface ImageGenerationTaskOutput {
  id: string
  task_id: string
  candidate_index: number
  content_type: string
  width: number | null
  height: number | null
  sha256: string | null
  seed: number | null
  content_url: string
  /** Present once this candidate has already been materialized into a
   * reusable `SiteAsset` (spec §28) — lets the UI render "已添加到画布"
   * instead of re-POSTing `/materialize` for the same candidate. */
  site_asset_id?: string
  picker_item_id?: string
}

export interface ImageGenerationGenerationView {
  operation: string
  width: number | null
  height: number | null
  steps: number | null
  candidate_count: number | null
  seed: number | null
  negative_prompt: string | null
  ratio: string | null
  quality: string | null
  references: ImageGenerationReferenceOut[]
}

export type ImageGenerationTaskStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'interrupted'

export interface ImageGenerationTask {
  id: string
  workspace_id: string
  status: ImageGenerationTaskStatus
  status_label_zh: string
  message: string | null
  prompt: string
  operation: string
  generation: ImageGenerationGenerationView
  generation_mode: ImageGenerationMode
  created_at: string | null
  started_at: string | null
  finished_at: string | null
  outputs: ImageGenerationTaskOutput[]
  error_code: string | null
  error_message: string | null
  // Local FLUX Engine Lifecycle pending UI (spec §13/§31/§53) — mirrors
  // VideoGenTask's own waiting_for_engine/engine_activation shape exactly
  // so Canvas can render "正在启动本地模型…" while the queued Job waits on
  // Residency Manager activation, instead of a generic network failure.
  waiting_for_engine?: boolean
  engine_activation?: {
    waiting_for_engine?: boolean
    activation_request_id?: string
    residency_request_id?: string
    lease_id?: string
    worker_id?: string
    engine_id?: string
    engine_state?: string | null
    readiness_code?: string
    requested_at?: string
    released?: boolean
  } | null
  execution?: {
    status?: 'queued' | 'running' | 'retrying'
    stage?: string
    message?: string | null
    attachment_count?: number
    attachment_ready?: number
    last_progress_at?: string | null
    last_heartbeat_at?: string | null
  } | null
}

/** VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_INTERACTION_V2 §15/§24 —
 * the ONE `input_assets` role the backend's IMAGE_TO_IMAGE contract
 * actually requires (`image_generation_service.get_config()["image_to_image"]`:
 * exactly 1 `reference_image`). Canvas never invents a second role. */
export interface ImageGenerationInputAssetRef {
  asset_id: string
  role: 'reference_image'
  media_type: 'image'
}

export interface CreateImageGenerationTaskPayload {
  prompt: string
  /** CREATE_NEW vs IMAGE_TO_IMAGE vs CREATE_WITH_REFERENCE */
  operation?: 'CREATE_NEW' | 'IMAGE_TO_IMAGE' | 'CREATE_WITH_REFERENCE'
  generation_mode?: ImageGenerationMode
  ratio?: string
  quality?: string
  candidate_count?: number
  strength?: number
  input_assets?: ImageGenerationInputAssetRef[]
  references?: ImageGenerationReference[]
  reference_image_inputs?: Array<{
    asset_id: string
    asset_ref: string
    label: string
    index: number
    role_optional?: string | null
  }>
  prompt_asset_bindings?: Array<{ token: string; asset_ref: string }>
  route_policy?: ImageGenerationRoutePolicy
  source_context?: Record<string, unknown>
}

export const createImageGenerationTask = (workspaceId: string, data: CreateImageGenerationTaskPayload) =>
  saasApi.post<ImageGenerationTask>(`/workspaces/${workspaceId}/image-generation/tasks`, data, {
    // Dispatch + reference materialization can exceed the default 30s client timeout
    // without implying the generation task failed (TASK-00142).
    timeout: 180_000,
  })

export const getImageGenerationTask = (workspaceId: string, taskId: string) =>
  saasApi.get<ImageGenerationTask>(`/workspaces/${workspaceId}/image-generation/tasks/${taskId}`)

/** ImageGenerationOutputAsset -> SiteAsset bridge (spec §28) — the
 * tenant clicked "添加到画布" on one candidate. Idempotent: calling this
 * again for the same output returns the SAME `asset_id`, never a
 * duplicate `SiteAsset` row. */
export const materializeImageGenerationOutput = (workspaceId: string, outputId: string) =>
  saasApi.post<{ asset_id: string; picker_item_id: string }>(
    `/workspaces/${workspaceId}/image-generation/outputs/${outputId}/materialize`,
    {},
  )

/** Direct (non-axios) URL for `<img src>` result thumbnails/lightbox —
 * same authenticated-same-origin-bytes convention as
 * `videoGenTaskOutputUrl` above (session cookie rides along automatically
 * on a plain `<img>` request). */
export const imageGenerationOutputContentUrl = (workspaceId: string, outputId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/image-generation/outputs/${outputId}/content`

// ---------------------------------------------------------------------
// Virtual Try-On (tryon.clothing) — Canvas「虚拟换装」node
// ---------------------------------------------------------------------

export type VirtualTryOnCategory = 'upper_body' | 'lower_body' | 'dress'

/** Canonical product outfit scope — AUTO is the default full-outfit intent. */
export type VirtualTryOnOutfitScope = 'FULL_LOOK_AUTO' | 'AUTO' | 'UPPER_ONLY' | 'LOWER_ONLY' | 'ONE_PIECE'
export type VirtualTryOnOutfitScopeSource = 'DEFAULT' | 'USER'

/** Canonical product quality — maps to PC1 CatVTON presets (not raw steps/CFG). */
export type VirtualTryOnQuality = 'fast' | 'balanced'

export const VIRTUAL_TRYON_TASK_PAYLOAD_VERSION = 2

export type VirtualTryOnTaskStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled'

export interface VirtualTryOnTaskOutput {
  output_id: string
  storage_key: string
  site_asset_id: string | null
  width: number | null
  height: number | null
  engine: string | null
}

export interface VirtualTryOnTask {
  job_id: string
  status: VirtualTryOnTaskStatus
  payload: Record<string, unknown>
  output: VirtualTryOnTaskOutput | null
  error_code?: string | null
  error_message?: string | null
  external_provider_run?: {
    provider_job_id?: string | null
    adapter_key?: string | null
    status?: string | null
    raw_status?: string | null
    credential_id?: string | null
    error_code?: string | null
    failed_stage?: string | null
    provider_error?: string | null
  } | null
}

export interface CreateVirtualTryOnTaskPayload {
  model_image_asset_id: string
  garment_reference_images: Array<{ asset_id: string; role?: string }>
  /** @deprecated Prefer outfit_scope — still accepted for backward compat. */
  category?: VirtualTryOnCategory
  outfit_scope?: VirtualTryOnOutfitScope
  outfit_scope_source?: VirtualTryOnOutfitScopeSource
  /** V2 canonical product quality (fast | balanced). */
  quality?: VirtualTryOnQuality
  /** Stable Provider Option id — never a display name / vendor string. */
  provider_option_id?: string
  /** Canvas selection semantics: DEFAULT inherits Admin default; EXPLICIT is user-pinned. */
  provider_selection_mode?: 'DEFAULT' | 'EXPLICIT'
  /** Optional supplementary instruction (backend already accepts; optional). */
  prompt?: string | null
  task_payload_version?: typeof VIRTUAL_TRYON_TASK_PAYLOAD_VERSION | 1
  /** @deprecated V1 — do not send from Canvas V2 UI. */
  quality_mode?: string
  /** @deprecated V1 — do not send from Canvas V2 UI. */
  engine?: string
  /** @deprecated V1 — resolution owned by worker preset mapping. */
  output?: { width?: number; height?: number }
}

/** User-facing Provider Option row (workspace API allowlist — no secrets). */
export type WorkspaceProviderOptionAvailability = 'AVAILABLE' | 'UNAVAILABLE' | string

export interface WorkspaceProviderOption {
  provider_option_id: string
  frontend_display_name: string
  frontend_description: string | null
  is_default: boolean
  availability: WorkspaceProviderOptionAvailability
  availability_reason: string | null
  /** TASK-00358: plan configuration is valid (a merely offline Worker is still valid). */
  config_valid?: boolean
  /** TASK-00358: the plan can execute right now. */
  runtime_available?: boolean
  /** TASK-00358: which axis blocks a plan — never "invalid" for a runtime-only problem. */
  unavailable_layer?: 'runtime' | 'configuration' | null
  selectable?: boolean
  /** Product-safe Try-On control shape (no vendor / model names). */
  outfit_scope_controls?: {
    outfit_scope_selector: 'HIDDEN' | 'FULL'
    supported_outfit_scopes: Array<'FULL_LOOK_AUTO' | 'AUTO' | 'UPPER_ONLY' | 'LOWER_ONLY' | 'ONE_PIECE'>
    default_outfit_scope: 'FULL_LOOK_AUTO' | 'AUTO' | 'UPPER_ONLY' | 'LOWER_ONLY' | 'ONE_PIECE'
    capability_gap_code?: string | null
  } | null
  /** Adapter parameter schema for provider-specific Canvas controls (no secrets). */
  parameter_schema?: Record<string, unknown> | null
  /** Provider-specific input limits (hint only — never rewrite Master). */
  input_constraints?: {
    profile_version?: string
    max_bytes?: number | null
    min_edge?: number | null
    max_edge?: number | null
    requires_https_public_url?: boolean
    allows_data_uri?: boolean
    hint_zh?: string
    hint_en?: string
  } | null
  /** Product-safe engine label for Advanced acceptance (e.g. Try-On Max). */
  engine_label?: string | null
  product_meta?: {
    engine_label?: string
    model_name?: string | null
    generation_mode?: string | null
    resolution?: string | null
    supports_shoes?: string
    supports_bag?: string
    supports_full_look?: string
  } | null
}

export interface WorkspaceProviderOptionsResponse {
  capability: string
  /** Server SoT for new nodes / DEFAULT selection mode. */
  default_provider_option_id?: string | null
  options: WorkspaceProviderOption[]
}

export const getWorkspaceProviderOptions = (workspaceId: string, capability: string, locale?: string) =>
  saasApi.get<WorkspaceProviderOptionsResponse>(`/workspaces/${workspaceId}/provider-options`, {
    params: { capability, ...(locale ? { locale } : {}) },
  })

export const TRYON_CLOTHING_CAPABILITY = 'tryon.clothing'

export const createVirtualTryOnTask = (workspaceId: string, data: CreateVirtualTryOnTaskPayload) =>
  saasApi.post<{
    job_id: string
    status: VirtualTryOnTaskStatus
    workspace_id: string
    payload: Record<string, unknown>
    provider_option_id?: string
  }>(`/workspaces/${workspaceId}/virtual-tryon/tasks`, data).then((res) => ({
    job_id: res.data.job_id,
    status: res.data.status,
    provider_option_id: res.data.provider_option_id,
  }))

export const getVirtualTryOnTask = (workspaceId: string, jobId: string) =>
  saasApi.get<VirtualTryOnTask>(`/workspaces/${workspaceId}/virtual-tryon/tasks/${jobId}`).then((res) => res.data)

export const virtualTryOnOutputContentUrl = (workspaceId: string, outputId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/virtual-tryon/outputs/${outputId}/content`

// ---------------------------------------------------------------------
// VistoeraTASK-00161 — 服装视频换装 (apparel.video_replication) Canvas
// 「服装视频换装」node. Deliberately its own section/capability, never
// merged into Virtual Try-On above — see
// `app.saas.apparel_video_providers` backend package docstring for the
// full rationale (reference video pacing/camera/pose + real product
// images/description [+ optional model images/BGM] -> new product
// commercial video, NOT an image try-on swap).
// ---------------------------------------------------------------------

export const APPAREL_VIDEO_REPLICATION_CAPABILITY = 'apparel.video_replication'

/** Kling clothing_dupe's own documented supported values (task spec
 * §12 hard rule) — Canvas must never render a value the bound Provider
 * cannot accept. V1 has exactly one adapter, so these are safe static
 * constants; if a second provider narrows/extends this list later, the
 * Provider Option's own `parameter_schema` (already threaded through
 * `WorkspaceProviderOption` for every other capability) is the place to
 * make this dynamic instead of a second hardcoded list. */
export const APPAREL_VIDEO_SUPPORTED_RESOLUTIONS = ['720p', '1080p'] as const
export type ApparelVideoResolution = (typeof APPAREL_VIDEO_SUPPORTED_RESOLUTIONS)[number]
export const APPAREL_VIDEO_SUPPORTED_ASPECT_RATIOS = [
  '9:16',
  '2:3',
  '3:4',
  '1:1',
  '4:3',
  '3:2',
  '16:9',
  '21:9',
] as const
export type ApparelVideoAspectRatio = (typeof APPAREL_VIDEO_SUPPORTED_ASPECT_RATIOS)[number]

export const APPAREL_VIDEO_PRODUCT_DESCRIPTION_MAX_LENGTH = 2500
export const APPAREL_VIDEO_MAX_PRODUCT_IMAGES = 5
export const APPAREL_VIDEO_MAX_MODEL_IMAGES = 5

export type ApparelVideoReplicationTaskStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled'

export interface ApparelVideoReplicationTaskOutput {
  output_asset_id: string
  content_type: string | null
  duration_seconds: number | null
  width: number | null
  height: number | null
}

export interface ApparelVideoReplicationTask {
  job_id: string
  status: ApparelVideoReplicationTaskStatus
  stage: string | null
  message: string | null
  error_code: string | null
  workspace_id: string
  payload: Record<string, unknown>
  run_state: {
    status: string
    provider_key: string | null
    kling_task_id: string | null
    error_code: string | null
    error_message: string | null
    output_asset_id: string | null
  }
  output: ApparelVideoReplicationTaskOutput | null
}

export interface CreateApparelVideoReplicationTaskPayload {
  source_video_asset_id: string
  product_image_asset_ids: string[]
  product_description: string
  model_image_asset_ids?: string[]
  bgm_asset_id?: string | null
  resolution?: ApparelVideoResolution | string
  aspect_ratio?: ApparelVideoAspectRatio | string
  provider_option_id?: string | null
  canvas_node_id?: string | null
}

export const createApparelVideoReplicationTask = (workspaceId: string, data: CreateApparelVideoReplicationTaskPayload) =>
  saasApi
    .post<{
      job_id: string
      status: ApparelVideoReplicationTaskStatus
      workspace_id: string
      payload: Record<string, unknown>
      provider_option_id?: string
      error_code?: string | null
      error_message?: string | null
    }>(`/workspaces/${workspaceId}/apparel-video-replication/tasks`, data)
    .then((res) => res.data)

export const getApparelVideoReplicationTask = (workspaceId: string, jobId: string) =>
  saasApi
    .get<ApparelVideoReplicationTask>(`/workspaces/${workspaceId}/apparel-video-replication/tasks/${jobId}`)
    .then((res) => res.data)

export const apparelVideoReplicationOutputContentUrl = (workspaceId: string, outputId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/apparel-video-replication/outputs/${outputId}/content`

// ---------------------------------------------------------------------
// VISTOERA_VIDEO_CONTINUATION_V1_P0 — "连续生成" (spec §3/§6/§11/§13/§15).
// Deliberately its own section: a thin client for
// `app/api/routes/saas_video_continuation.py`, reusing every type/URL
// helper above rather than duplicating them.
// ---------------------------------------------------------------------

export interface PickableVideo {
  id: string
  prompt: string | null
  duration_seconds: number | null
  width: number | null
  height: number | null
  aspect_ratio: string | null
  quality_tier: string | null
  created_at: string | null
  finished_at: string | null
}

export interface PickableVideosResult {
  items: PickableVideo[]
  total: number
  limit: number
  offset: number
}

/** Video Picker (spec §3) — completed/ready videos in this workspace
 * only, paginated + searchable by prompt substring. */
export const listPickableVideos = (
  workspaceId: string,
  params?: { search?: string; limit?: number; offset?: number },
) =>
  saasApi.get<PickableVideosResult>(`/workspaces/${workspaceId}/video-generation/continuation/pickable-videos`, {
    params,
  })

// VISTOERA_CONTINUOUS_VIDEO_STATE_MACHINE_AND_LAST_FRAME_P0 — canonical
// SOURCE/LAST_FRAME states + the single `can_generate_next` decision, all
// computed server-side (spec §3/§4/§31/§32). The frontend must only ever
// *display* these, never re-derive an equivalent boolean of its own from
// `root`/`segments`' raw `status` fields — that duplication is exactly
// what produced the original contradictory screenshot.
export type ContinuationSourceState = 'source_loading' | 'source_ready' | 'source_invalid' | 'source_failed'
export type ContinuationLastFrameState = 'not_requested' | 'ready' | 'failed'
export type ContinuationGenerateBlockReason = 'SOURCE_NOT_READY' | 'SOURCE_INVALID' | 'LAST_FRAME_NOT_READY' | null

export interface ContinuationSequenceContext {
  sequence_id: string | null
  root_video_id: string
  root: VideoGenTask
  segments: VideoGenTask[]
  anchor_video_id: string
  anchor_source_state: ContinuationSourceState
  anchor_last_frame: { state: ContinuationLastFrameState; asset_id: string | null }
  // Informational only (spec §11/§29) — the anchor's own most recent
  // in-flight (queued/generating) child, if any. Branching (multiple
  // sibling segments from the same completed parent) is supported, so
  // this deliberately does NOT gate `can_generate_next`; it's only a UI
  // hint (e.g. "该视频还有其他分支正在生成").
  active_child_segment_id: string | null
  can_generate_next: boolean
  generate_block_reason: ContinuationGenerateBlockReason
  /** @deprecated kept for back-compat; equivalent to `anchor_source_state === 'source_ready'`. */
  can_continue_from_anchor: boolean
  can_compose: boolean
  is_composed: boolean
  completed_segment_count?: number
  /** Generated continuation segments in the sequence (excludes source/root). */
  generated_segment_count?: number
  /** Contiguous compose-ready generated segments from segment_index 1. */
  compose_ready_generated_segment_count?: number
  /** Source video (when ready) + compose-ready generated segments. */
  compose_ready_video_count?: number
  /** @deprecated Alias for `compose_ready_generated_segment_count`. */
  compose_ready_segment_count?: number
  playable_segment_count?: number
}

/** Right-side "连续序列" panel (spec §11) — resolves the whole chain
 * regardless of whether `videoId` is the root or any segment. */
export const getContinuationSequence = (workspaceId: string, videoId: string) =>
  saasApi.get<ContinuationSequenceContext>(
    `/workspaces/${workspaceId}/video-generation/continuation/sequence/${videoId}`,
  )

/** Top-right "最后一帧" preview (spec §4) — the exact same lazily-cached
 * derivative a real continuation task will use as its primary image
 * conditioning, fetched here purely for display before submission. */
export const videoGenContinuationFinalFrameUrl = (workspaceId: string, videoId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/video-generation/continuation/final-frame/${videoId}`

export interface CreateContinuationPayload {
  parent_video_id: string
  prompt: string
  duration_seconds: number
  seed_mode?: 'random' | 'fixed'
  seed?: number | null
  enhance_prompt?: boolean
  // Forward-declared (spec §16) — V1 only ever sends `last_frame_i2v`;
  // the backend explicitly rejects `native_prefix` today.
  continuation_mode?: 'last_frame_i2v' | 'native_prefix'
}

export const createContinuationTask = (workspaceId: string, data: CreateContinuationPayload) =>
  saasApi.post<VideoGenTask>(`/workspaces/${workspaceId}/video-generation/continuation`, data)

export interface ComposeSequenceResult {
  sequence_id: string
  storage_key: string
  video_count: number
  duration_seconds: number
  size_bytes: number
  audio_dropped: boolean
}

/** 合成视频 (spec §13) — root + every contiguous completed segment,
 * `segment_index` ASC, via FFmpeg. `videoId` may be the root or any
 * segment of the sequence. */
export const composeContinuationSequence = (workspaceId: string, videoId: string) =>
  saasApi.post<ComposeSequenceResult>(`/workspaces/${workspaceId}/video-generation/continuation/${videoId}/compose`)

export const videoGenComposedOutputUrl = (workspaceId: string, videoId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/video-generation/continuation/${videoId}/composed`

export const videoGenCompositePosterUrl = (workspaceId: string, compositeId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/video-generation/continuation/composites/${compositeId}/poster`

export const deleteVideoGenComposite = (workspaceId: string, compositeId: string) =>
  saasApi.delete<{ id: string; deleted: boolean }>(
    `/workspaces/${workspaceId}/video-generation/continuation/composites/${compositeId}`,
  )

/** "Published Snapshot" — reads back exactly what the last successful
 * `publishSite` call wrote; never re-renders live (spec §11/§14). */
export const fetchSitePublishedPreview = (workspaceId: string, siteId: string, path: string) =>
  saasApi.get<string>(`/workspaces/${workspaceId}/sites/${siteId}/publish/preview`, {
    params: { path },
    responseType: 'text',
    transformResponse: (data) => data,
  })

// ------------------------------------------- AI Chat (VISTOERA_AI_CHAT_TENANT_P0)
// Tenant Console native "AI 对话" feature — one User Message fans out to
// THREE independent provider Executions/Worker Tasks (never one Task
// wrapping three models — see backend `app.saas.services.ai_chat_service`
// docstring). No Worker claims these Tasks yet this phase; they will sit
// `queued` until the next round's PC1 Worker registers the matching
// `ai.browser.chat.*` capabilities (see
// `docs/handoff/AI_BROWSER_CHAT_WORKER_HANDOFF.md`). Image uploads reuse
// the exact same initiate/PUT-to-R2/complete + `/assets/direct` proxy
// contract as Video Generation above — never a bespoke second upload path.
export type AIChatProvider = 'chatgpt' | 'gemini' | 'grok' | 'qwen' | 'deepseek'

export interface AIChatConfig {
  providers: { id: AIChatProvider; label: string }[]
  prompt_max_length: number
  max_images_per_message: number
  image_allowed_mime: string[]
  image_max_size_mb: number
  direct_to_r2: boolean
}

export const getAIChatConfig = (workspaceId: string) =>
  saasApi.get<AIChatConfig>(`/workspaces/${workspaceId}/ai-chat/config`)

export interface AIChatConversationSummary {
  id: string
  title: string
  created_at: string | null
  updated_at: string | null
  bucket: 'today' | 'yesterday' | 'earlier'
  /** True when any execution in this conversation has persisted output_assets. */
  has_generated_image: boolean
}

export const createAIChatConversation = (workspaceId: string) =>
  saasApi.post<{ id: string; title: string; created_at: string }>(`/workspaces/${workspaceId}/ai-chat/conversations`)

export const listAIChatConversations = (workspaceId: string) =>
  saasApi.get<{ items: AIChatConversationSummary[] }>(`/workspaces/${workspaceId}/ai-chat/conversations`)

export const deleteAIChatConversation = (workspaceId: string, conversationId: string) =>
  saasApi.delete<{ ok: boolean }>(`/workspaces/${workspaceId}/ai-chat/conversations/${conversationId}`)

// Mirrors backend `JobStatus` 1:1 (spec §12 — "后端是 source of truth", the
// frontend never invents/derives its own execution state).
export type AIChatExecutionStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled' | 'interrupted'

export interface AIChatOutputAsset {
  ordinal: number
  site_asset_id: string
  picker_item_id: string
  storage_key: string
  content_type: string
  size_bytes: number | null
  width: number | null
  height: number | null
  asset_role?: 'generated' | 'inline' | 'reference' | 'decorative' | 'provider_ui' | 'unknown'
  thumbnail_url: string | null
  download_url: string | null
}

export interface AIChatRichResponse {
  version: number
  blocks: Array<Record<string, unknown>>
  meta?: Record<string, unknown>
}

export interface AIChatExecution {
  id: string
  message_id: string
  conversation_id: string
  provider: AIChatProvider
  provider_label: string
  status: AIChatExecutionStatus
  response_text: string | null
  rich_response?: AIChatRichResponse | null
  output_assets?: AIChatOutputAsset[]
  error_code: string | null
  error_message: string | null
  queued_at: string | null
  started_at: string | null
  completed_at: string | null
}

export interface AIChatMessageAssetRef {
  asset_id: string
  ordinal: number
  original_filename: string | null
}

export interface AIChatMessage {
  id: string
  conversation_id: string
  role: 'user'
  text: string
  created_at: string | null
  assets: AIChatMessageAssetRef[]
  executions: AIChatExecution[]
}

export interface AIChatConversationDetail {
  id: string
  title: string
  created_at: string | null
  updated_at: string | null
  messages: AIChatMessage[]
}

export const getAIChatConversation = (workspaceId: string, conversationId: string) =>
  saasApi.get<AIChatConversationDetail>(`/workspaces/${workspaceId}/ai-chat/conversations/${conversationId}`)

export interface SendAIChatMessagePayload {
  conversation_id?: string | null
  text: string
  asset_ids: string[]
  client_request_id: string
}

/** Atomic entry point (spec §24) — creates Message + 3 Executions + 3
 * Tasks in one backend transaction; `conversation_id` omitted auto-creates
 * a new conversation. */
export const sendAIChatMessage = (workspaceId: string, data: SendAIChatMessagePayload) =>
  saasApi.post<AIChatMessage>(`/workspaces/${workspaceId}/ai-chat/messages`, data)

export const getAIChatMessageExecutions = (workspaceId: string, messageId: string) =>
  saasApi.get<{ items: AIChatExecution[] }>(`/workspaces/${workspaceId}/ai-chat/messages/${messageId}/executions`)

/** Retries ONLY the targeted provider (spec §11/§19) — a brand-new
 * Execution + Task for the same (message, provider); sibling providers'
 * Executions are never touched. */
export const retryAIChatExecution = (workspaceId: string, executionId: string) =>
  saasApi.post<AIChatExecution>(`/workspaces/${workspaceId}/ai-chat/executions/${executionId}/retry`)

export interface AIChatAsset {
  id: string
  workspace_id: string
  media_type: 'image'
  status: 'pending' | 'ready' | 'failed'
  original_filename: string
  content_type: string
  size_bytes: number | null
  width: number | null
  height: number | null
  created_at: string | null
}

export const aiChatInitiateAssetUpload = (
  workspaceId: string,
  data: { filename: string; content_type: string; size?: number },
) =>
  saasApi.post<{ asset_id: string; media_type: 'image'; storage_key: string; upload_url: string; expires_at: string }>(
    `/workspaces/${workspaceId}/ai-chat/assets/initiate`,
    data,
  )

export const aiChatCompleteAssetUpload = (workspaceId: string, assetId: string) =>
  saasApi.post<AIChatAsset>(`/workspaces/${workspaceId}/ai-chat/assets/${assetId}/complete`)

/** Proxy transport (used whenever `AIChatConfig.direct_to_r2` is false, the
 * default — same rationale as `videoGenUploadAssetDirect`). */
export const aiChatUploadAssetDirect = (workspaceId: string, file: File) => {
  const form = new FormData()
  form.append('file', file)
  return saasApi.post<AIChatAsset>(`/workspaces/${workspaceId}/ai-chat/assets/direct`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const aiChatRetryAssetUploadPresign = (workspaceId: string, assetId: string) =>
  saasApi.post<{ asset_id: string; storage_key: string; upload_url: string }>(
    `/workspaces/${workspaceId}/ai-chat/assets/${assetId}/retry-presign`,
  )

/** Authenticated same-origin thumbnail/full-image byte delivery (spec §27
 * — never a bare R2 URL, never a local filesystem path). */
export const aiChatAssetContentUrl = (workspaceId: string, assetId: string) =>
  `${SAAS_API_BASE_URL}/workspaces/${workspaceId}/ai-chat/assets/${assetId}/content`

// ------------------------------------------------- Virtual IP Positioning
// VISTOERA_VIRTUAL_IP_POSITIONING_STEP1_V1 — STEP 1 "告诉 AI 你想做什么"
// questionnaire schema + per-Workspace draft. ``options``/``fields``/
// ``answers``/... stay loosely typed (``Record<string, unknown>``-ish)
// deliberately — the questionnaire is backend-seeded/data-driven (spec
// §9) and expected to grow question-by-question without a frontend type
// change; ``VirtualIpQuestion['type']`` is the one place the renderer
// actually branches.
export type VirtualIpQuestionType =
  | 'multi_select'
  | 'single_select'
  | 'text'
  | 'textarea'
  | 'composite'
  | 'repeatable_group'

export interface VirtualIpQuestionOption {
  value: string
  label: string
}

export interface VirtualIpSecondaryField {
  key: string
  label: string
  placeholder?: string
  max_length?: number | null
}

export interface VirtualIpSubField {
  key: string
  title: string
  type: VirtualIpQuestionType
  required?: boolean
  max_selections?: number | null
  options?: VirtualIpQuestionOption[]
  placeholder?: string
}

export interface VirtualIpRepeatableField {
  key: string
  label: string
  placeholder?: string
}

export interface VirtualIpConditionalVisibility {
  trigger_option: string
  field_key: string
  field_label: string
  field_placeholder?: string
}

export interface VirtualIpQuestion {
  id: string
  key: string
  title: string
  description?: string
  type: VirtualIpQuestionType
  required: boolean
  order: number
  max_selections?: number | null
  options?: VirtualIpQuestionOption[]
  placeholder?: string
  max_length?: number | null
  secondary_field?: VirtualIpSecondaryField | null
  fields?: VirtualIpSubField[]
  repeatable_fields?: VirtualIpRepeatableField[]
  repeatable_max_items?: number | null
  conditional_visibility?: VirtualIpConditionalVisibility | null
}

export interface VirtualIpQuestionnaire {
  id: string
  version: string
  status: string
  questions: VirtualIpQuestion[]
  created_at: string
  updated_at: string
}

/** Everything is deliberately ``unknown``-shaped answer values — the
 * frontend never inspects ``answers`` by fixed key, only via the
 * questionnaire's own ``question.key`` (spec §9: never ``q1``/``q2``
 * hardcoding, front or back). */
export type VirtualIpAnswers = Record<string, unknown>

export type VirtualIpDraftStatus =
  | 'draft'
  | 'step_1_completed'
  | 'step_2_completed'
  | 'step_3_completed'
  | 'positioning_confirmed'

/** The stable, UI-decoupled ``virtual_ip_intake_v1`` contract
 * (VISTOERA_VIRTUAL_IP_POSITIONING_STEP1_HARNESS_READY_PATCH_V1 §三) —
 * this is what a future AI Harness / STEP 2 reads, never the raw
 * ``answers`` above. Enum values inside every list here (e.g.
 * ``business_goals``) are always the same stable ``value`` codes used
 * in ``VirtualIpQuestionOption``, never the Chinese ``label``. */
export interface VirtualIpIntakeV1 {
  schema_version: string
  business_goals: string[]
  commercial_direction: { categories: string[]; description: string }
  target_audience: {
    age_ranges: string[]
    gender_focus: string
    spending_power: string
    description: string
  }
  content_preferences: string[]
  target_platforms: string[]
  self_presence_level: string
  desired_first_impressions: string[]
  benchmark_references: Array<{ type: 'account_name' | 'url'; value: string }>
  avoidances: string
  metadata: { questionnaire_version: string; source: string }
  extensions?: Record<string, unknown>
}

/** ``virtual_ip_intake_v1`` + its deterministic Markdown projection
 * (spec §六) — the database row (never the Markdown) remains Source of
 * Truth; this ``markdown`` is regenerated from ``intake`` at submit
 * time, never hand-edited. */
export interface VirtualIpIntakeOut {
  schema_version: string
  intake: VirtualIpIntakeV1
  markdown: string
}

export interface VirtualIpDraft {
  id: string
  workspace_id: string
  questionnaire_id: string
  questionnaire_version: string
  answers: VirtualIpAnswers
  current_step: number
  status: VirtualIpDraftStatus
  intake: VirtualIpIntakeOut | null
  selected_proposal_id?: string | null
  positioning_profile_revision?: number
  positioning_confirmed_at?: string | null
  workflow_confirmed_at?: string | null
  created_at: string
  updated_at: string
}

const virtualIpBase = (workspaceId: string) => `/workspaces/${workspaceId}/virtual-ip`

export type VirtualIpProjectLifecycleStatus = 'draft' | 'positioning' | 'generating' | 'completed'

export interface VirtualIpProjectSummary {
  id: string
  name: string
  one_liner: string | null
  status: VirtualIpProjectLifecycleStatus | string
  current_step: number
  cover_asset_id: string | null
  cover_url: string | null
  tags: string[]
  workflow_confirmed_at: string | null
  updated_at: string | null
  created_at: string | null
}

export interface VirtualIpProjectList {
  items: VirtualIpProjectSummary[]
  total: number
  page: number
  page_size: number
}

export interface VirtualIpAssetSummary {
  id: string
  project_id: string
  name: string
  one_liner: string | null
  status: string
  cover_asset_id: string | null
  cover_url: string | null
  tags: string[]
  workflow_confirmed_at: string | null
  updated_at: string | null
  created_at: string | null
}

export interface VirtualIpAssetList {
  items: VirtualIpAssetSummary[]
  total: number
  page: number
  page_size: number
}

export interface VirtualIpAssetDetail extends VirtualIpAssetSummary {
  positioning_profile: Record<string, unknown> | null
  character: {
    selected_id?: string
    selected_kind?: string
    cover_asset_id?: string
    cover_url?: string | null
  } | null
  voice: {
    voice_mode?: string | null
    display_name?: string | null
    description?: string | null
    preview_text?: string | null
    preview_audio_asset_id?: string | null
    status?: string | null
  } | null
  selected_ip_type: string | null
  selected_proposal_id: string | null
}

export const getVirtualIpQuestionnaire = (workspaceId: string) =>
  saasApi.get<VirtualIpQuestionnaire>(`${virtualIpBase(workspaceId)}/questionnaire`)

export const listVirtualIpProjects = (
  workspaceId: string,
  params?: { q?: string; page?: number; page_size?: number },
) => saasApi.get<VirtualIpProjectList>(`${virtualIpBase(workspaceId)}/projects`, { params })

export const createVirtualIpProject = (workspaceId: string) =>
  saasApi.post<VirtualIpProjectSummary>(`${virtualIpBase(workspaceId)}/projects`)

export const getVirtualIpProject = (workspaceId: string, projectId: string) =>
  saasApi.get<VirtualIpProjectSummary>(`${virtualIpBase(workspaceId)}/projects/${projectId}`)

export const listVirtualIpAssets = (
  workspaceId: string,
  params?: { q?: string; page?: number; page_size?: number },
) => saasApi.get<VirtualIpAssetList>(`${virtualIpBase(workspaceId)}/assets`, { params })

export const getVirtualIpAsset = (workspaceId: string, assetId: string) =>
  saasApi.get<VirtualIpAssetDetail>(`${virtualIpBase(workspaceId)}/assets/${assetId}`)

export const getVirtualIpDraft = (workspaceId: string, draftId?: string) =>
  draftId
    ? saasApi.get<VirtualIpDraft>(`${virtualIpBase(workspaceId)}/draft/${draftId}`)
    : saasApi.get<VirtualIpDraft>(`${virtualIpBase(workspaceId)}/draft`)

export const saveVirtualIpDraft = (
  workspaceId: string,
  answers: VirtualIpAnswers,
  currentStep?: number,
  draftId?: string,
) =>
  draftId
    ? saasApi.put<VirtualIpDraft>(`${virtualIpBase(workspaceId)}/draft/${draftId}`, {
        answers,
        current_step: currentStep,
      })
    : saasApi.put<VirtualIpDraft>(`${virtualIpBase(workspaceId)}/draft`, {
        answers,
        current_step: currentStep,
      })

export const submitVirtualIpStep1 = (workspaceId: string, answers: VirtualIpAnswers, draftId?: string) =>
  draftId
    ? saasApi.post<VirtualIpDraft>(`${virtualIpBase(workspaceId)}/draft/${draftId}/submit-step1`, { answers })
    : saasApi.post<VirtualIpDraft>(`${virtualIpBase(workspaceId)}/draft/submit-step1`, { answers })

export const confirmVirtualIpWorkflow = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpDraft>(`${virtualIpBase(workspaceId)}/draft/${draftId}/workflow/confirm`)

// -------------------------------------------- STEP 2 — AI Harness (V1)
// VISTOERA_VIRTUAL_IP_POSITIONING_STEP2_HARNESS_INTEGRATION_V1 §十四/§十五.
// ``analysis`` stays an open ``Record<string, unknown>`` here (never a
// rigid nested interface) for the same reason ``VirtualIpIntakeV1``'s own
// fields are all open — this response shape must not need a frontend
// change if ``virtual_ip_positioning_analysis_v1`` grows a v2. The page
// component destructures the specific fields it renders defensively.
export type VirtualIpAnalysisStatus = 'NOT_STARTED' | 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'STALE'

export interface VirtualIpAnalysis {
  status: VirtualIpAnalysisStatus
  run_id: string | null
  schema_version: string | null
  analysis: Record<string, unknown> | null
  markdown: string | null
  source_intake_hash: string | null
  current_intake_hash: string | null
  error_code: string | null
  error_message: string | null
  repair_attempts: number | null
  created_at: string | null
  completed_at: string | null
}

const virtualIpAnalysisPath = (workspaceId: string, draftId: string) =>
  `${virtualIpBase(workspaceId)}/draft/${draftId}/analysis`

export const getVirtualIpAnalysis = (workspaceId: string, draftId: string) =>
  saasApi.get<VirtualIpAnalysis>(virtualIpAnalysisPath(workspaceId, draftId))

export const startVirtualIpAnalysis = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpAnalysis>(virtualIpAnalysisPath(workspaceId, draftId))

export const regenerateVirtualIpAnalysis = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpAnalysis>(`${virtualIpAnalysisPath(workspaceId, draftId)}/regenerate`)

// ---------------------------------------- STEP 2 — IP 类型选择 (V1)
// VISTOERA_VIRTUAL_IP_STEP2_IP_TYPE_SELECTION_UI_P0. ``candidates`` /
// ``analysis_summary`` / ``decision_guidance`` stay open (never a rigid
// nested interface) — this is the real, unmodified ``ip_type_recommendation_v1``
// structured result, read straight from the backend's persisted AI
// Harness Memory. The page renders only fields that are actually present;
// it never invents a field to match a mockup.
export type VirtualIpTypeRecommendationStatus = 'NOT_STARTED' | 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'STALE'

export interface VirtualIpTypeCandidate {
  rank: number
  ip_type: string
  display_name: string
  suitability_score: number
  one_sentence_positioning: string
  why_it_fits: string[]
  strengths: string[]
  risks: string[]
  audience_fit: string
  platform_fit: string
  monetization_fit: string
  long_term_scalability: string
  visual_direction_preview: string[]
  best_for: string[]
  [key: string]: unknown
}

export interface VirtualIpSelectedType {
  ip_type: string
  source_run_id: string | null
  selected_at: string | null
  snapshot: VirtualIpTypeCandidate | null
}

export interface VirtualIpTypeRecommendation {
  status: VirtualIpTypeRecommendationStatus
  run_id: string | null
  schema_version: string | null
  analysis_summary: Record<string, unknown> | null
  candidates: VirtualIpTypeCandidate[] | null
  decision_guidance: Record<string, unknown> | null
  markdown: string | null
  source_intake_hash: string | null
  current_intake_hash: string | null
  error_code: string | null
  error_message: string | null
  repair_attempts: number | null
  created_at: string | null
  completed_at: string | null
  selected: VirtualIpSelectedType | null
}

const virtualIpTypeRecommendationPath = (workspaceId: string, draftId: string) =>
  `${virtualIpBase(workspaceId)}/draft/${draftId}/type-recommendation`

export const getVirtualIpTypeRecommendation = (workspaceId: string, draftId: string) =>
  saasApi.get<VirtualIpTypeRecommendation>(virtualIpTypeRecommendationPath(workspaceId, draftId))

export const startVirtualIpTypeRecommendation = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpTypeRecommendation>(virtualIpTypeRecommendationPath(workspaceId, draftId))

export const regenerateVirtualIpTypeRecommendation = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpTypeRecommendation>(`${virtualIpTypeRecommendationPath(workspaceId, draftId)}/regenerate`)

export const selectVirtualIpType = (workspaceId: string, draftId: string, ipType: string, sourceRunId: string) =>
  saasApi.put<VirtualIpTypeRecommendation>(`${virtualIpTypeRecommendationPath(workspaceId, draftId)}/selection`, {
    ip_type: ipType,
    source_run_id: sourceRunId,
  })

export type VirtualIpHarnessStatus =
  | 'NOT_STARTED'
  | 'QUEUED'
  | 'RUNNING'
  | 'REGENERATING'
  | 'COMPLETED'
  | 'FAILED'
  | 'STALE'

export interface VirtualIpProposals {
  status: VirtualIpHarnessStatus
  run_id: string | null
  schema_version: string | null
  proposals_payload: Record<string, unknown> | null
  markdown: string | null
  source_context_hash: string | null
  current_context_hash: string | null
  selected_proposal_id: string | null
  selected_proposal: Record<string, unknown> | null
  selected_proposal_at: string | null
  error_code: string | null
  error_message: string | null
  repair_attempts: number | null
  created_at: string | null
  completed_at: string | null
}

const virtualIpProposalsPath = (workspaceId: string, draftId: string) =>
  `${virtualIpBase(workspaceId)}/draft/${draftId}/proposals`

export const getVirtualIpProposals = (workspaceId: string, draftId: string) =>
  saasApi.get<VirtualIpProposals>(virtualIpProposalsPath(workspaceId, draftId))

export const startVirtualIpProposals = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpProposals>(virtualIpProposalsPath(workspaceId, draftId))

export const regenerateVirtualIpProposals = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpProposals>(`${virtualIpProposalsPath(workspaceId, draftId)}/regenerate`)

export const selectVirtualIpProposal = (workspaceId: string, draftId: string, proposalId: string) =>
  saasApi.put<VirtualIpDraft>(`${virtualIpProposalsPath(workspaceId, draftId)}/select`, {
    proposal_id: proposalId,
  })

export type VirtualIpPositioningProfileStatus =
  | 'LOADING'
  | 'READY_TO_CONFIRM'
  | 'CONFIRMING'
  | 'CONFIRMED'
  | 'STALE'
  | 'ERROR'

export interface VirtualIpPositioningProfile {
  status: VirtualIpPositioningProfileStatus
  profile_preview: Record<string, unknown> | null
  confirmed_profile: Record<string, unknown> | null
  positioning_revision: number
  confirmed_at: string | null
  stale_reason: string | null
  error_message: string | null
}

const virtualIpPositioningProfilePath = (workspaceId: string, draftId: string) =>
  `${virtualIpBase(workspaceId)}/draft/${draftId}/positioning-profile`

export const getVirtualIpPositioningProfile = (workspaceId: string, draftId: string) =>
  saasApi.get<VirtualIpPositioningProfile>(virtualIpPositioningProfilePath(workspaceId, draftId))

export const confirmVirtualIpPositioningProfile = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpPositioningProfile>(`${virtualIpPositioningProfilePath(workspaceId, draftId)}/confirm`)

export const reopenVirtualIpPositioningProfile = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpDraft>(`${virtualIpPositioningProfilePath(workspaceId, draftId)}/reopen`)

// ---------------------------------- STEP 5 — IP Character Generation
// VISTOERA_VIRTUAL_IP_STEP5_V1 — thin client for
// `app.api.routes.saas_virtual_ip_character`. Every shape below mirrors
// `virtual_ip_character_service.batch_out()` / `get_readiness` /
// `get_context` 1:1 — no client-side re-derivation of readiness or
// status; the backend is the sole source of truth for both.
export type VirtualIpCharacterSourceMode = 'AI_FROM_POSITIONING' | 'REFERENCE_IMAGES' | 'EXISTING_IP_ASSET' | 'REAL_PERSON_CLONE'

export type VirtualIpReferenceRole = 'IDENTITY' | 'STYLE' | 'PRODUCT' | 'COLOR' | 'CLOTHING' | 'SCENE' | 'OTHER'

export type VirtualIpCharacterBatchStatus =
  | 'DRAFT'
  | 'READY_TO_GENERATE'
  | 'QUEUED'
  | 'RUNNING'
  | 'WAITING_PROVIDER'
  | 'RESULT_RECEIVED'
  | 'SUCCEEDED'
  | 'FAILED'

export interface VirtualIpCharacterReferenceAsset {
  asset_id: string
  reference_role: VirtualIpReferenceRole | string
}

export type VirtualIpRealPersonAngle = 'front' | 'left_45' | 'right_45' | 'half_body' | 'full_body'

export interface VirtualIpRealPersonPhoto {
  asset_id: string
  angle: VirtualIpRealPersonAngle | string
}

export interface VirtualIpCharacterRun {
  run_id: string
  attempt_number: number
  skill_name: string
  skill_version: string
  provider: string
  provider_mode: string
  harness_run_id: string | null
  status: 'QUEUED' | 'DISPATCHED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED'
  error_code: string | null
  error_message: string | null
  created_at: string | null
  started_at: string | null
  completed_at: string | null
}

export interface VirtualIpCharacterCandidate {
  candidate_id: string
  asset_id: string | null
  canonical_asset_reference: string | null
  concept_name: string | null
  short_difference: string | null
  visual_tags: string[]
  sort_order: number
  thumbnail_url: string | null
  download_url: string | null
}

export interface VirtualIpCharacterBatch {
  generation_batch_id: string
  virtual_ip_profile_id: string
  status: VirtualIpCharacterBatchStatus
  status_label: string
  source_mode: VirtualIpCharacterSourceMode | string
  reference_assets: VirtualIpCharacterReferenceAsset[]
  existing_character_asset_id: string | null
  real_person_mode: boolean
  real_person_photos: VirtualIpRealPersonPhoto[]
  real_person_authorization_confirmed_at: string | null
  visual_preferences: string | null
  avoidances: string | null
  generation_count: number
  positioning_profile_revision: number
  error_code: string | null
  error_message: string | null
  latest_run: VirtualIpCharacterRun | null
  candidates: VirtualIpCharacterCandidate[]
  created_at: string | null
  updated_at: string | null
}

export interface VirtualIpCharacterReadiness {
  step5_page_ready: boolean
  upstream_positioning_ready: boolean
  upstream_not_ready_reason: string | null
  ai_execution_ready: boolean
  ai_execution_status: 'READY' | 'TEMPORARILY_UNAVAILABLE'
  ai_execution_message: string
}

export interface VirtualIpCharacterContext {
  virtual_ip_id: string
  readiness: VirtualIpCharacterReadiness
  confirmed_profile: Record<string, unknown> | null
  positioning_profile_revision: number
  latest_batch: VirtualIpCharacterBatch | null
}

const virtualIpCharacterBase = (workspaceId: string, draftId: string) =>
  `${virtualIpBase(workspaceId)}/draft/${draftId}/character-generation`

export const getVirtualIpCharacterReadiness = (workspaceId: string, draftId: string) =>
  saasApi.get<VirtualIpCharacterReadiness>(`${virtualIpCharacterBase(workspaceId, draftId)}/readiness`)

export const getVirtualIpCharacterContext = (workspaceId: string, draftId: string) =>
  saasApi.get<VirtualIpCharacterContext>(`${virtualIpCharacterBase(workspaceId, draftId)}/context`)

export interface VirtualIpCharacterDraftSaveInput {
  source_mode: VirtualIpCharacterSourceMode | string
  reference_assets?: VirtualIpCharacterReferenceAsset[]
  existing_character_asset_id?: string | null
  real_person_mode?: boolean
  real_person_photos?: VirtualIpRealPersonPhoto[]
  visual_preferences?: string | null
  avoidances?: string | null
  generation_count?: number
}

export const saveVirtualIpCharacterDraft = (workspaceId: string, draftId: string, body: VirtualIpCharacterDraftSaveInput) =>
  saasApi.put<VirtualIpCharacterBatch>(`${virtualIpCharacterBase(workspaceId, draftId)}/draft`, body)

export const authorizeVirtualIpRealPerson = (workspaceId: string, draftId: string, batchId: string) =>
  saasApi.post<VirtualIpCharacterBatch>(`${virtualIpCharacterBase(workspaceId, draftId)}/batches/${batchId}/authorize-real-person`)

export const dispatchVirtualIpCharacterBatch = (workspaceId: string, draftId: string, batchId: string) =>
  saasApi.post<VirtualIpCharacterBatch>(`${virtualIpCharacterBase(workspaceId, draftId)}/batches/${batchId}/dispatch`)

export const retryVirtualIpCharacterBatch = (workspaceId: string, draftId: string, batchId: string) =>
  saasApi.post<VirtualIpCharacterBatch>(`${virtualIpCharacterBase(workspaceId, draftId)}/batches/${batchId}/retry`)

export const getVirtualIpCharacterBatch = (workspaceId: string, draftId: string, batchId: string) =>
  saasApi.get<VirtualIpCharacterBatch>(`${virtualIpCharacterBase(workspaceId, draftId)}/batches/${batchId}`)

// ---------------------------------------- STEP 6 — 形象选择与优化 (V1)
// VISTOERA_VIRTUAL_IP_STEP6_CHARACTER_SELECTION_AND_REFINEMENT_V1 — thin
// client for `app.api.routes.saas_virtual_ip_character_selection`. STEP 6
// never generates a candidate itself: every batch/candidate below IS a
// real STEP 5 shape (`VirtualIpCharacterBatch`/`VirtualIpCharacterCandidate`
// above) — the workspace read model is `batch_out()` plus exactly two
// additive, never-persisted fields (`round_index` on the batch,
// `slot_label`/`candidate_kind` on each candidate — see
// `virtual_ip_character_selection_service._slot_letter`), never a
// re-shaped/renamed STEP6-only projection. Only refinement requests/
// versions/the final selection are STEP 6's own tables — see
// `app.saas.models.virtual_ip_character_selection`.
//
// PROVENANCE (spec §2/§32) — `VirtualIpCharacterRun`'s Provider/Run ID/
// Execution ID fields above are intentionally NEVER read by
// `VirtualIpStep6Page` — they stay backend-retained-only (Platform
// Admin/audit/forensic), never rendered in tenant UI.
export type VirtualIpCharacterCandidateKind = 'original' | 'refinement'

export interface VirtualIpCharacterAssetReference {
  asset_id: string
  thumbnail_url: string
  preview_url: string
  full_url: string
  download_url: string
}

export interface VirtualIpCharacterSelectionCandidate extends VirtualIpCharacterCandidate {
  /** "A"/"B"/... — a global slot letter across every round (spec §9's
   * "第一轮 A-D / 第二轮 E-H"), computed fresh on every read — never
   * persisted. Always set for `candidate_kind === 'original'`. */
  slot_label: string | null
  candidate_kind: VirtualIpCharacterCandidateKind
}

export interface VirtualIpCharacterSelectionBatch extends Omit<VirtualIpCharacterBatch, 'candidates'> {
  /** 1-based — which "重新生成更多方案" round this batch is (spec §9). */
  round_index: number
  candidates: VirtualIpCharacterSelectionCandidate[]
}

export type VirtualIpCharacterRefinementRequestStatus = 'pending' | 'completed' | 'failed'

export interface VirtualIpCharacterRefinementRequestOut {
  request_id: string
  base_candidate_id: string
  base_candidate_kind: VirtualIpCharacterCandidateKind
  instruction: string
  status: VirtualIpCharacterRefinementRequestStatus
  result_version_id: string | null
  error_code: string | null
  error_message: string | null
  created_at: string | null
  updated_at: string | null
}

export interface VirtualIpCharacterRefinementVersionOut {
  version_id: string
  request_id: string
  asset_id: string
  canonical_asset_reference: VirtualIpCharacterAssetReference
  concept_name: string | null
  short_difference: string | null
  visual_tags: string[]
  /** The candidate/version this was refined FROM — either a real STEP 5
   * `candidate_id` (`parent_kind === 'original'`) or an earlier
   * `version_id` of this same lineage (`parent_kind === 'refinement'`). */
  parent_candidate_id: string
  parent_kind: VirtualIpCharacterCandidateKind
  /** The STEP 5 `candidate_id` at the root of this version's lineage —
   * denormalized onto every version so the whole A → A-v1 → A-v2 chain
   * groups by ONE stable key regardless of round trips (spec §21). */
  lineage_root_id: string
  version_index: number
  refinement_instruction: string | null
  created_at: string | null
}

export type VirtualIpCharacterSelectionOut = {
  selected_id: string
  selected_kind: VirtualIpCharacterCandidateKind
  selected_by_user_id: string | null
  selected_at: string | null
}

export interface VirtualIpCharacterSelectionWorkspace {
  gate_ok: boolean
  gate_reason: string | null
  real_person_mode: boolean
  has_any_batch: boolean
  batches: VirtualIpCharacterSelectionBatch[]
  refinement_requests: VirtualIpCharacterRefinementRequestOut[]
  refinement_versions: VirtualIpCharacterRefinementVersionOut[]
  selection: VirtualIpCharacterSelectionOut | null
}

export type VirtualIpCharacterSelectionRegenerateMode = 'keep_positioning' | 'reference_candidate' | 'new_instruction'

const virtualIpCharacterSelectionBase = (workspaceId: string, draftId: string) =>
  `${virtualIpBase(workspaceId)}/draft/${draftId}/character-selection`

export const getVirtualIpCharacterSelectionWorkspace = (workspaceId: string, draftId: string) =>
  saasApi.get<VirtualIpCharacterSelectionWorkspace>(`${virtualIpCharacterSelectionBase(workspaceId, draftId)}/workspace`)

export interface VirtualIpCharacterSelectionRegenerateInput {
  mode: VirtualIpCharacterSelectionRegenerateMode
  reference_candidate_id?: string | null
  reference_candidate_kind?: VirtualIpCharacterCandidateKind
  instruction?: string | null
}

export const regenerateVirtualIpCharacterSelectionBatch = (
  workspaceId: string,
  draftId: string,
  payload: VirtualIpCharacterSelectionRegenerateInput,
) =>
  saasApi.post<VirtualIpCharacterSelectionWorkspace>(
    `${virtualIpCharacterSelectionBase(workspaceId, draftId)}/batches/regenerate`,
    payload,
  )

export interface VirtualIpCharacterSelectionRefinementInput {
  base_candidate_id: string
  base_candidate_kind: VirtualIpCharacterCandidateKind
  instruction: string
}

export const submitVirtualIpCharacterSelectionRefinement = (
  workspaceId: string,
  draftId: string,
  payload: VirtualIpCharacterSelectionRefinementInput,
) =>
  saasApi.post<VirtualIpCharacterSelectionWorkspace>(
    `${virtualIpCharacterSelectionBase(workspaceId, draftId)}/refinements`,
    payload,
  )

export const retryVirtualIpCharacterSelectionRefinement = (workspaceId: string, draftId: string, requestId: string) =>
  saasApi.post<VirtualIpCharacterSelectionWorkspace>(
    `${virtualIpCharacterSelectionBase(workspaceId, draftId)}/refinements/${requestId}/retry`,
  )

export const selectVirtualIpCharacterSelectionFinal = (
  workspaceId: string,
  draftId: string,
  payload: { selected_id: string; selected_kind: VirtualIpCharacterCandidateKind },
) =>
  saasApi.put<VirtualIpCharacterSelectionWorkspace>(
    `${virtualIpCharacterSelectionBase(workspaceId, draftId)}/selection`,
    payload,
  )

// ------------------------------------------------------- Virtual IP STEP 7 — Voice Identity
// VISTOERA_VIRTUAL_IP_STEP7_VOICE_IDENTITY_UI_AND_DOMAIN_V1

export type VirtualIpVoiceCreationMode = 'DESIGN_FROM_TEXT' | 'CLONE_FROM_REFERENCE' | 'CLONE_SELF'

export type VirtualIpVoiceMode = 'NONE' | 'AI_GENERATED' | 'REFERENCE' | 'CLONED'

export type VirtualIpVoiceCandidateStatus = 'PENDING' | 'GENERATING' | 'READY' | 'FAILED' | 'SELECTED'

export interface VirtualIpVoiceAudioAsset {
  asset_id: string
  filename: string | null
  mime_type: string
  size_bytes: number
  duration_ms: number | null
  media_type: string
  playback_url: string
  status: string
}

export interface VirtualIpVoiceSelectedCharacter {
  selected_id: string
  selected_kind: VirtualIpCharacterCandidateKind | string
  candidate_label: string | null
  slot_label: string | null
  ip_name: string | null
  core_positioning: string
  persona_traits: string[]
  thumbnail_url: string | null
  download_url: string | null
  asset_id: string | null
}

export interface VirtualIpVoiceCandidate {
  candidate_id: string
  batch_id: string
  creation_mode: VirtualIpVoiceCreationMode | string
  display_name: string
  description: string
  trait_tags: string[]
  preview_text: string
  status: VirtualIpVoiceCandidateStatus | string
  sort_order: number
  audio_asset_id: string | null
  audio: VirtualIpVoiceAudioAsset | null
  selected_at: string | null
  created_at: string | null
}

export interface VirtualIpVoiceBatch {
  batch_id: string
  creation_mode: VirtualIpVoiceCreationMode | string
  requested_candidate_count: number
  delivered_candidate_count: number
  status: string
  error_code: string | null
  error_message: string | null
  created_at: string | null
  completed_at: string | null
  candidates: VirtualIpVoiceCandidate[]
}

export interface VirtualIpVoiceProfile {
  voice_profile_id: string
  creation_mode: VirtualIpVoiceCreationMode | string
  voice_mode: VirtualIpVoiceMode | string
  display_name: string | null
  description: string | null
  language: string
  locale: string
  voice_brief: string | null
  preview_text: string | null
  voice_traits: Record<string, unknown>
  speaking_style: Record<string, unknown>
  reference_audio_assets: VirtualIpVoiceAudioAsset[]
  reference_audio_asset_ids: string[]
  voice_rights_confirmed: boolean
  voice_rights_confirmed_at: string | null
  voice_rights_confirmation_version: string | null
  status: string
  version: number
  selected_candidate_id: string | null
  latest_batch_id: string | null
  confirmed_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface VirtualIpVoiceWorkspace {
  gate_ok: boolean
  gate_reason: string | null
  recommended_creation_mode: VirtualIpVoiceCreationMode | string
  voice_mode: VirtualIpVoiceMode | string | null
  selected_character: VirtualIpVoiceSelectedCharacter | null
  profile: VirtualIpVoiceProfile | null
  latest_batch: VirtualIpVoiceBatch | null
  candidates: VirtualIpVoiceCandidate[]
  voice_execution_ready: boolean
  voice_execution_status: string
  voice_execution_message: string
  voice_confirmed: boolean
}

export interface VirtualIpVoiceIntakeInput {
  creation_mode?: VirtualIpVoiceCreationMode | string
  voice_mode?: VirtualIpVoiceMode | string
  voice_brief?: string | null
  preview_text?: string | null
  speaking_style?: Record<string, unknown> | null
  voice_traits?: Record<string, unknown> | null
  reference_audio_asset_ids?: string[] | null
  voice_rights_confirmed?: boolean | null
}

const virtualIpVoiceBase = (workspaceId: string, draftId: string) =>
  `${virtualIpBase(workspaceId)}/draft/${draftId}/voice`

export const getVirtualIpVoiceWorkspace = (workspaceId: string, draftId: string) =>
  saasApi.get<VirtualIpVoiceWorkspace>(virtualIpVoiceBase(workspaceId, draftId))

export const saveVirtualIpVoiceIntake = (workspaceId: string, draftId: string, body: VirtualIpVoiceIntakeInput) =>
  saasApi.put<VirtualIpVoiceWorkspace>(`${virtualIpVoiceBase(workspaceId, draftId)}/intake`, body)

export const uploadVirtualIpVoiceReferenceAudio = (
  workspaceId: string,
  draftId: string,
  file: File,
  durationMs?: number | null,
) => {
  const form = new FormData()
  form.append('file', file)
  if (durationMs != null) form.append('duration_ms', String(durationMs))
  return saasApi.post<VirtualIpVoiceAudioAsset>(`${virtualIpVoiceBase(workspaceId, draftId)}/reference-audio`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const generateVirtualIpVoiceCandidates = (
  workspaceId: string,
  draftId: string,
  candidateCount = 3,
) =>
  saasApi.post<VirtualIpVoiceWorkspace>(`${virtualIpVoiceBase(workspaceId, draftId)}/generate`, {
    candidate_count: candidateCount,
  }, {
    timeout: 240000,
  })

export const getVirtualIpVoiceBatch = (workspaceId: string, draftId: string, batchId: string) =>
  saasApi.get<VirtualIpVoiceBatch>(`${virtualIpVoiceBase(workspaceId, draftId)}/batches/${batchId}`)

export const selectVirtualIpVoiceCandidate = (workspaceId: string, draftId: string, candidateId: string) =>
  saasApi.post<VirtualIpVoiceWorkspace>(
    `${virtualIpVoiceBase(workspaceId, draftId)}/candidates/${candidateId}/select`,
  )

export const confirmVirtualIpVoice = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpVoiceWorkspace>(`${virtualIpVoiceBase(workspaceId, draftId)}/confirm`)

export const confirmVirtualIpVoiceNone = (workspaceId: string, draftId: string) =>
  saasApi.post<VirtualIpVoiceWorkspace>(`${virtualIpVoiceBase(workspaceId, draftId)}/confirm-none`)

// ------------------------------------------------------- Content Publishing Center
// VISTOERA_CONTENT_PUBLISHING_CENTER_UI_AND_DOMAIN_V1 — thin client for
// `app.api.routes.saas_publishing`. `source_asset_id` on every shape below
// is a real `video-generation` task id — `videoGenTaskOutputUrl`/
// `videoGenTaskPosterUrl` above already serve its real bytes; this module
// never introduces a second video byte-delivery path (spec §十一).

export type PublishTabKey = 'all' | 'ready_to_confirm' | 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed'
export type PublishItemStatus =
  | 'draft'
  | 'ready_for_publisher'
  | 'scheduled'
  | 'queued'
  | 'waiting_for_local_agent'
  | 'publishing'
  | 'awaiting_human_confirmation'
  | 'verifying_result'
  | 'published'
  | 'failed'
  | 'failed_retryable'
  | 'failed_final'
  | 'cancelled'
export type PublishMode = 'immediate' | 'scheduled'

export interface PublishTabSummary {
  key: PublishTabKey
  label: string
  count: number
}

export interface PublishItemSummary {
  id: string
  workspace_id: string
  source_asset_id: string
  content_type: string
  title: string
  duration_seconds: number | null
  status: PublishItemStatus
  status_label: string
  bucket: PublishTabKey
  publish_mode: PublishMode
  scheduled_at: string | null
  confirmed_at: string | null
  selected_agent_id?: string | null
  platforms: string[]
  platform_count: number
  ready_for_confirmation: boolean
  created_at: string | null
  updated_at: string | null
}

export interface PublishItemsListResult {
  items: PublishItemSummary[]
  total: number
  page: number
  page_size: number
  summary: Record<string, number>
  tabs: PublishTabSummary[]
}

export interface PlatformPublishPackageOut {
  id: string
  publish_item_id: string | null
  platform: string
  platform_label: string
  platform_icon: string | null
  platform_account_id: string | null
  content_type: string
  title: string
  caption: string
  cover_asset_id: string | null
  hashtags: string[]
  metadata: Record<string, unknown>
  selected_for_publish: boolean
  status: string
  is_complete: boolean
  created_at: string | null
  updated_at: string | null
}

export interface PublishSourceVideoSummary {
  available: boolean
  id: string
  prompt?: string | null
  status?: string
  duration_seconds?: number | null
  width?: number | null
  height?: number | null
  aspect_ratio?: string | null
  content_type?: string | null
  has_poster?: boolean
}

export interface PublishItemDetail extends PublishItemSummary {
  packages: PlatformPublishPackageOut[]
  source_video: PublishSourceVideoSummary
  selected_agent?: {
    id: string
    display_name: string | null
    machine_name: string | null
    status: string | null
    status_label: string
    available: boolean
    selectable: boolean
    unavailable_reason: string | null
    capabilities: string[]
  } | null
  jobs?: Array<{
    id: string
    publish_package_id: string
    status: string
    status_label: string
    remote_post_id: string | null
    remote_post_url: string | null
    assigned_agent_id: string | null
    claimed: boolean
    requires_user_action: boolean
  }>
}

export interface PublishConfirmResult extends PublishItemDetail {
  jobs_created: Array<{
    id: string
    publish_package_id: string
    publish_mode: PublishMode
    scheduled_at: string | null
    status: string
  }>
}

export interface PublishConfirmCheckResult {
  item_id: string
  checks: Record<string, boolean>
  can_confirm: boolean
  packages: PlatformPublishPackageOut[]
}

export type PlatformAccountConnectionStatus =
  | 'pending'
  | 'waiting_for_login'
  | 'connected'
  | 'needs_reauth'
  | 'disconnected'
  | 'not_connected'
  | 'expired'

export interface PlatformAccountOnboardingTaskOut {
  id: string
  status: 'assigned' | 'claimed' | 'succeeded' | 'failed' | 'expired'
  assigned_agent_id: string | null
  error_code: string | null
  error_message: string | null
  created_at: string | null
  claimed_at: string | null
  completed_at: string | null
}

export interface PlatformAccountOut {
  id: string
  platform: string
  platform_label: string
  external_account_id: string | null
  display_name: string
  platform_nickname: string | null
  avatar_url: string | null
  connection_type: string
  connection_status: PlatformAccountConnectionStatus
  connection_status_label: string
  is_connected: boolean
  capabilities: Record<string, unknown>
  publisher_device_id: string | null
  publisher_device_display_name: string | null
  created_at: string | null
  last_verified_at: string | null
  onboarding_task: PlatformAccountOnboardingTaskOut | null
}

export interface PlatformCapabilityOut {
  platform_id: string
  display_name: string
  icon: string
  enabled: boolean
  connection_supported: boolean
  publish_supported: boolean
  content_types: string[]
  scheduling_supported: boolean
  publisher_mode?: string
  requires_local_agent?: boolean
  requires_human_confirmation?: boolean
  supports_unattended_schedule?: boolean
  publisher_capability?: string | null
  fields: Record<string, { label: string; required: boolean }>
}

const publishingBase = (workspaceId: string) => `/workspaces/${workspaceId}/content-publishing`

export const listPublishItems = (
  workspaceId: string,
  params?: { tab?: PublishTabKey | 'all'; q?: string; page?: number; page_size?: number },
) => saasApi.get<PublishItemsListResult>(publishingBase(workspaceId), { params })

export const createPublishItem = (workspaceId: string, sourceAssetId: string) =>
  saasApi.post<PublishItemSummary>(publishingBase(workspaceId), { source_asset_id: sourceAssetId })

export const getPublishItemDetail = (workspaceId: string, itemId: string) =>
  saasApi.get<PublishItemDetail>(`${publishingBase(workspaceId)}/${itemId}`)

export interface UpdatePublishItemPayload {
  title?: string
  publish_mode?: PublishMode
  scheduled_at?: string | null
  clear_scheduled_at?: boolean
  selected_agent_id?: string | null
}

export const updatePublishItem = (workspaceId: string, itemId: string, payload: UpdatePublishItemPayload) =>
  saasApi.patch<PublishItemDetail>(`${publishingBase(workspaceId)}/${itemId}`, payload)

export const addPublishPlatform = (
  workspaceId: string,
  itemId: string,
  platform: string,
  platformAccountId?: string | null,
) =>
  saasApi.post<PublishItemDetail>(`${publishingBase(workspaceId)}/${itemId}/packages`, {
    platform,
    platform_account_id: platformAccountId ?? null,
  })

export interface UpdatePublishPackagePayload {
  title?: string
  caption?: string
  hashtags?: string[]
  cover_asset_id?: string | null
  clear_cover_asset_id?: boolean
  platform_account_id?: string | null
  clear_platform_account_id?: boolean
}

export const updatePublishPackage = (
  workspaceId: string,
  itemId: string,
  packageId: string,
  payload: UpdatePublishPackagePayload,
) => saasApi.patch<PublishItemDetail>(`${publishingBase(workspaceId)}/${itemId}/packages/${packageId}`, payload)

export const removePublishPlatform = (workspaceId: string, itemId: string, packageId: string) =>
  saasApi.delete<PublishItemDetail>(`${publishingBase(workspaceId)}/${itemId}/packages/${packageId}`)

export const checkPublishConfirmable = (workspaceId: string, itemId: string) =>
  saasApi.get<PublishConfirmCheckResult>(`${publishingBase(workspaceId)}/${itemId}/confirm-check`)

export const confirmPublish = (workspaceId: string, itemId: string) =>
  saasApi.post<PublishConfirmResult>(`${publishingBase(workspaceId)}/${itemId}/confirm`)

export const duplicatePublishItemAsDraft = (workspaceId: string, itemId: string) =>
  saasApi.post<PublishItemDetail>(`${publishingBase(workspaceId)}/${itemId}/duplicate-as-draft`)

export const listPlatformAccounts = (workspaceId: string, platform?: string) =>
  saasApi.get<{ items: PlatformAccountOut[] }>(`${publishingBase(workspaceId)}/platform-accounts`, {
    params: platform ? { platform } : undefined,
  })

export const createPlatformAccount = (workspaceId: string, platform: string, displayName: string) =>
  saasApi.post<PlatformAccountOut>(`${publishingBase(workspaceId)}/platform-accounts`, {
    platform,
    display_name: displayName,
  })

// ------------------------------------------------- PlatformAccount onboarding
// VISTOERA_PLATFORM_ACCOUNT_ONBOARDING_V1 — machine-agnostic: a "Publisher
// Device" here is always whichever LocalPublisherAgent the tenant picks,
// never a hardcoded device name.

export const listOnboardingDevices = (workspaceId: string, platform: string) =>
  saasApi.get<{ items: LocalPublisherAgentSelectionOut[] }>(`${publishingBase(workspaceId)}/platform-accounts/onboarding-devices`, {
    params: { platform },
  })

export const startPlatformAccountOnboarding = (workspaceId: string, platform: string, publisherDeviceId: string) =>
  saasApi.post<PlatformAccountOut>(`${publishingBase(workspaceId)}/platform-accounts/onboarding`, {
    platform,
    publisher_device_id: publisherDeviceId,
  })

export const getPlatformAccount = (workspaceId: string, accountId: string) =>
  saasApi.get<PlatformAccountOut>(`${publishingBase(workspaceId)}/platform-accounts/${accountId}`)

export const retryPlatformAccountOnboarding = (workspaceId: string, accountId: string, publisherDeviceId: string) =>
  saasApi.post<PlatformAccountOut>(`${publishingBase(workspaceId)}/platform-accounts/${accountId}/onboarding-retry`, {
    publisher_device_id: publisherDeviceId,
  })

export const listPlatformCapabilities = (workspaceId: string) =>
  saasApi.get<{ items: PlatformCapabilityOut[] }>(`${publishingBase(workspaceId)}/platform-capabilities`)

// ------------------------------------------------------- Local Publisher Agent
// VISTOERA_INTERACTIVE_LOCAL_PUBLISHER_CONTROL_PLANE_CONTRACT_V1

export type LocalPublisherAgentStatus =
  | 'online'
  | 'offline'
  | 'busy'
  | 'auth_required'
  | 'update_required'
  | 'revoked'

export interface LocalPublisherAgentOut {
  id: string
  workspace_id: string
  display_name: string
  machine_name: string | null
  os_name: string | null
  agent_version: string | null
  status: LocalPublisherAgentStatus
  capabilities: string[]
  platform_session: Record<string, string>
  last_seen_at: string | null
  created_at: string | null
}

export interface LocalPublisherAgentSelectionOut extends LocalPublisherAgentOut {
  selectable: boolean
  disabled_reason: string | null
  status_label: string
}

export interface PublishJobProgressOut {
  job_id: string
  status: string
  status_label: string
  remote_post_id: string | null
  remote_post_url: string | null
  assigned_agent_id: string | null
  requires_user_action: boolean
  events: Array<{
    sequence: number
    state: string
    progress: number | null
    message_code: string
    message_label: string
    requires_user_action: boolean
    action_type: string | null
    created_at: string | null
  }>
}

const localPublisherBase = (workspaceId: string) => `/workspaces/${workspaceId}/local-publishers`

export const listLocalPublisherAgents = (workspaceId: string) =>
  saasApi.get<{ items: LocalPublisherAgentOut[] }>(localPublisherBase(workspaceId))

export const listLocalPublisherAgentsForSelection = (workspaceId: string, capabilities: string[]) =>
  saasApi.get<{ items: LocalPublisherAgentSelectionOut[] }>(localPublisherBase(workspaceId), {
    params: {
      for_publish_selection: true,
      capability: capabilities.length > 0 ? capabilities : undefined,
    },
  })

export interface LocalPublisherEnrollmentOut {
  enrollment_token_id: string
  enrollment_token: string
  pairing_code: string
  expires_at: string
  label: string | null
}

// VISTOERA_LOCAL_PUBLISHER_ENROLLMENT_GENERATION_PATH_AND_ADMIN_UI_P0 —
// canonical (and only) way to mint a Local Publisher credential seed.
// Deliberately a *different* namespace/endpoint from the Platform Admin
// `platformCreateEnrollmentToken` (Worker Enrollment Tokens) above: that
// call mints a global, cross-tenant `vwenroll_` row in
// `worker_enrollment_tokens` for `POST /api/v1/worker/enroll`, while this
// one mints a workspace-scoped `vpaenroll_`/pairing-code row in
// `saas_publisher_agent_enrollment_tokens` for
// `POST /api/v1/local-publisher/enroll` — the two token tables/hash
// namespaces never overlap, so a Worker Enrollment Token pasted into a
// Local Publisher Agent will always fail with "配对码无效或已过期".
export const createLocalPublisherEnrollment = (workspaceId: string, label?: string, ttlSeconds?: number) =>
  saasApi.post<LocalPublisherEnrollmentOut>(`${localPublisherBase(workspaceId)}/enrollment-tokens`, {
    label,
    ttl_seconds: ttlSeconds,
  })

export const revokeLocalPublisherAgent = (workspaceId: string, agentId: string) =>
  saasApi.delete<{ id: string; revoked: boolean }>(`${localPublisherBase(workspaceId)}/${encodeURIComponent(agentId)}`)

// ---------------------------------------------------------------------
// VistoeraTASK-00082 — Local Publisher Agent release/distribution
// metadata (`app/api/routes/local_publisher_releases.py`). Deliberately
// NOT under `saasApi` (`/api/saas/...`, tenant-scoped + credentialed):
// this is a public, tenant-agnostic "what's the current downloadable
// build" endpoint at `/api/local-publisher/releases/...`, so it gets its
// own small unauthenticated axios instance with the *same* origin
// resolution `../api/client.ts`'s `STANDALONE_BASE_URL` already uses
// (derive from `VITE_API_BASE_URL`'s origin, `/api` suffix, `/api`
// relative fallback) rather than `SAAS_API_BASE_URL`'s `/api/saas` suffix.
function withApiSuffix(raw: string): string {
  const trimmed = raw.replace(/\/+$/, '')
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`
}
const API_BASE_URL = RAW_API_BASE_URL ? withApiSuffix(RAW_API_BASE_URL) : '/api'

const localPublisherReleasesApi = axios.create({ baseURL: API_BASE_URL, timeout: 30000 })

// VistoeraTASK-00105 — belt-and-suspenders: this instance must attach the
// same Preview Access header as ``saasApi`` (see its interceptor above).
// Even with the backend list-route gate exemption, keeping the header here
// matches every other axios client and avoids a second class of silent 401
// if the exemption regresses.
localPublisherReleasesApi.interceptors.request.use((config) => {
  const key = getStoredPreviewAccessKey()
  if (key) {
    config.headers = config.headers ?? {}
    ;(config.headers as Record<string, string>)[PREVIEW_ACCESS_HEADER] = key
  }
  return config
})

export type LocalPublisherReleaseChannel = 'preview' | 'production'

export interface LocalPublisherReleaseOut {
  platform: 'macos' | 'windows'
  channel: LocalPublisherReleaseChannel
  arch?: string
  ready: boolean
  release_id?: string
  version?: string
  filename?: string
  size_bytes?: number
  sha256?: string
  content_type?: string
  release_notes?: string | null
  created_at?: string | null
  download_url?: string
}

export interface LocalPublisherReleasesLatestOut {
  channel: LocalPublisherReleaseChannel
  platforms: {
    macos: LocalPublisherReleaseOut
    windows: LocalPublisherReleaseOut
  }
}

/** P26 — canonical read path for the download area; never a hardcoded
 * URL in the component. `channel` defaults server-side to `"preview"`
 * (see `local_publisher_release_service.py`) — pass it explicitly only if
 * a future Production build of this same page needs to resolve
 * Production-channel releases instead (P27 environment separation). */
export const getLocalPublisherReleases = (channel?: LocalPublisherReleaseChannel) =>
  localPublisherReleasesApi.get<LocalPublisherReleasesLatestOut>('/local-publisher/releases', {
    params: channel ? { channel } : undefined,
  })

export const getPublishJobProgress = (workspaceId: string, jobId: string) =>
  saasApi.get<PublishJobProgressOut>(`${localPublisherBase(workspaceId)}/publish-jobs/${jobId}/progress`)

export const focusPublishWindow = (workspaceId: string, jobId: string) =>
  saasApi.post(`${localPublisherBase(workspaceId)}/publish-jobs/${jobId}/focus-window`)

export const cancelPublishSession = (workspaceId: string, jobId: string) =>
  saasApi.post(`${localPublisherBase(workspaceId)}/publish-jobs/${jobId}/cancel`)

// ---------------------------------------------------------------------
// VISTOERA_VIDEO_ASSETS_V1 — "视频资产" (spec §一/§九/§十四/§十六). A thin
// client for `app/api/routes/saas_video_assets.py` — a read-mostly
// aggregation over the existing 视频生成/连续生成 Job+Composite output rows
// plus uploaded reference videos, never a second parallel video store.
// ---------------------------------------------------------------------

export type VideoAssetSourceKind = 'ai_generated' | 'continuous_generated' | 'upload'

export type VideoAssetOriginKind =
  | 'user_upload'
  | 'canvas'
  | 'video_project'
  | 'continuous_generation'
  | 'tenant_surface'
  | 'legacy_unknown'

export interface VideoAssetItem {
  id: string // "job:<id>" | "composite:<id>" | "upload:<id>"
  source_kind: VideoAssetSourceKind
  title: string
  description: string | null
  duration_seconds: number | null
  width: number | null
  height: number | null
  size_bytes: number | null
  created_at: string | null
  is_continuation_segment: boolean
  is_continuation_root: boolean
  is_composite: boolean
  is_published: boolean
  job_id: string | null
  composite_id: string | null
  upload_asset_id: string | null
  filename: string | null
  can_delete_hint: boolean
  can_edit_meta: boolean
  can_publish: boolean
  root_video_id?: string
  origin_kind?: VideoAssetOriginKind
  origin_display_name?: string
  origin_secondary_label?: string | null
  origin_project_id?: string | null
  origin_canvas_id?: string | null
  origin_node_id?: string | null
  origin_generation_job_id?: string | null
  origin_video_project_id?: string | null
  origin_project_exists?: boolean
  origin_canvas_exists?: boolean
  origin_node_exists?: boolean | null
  origin_navigate_route?: string | null
  origin_navigate_enabled?: boolean
  origin_deleted_hint?: string | null
  origin_is_legacy?: boolean
  created_by_user_id?: string | null
  visibility?: 'private' | 'workspace' | string
  /** Present only in team Workspaces (batched from list response). */
  creator?: VideoAssetCreator | null
}

export interface VideoAssetCreator {
  user_id: string
  display_name: string
  is_me: boolean
}

export interface VideoAssetTeamMember {
  user_id: string
  display_name: string
  role: string
}

export interface CanvasNodeVideoAssetItem {
  id: string
  job_id: string
  title: string
}

export type VideoLibraryView = 'mine' | 'shared' | 'all'

export interface VideoAssetTeamBlock {
  enabled: boolean
  active_member_count: number
  view?: VideoLibraryView
  user_id?: string
  role?: string
  /** @deprecated VistoeraTASK-00580 — use creator_options. */
  members?: VideoAssetTeamMember[]
  creator_options?: Array<{ user_id: string; display_name: string; is_me?: boolean }>
}

export interface VideoAssetListResult {
  items: VideoAssetItem[]
  total: number
  page: number
  page_size: number
  team?: VideoAssetTeamBlock
}

export interface VideoAssetStats {
  total: number
  original: number
  ai_generated: number
  continuous_generated: number
  upload: number
  published: number
  total_duration_minutes: number
  team?: VideoAssetTeamBlock
}

export type VideoAssetSourceFilter = 'ai_generated' | 'continuous_generated' | 'upload' | 'published'
export type VideoAssetOriginFilter = 'upload' | 'vistoera_generated'
export type VideoAssetDurationFilter = 'short' | 'medium' | 'long'
export type VideoAssetResolutionFilter = '4k' | '1080p' | '720p' | 'other'

const videoAssetsBase = (workspaceId: string) => `/workspaces/${workspaceId}/video-assets`

export const listVideoAssets = (
  workspaceId: string,
  params?: {
    search?: string
    source?: VideoAssetSourceFilter
    origin?: VideoAssetOriginFilter
    duration?: VideoAssetDurationFilter
    resolution?: VideoAssetResolutionFilter
    sort?: 'recent' | 'oldest'
    page?: number
    page_size?: number
    offset?: number
    view?: VideoLibraryView
    /** Team Workspace only: member user_id or ``me``. */
    creator_id?: string
  },
) => saasApi.get<VideoAssetListResult>(videoAssetsBase(workspaceId), { params })

export const getVideoAssetStats = (workspaceId: string, params?: { view?: VideoLibraryView }) =>
  saasApi.get<VideoAssetStats>(`${videoAssetsBase(workspaceId)}/stats`, { params })

export const listVideoAssetsByCanvasNode = (workspaceId: string, nodeId: string) =>
  saasApi.get<{ items: CanvasNodeVideoAssetItem[]; count: number }>(
    `${videoAssetsBase(workspaceId)}/by-canvas-node/${encodeURIComponent(nodeId)}`,
  )

export const uploadVideoAsset = (
  workspaceId: string,
  file: File,
  title?: string,
  onProgress?: (percent: number) => void,
) => {
  const form = new FormData()
  form.append('file', file)
  return saasApi.post<{ id: string; asset_id: string }>(`${videoAssetsBase(workspaceId)}/upload`, form, {
    params: title ? { title } : undefined,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
      ? (evt) => {
          if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100))
        }
      : undefined,
    // VistoeraTASK-00220/00221 — root-cause fix for "slow-but-legal upload
    // misjudged as failure". BEFORE: no override at all here -> silently
    // inherited `saasApi`'s global 30_000ms default (see this file's own
    // `axios.create({ timeout: 30000 })`), even though the REAL backend
    // limit for this exact upload (`video_gen_video_max_size_mb`,
    // `video_generation_assets._max_bytes_for`) is 300MB, enforced via
    // true streaming (never a "read fully then reject" shortcut) — i.e.
    // the backend was already willing to accept a slow multi-minute
    // transfer that the client was killing after 30s.
    // AFTER: 2_400_000ms (40 min). Derivation (not a "just make it big"
    // guess): `uploadSiteAsset` above already establishes a floor of
    // ~113KB/s (~0.9Mbps) minimum-sustained-throughput assumption for
    // THIS SAME axios instance/pipeline (its 180_000ms override divided
    // by this codebase's own ~20MB soft image-size ceiling, see
    // `canvasMediaImportLimits.ts`). Applying that identical floor to
    // VIDEO's real 300MB backend ceiling gives 300MB / 113KB/s ~= 2655s;
    // 2_400_000ms (40min/125KB/s) is the nearest clean round number at
    // essentially the same floor. Every other caller of this shared
    // function (VideoAssetPicker, VideoUploadModal, Canvas OS File Drop)
    // inherits this same real fix, not just the new Canvas path.
    timeout: 2_400_000,
  })
}

export const updateVideoAsset = (workspaceId: string, id: string, payload: { title?: string; description?: string }) =>
  saasApi.patch<{ id: string; title: string; description: string | null }>(
    `${videoAssetsBase(workspaceId)}/${encodeURIComponent(id)}`,
    payload,
  )

export const deleteVideoAsset = (workspaceId: string, id: string) =>
  saasApi.delete<{ id: string; deleted: boolean }>(`${videoAssetsBase(workspaceId)}/${encodeURIComponent(id)}`)

export interface VideoAssetBatchDeleteResult {
  deleted: string[]
  failed: Array<{ id: string; code: string; message: string }>
  deleted_count: number
  failed_count: number
}

export const batchDeleteVideoAssets = (workspaceId: string, ids: string[]) =>
  saasApi.post<VideoAssetBatchDeleteResult>(`${videoAssetsBase(workspaceId)}/batch-delete`, { ids })

/** Playback/download for an `upload:`-sourced item only — job-/
 * composite-sourced items reuse `videoGenTaskOutputUrl`/
 * `videoGenTaskPosterUrl`/`videoGenComposedOutputUrl`/
 * `videoGenCompositePosterUrl` directly (see this section's docstring). */
export const videoAssetUploadContentUrl = (workspaceId: string, uploadAssetId: string) =>
  `${SAAS_API_BASE_URL}${videoAssetsBase(workspaceId)}/${uploadAssetId}/content`

export const videoAssetUploadDownloadUrl = (workspaceId: string, uploadAssetId: string) =>
  `${SAAS_API_BASE_URL}${videoAssetsBase(workspaceId)}/${uploadAssetId}/content?download=true`

/** Resolve the correct existing playback/poster/download URL for a
 * `VideoAssetItem` by dispatching on which id it carries — mirrors the
 * backend's own `job:` / `composite:` / `upload:` dispatch, never a
 * fourth byte-delivery path (spec §一/§十). */
export function videoAssetPlaybackUrl(workspaceId: string, item: VideoAssetItem, download = false): string {
  if (item.job_id) return `${videoGenTaskOutputUrl(workspaceId, item.job_id)}${download ? '?download=true' : ''}`
  if (item.composite_id && item.root_video_id)
    return `${videoGenComposedOutputUrl(workspaceId, item.root_video_id)}${download ? '?download=true' : ''}`
  if (item.upload_asset_id)
    return download
      ? videoAssetUploadDownloadUrl(workspaceId, item.upload_asset_id)
      : videoAssetUploadContentUrl(workspaceId, item.upload_asset_id)
  return ''
}

export function videoAssetPosterUrl(workspaceId: string, item: VideoAssetItem): string | undefined {
  if (item.job_id) return videoGenTaskPosterUrl(workspaceId, item.job_id)
  if (item.composite_id) return videoGenCompositePosterUrl(workspaceId, item.composite_id)
  if (item.upload_asset_id) return videoGenAssetPosterUrl(workspaceId, item.upload_asset_id)
  return undefined
}

// ---------------------------------------------------------------------
// VISTOERA_TENANT_TEXT_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1 —
// "文本资产". A thin client for `app/api/routes/saas_text_assets.py` — a
// unified READ MODEL over already-canonical sources (CanvasNode
// text_asset/generate_creative/script_generation/storyboard_generation,
// and TranscriptSegment). Never a second Source of Truth: `id` here is
// always `canonical_source_type`/`canonical_source_id` under the hood
// (see `canonical_source_type`/`canonical_source_id` fields), so a row
// can always be traced back to its real backing object.
// ---------------------------------------------------------------------

export type TextAssetType = 'GENERATED_TEXT' | 'TEXT' | 'CREATIVE' | 'SCRIPT' | 'STORYBOARD' | 'COPY' | 'TRANSCRIPT'

export type TextLibraryView = 'mine' | 'shared' | 'all'

export interface TextAssetCreator {
  user_id: string
  display_name: string
  is_me?: boolean
}

export interface TextAssetTeamMember {
  user_id: string
  display_name: string
  role: string
}

export interface TextAssetTeamBlock {
  enabled: boolean
  active_member_count: number
  view?: TextLibraryView
  user_id?: string
  role?: string
  /** @deprecated VistoeraTASK-00580 — use creator_options. */
  members?: TextAssetTeamMember[]
  creator_options?: Array<{ user_id: string; display_name: string; is_me?: boolean }>
}

export interface TextAssetItem {
  id: string // presentation id, e.g. "canvas_node:<id>" | "transcript:<reference_content_id>"
  canonical_source_type: string
  canonical_source_id: string
  type: TextAssetType
  type_label: string
  role: string | null
  source: string
  source_label: string
  title: string
  preview_text: string
  word_count: number
  tags: string[]
  created_at: string | null
  updated_at: string | null
  usage_count: number | null
  project_id: string | null
  project_title: string | null
  created_by_user_id?: string | null
  visibility?: 'workspace' | 'private'
  creator?: TextAssetCreator | null
}

export interface TextAssetTypeCount {
  value: TextAssetType
  label: string
  count: number
}

export interface TextAssetSourceCount {
  value: string
  label: string
  count: number
}

export interface TextAssetListResult {
  items: TextAssetItem[]
  total: number
  page: number
  page_size: number
  summary: Record<string, number>
  available_sources: TextAssetSourceCount[]
  types: TextAssetTypeCount[]
  team?: TextAssetTeamBlock
}

export interface TextAssetDetail extends TextAssetItem {
  detail: Record<string, unknown>
  canvas_available: boolean
  canvas_project_id?: string | null
}

export type TextAssetSort = 'updated_desc' | 'created_desc' | 'name_asc' | 'word_count_desc' | 'usage_desc'

const textAssetsBase = (workspaceId: string) => `/workspaces/${workspaceId}/text-assets`

export const listTextAssets = (
  workspaceId: string,
  params?: {
    q?: string
    type?: TextAssetType | 'all'
    source?: string
    tag?: string
    sort?: TextAssetSort
    page?: number
    page_size?: number
    view?: TextLibraryView
    creator_id?: string
  },
) => saasApi.get<TextAssetListResult>(textAssetsBase(workspaceId), { params })

export const getTextAsset = (workspaceId: string, presentationId: string) =>
  saasApi.get<TextAssetDetail>(`${textAssetsBase(workspaceId)}/${encodeURIComponent(presentationId)}`)

export const createTextAsset = (workspaceId: string, payload: { title?: string; body: string }) =>
  saasApi.post<TextAssetDetail>(textAssetsBase(workspaceId), payload)

export const updateTextAsset = (
  workspaceId: string,
  presentationId: string,
  payload: { title?: string; body?: string },
) => saasApi.patch<TextAssetDetail>(`${textAssetsBase(workspaceId)}/${encodeURIComponent(presentationId)}`, payload)

/** VistoeraTASK-00689 — share / unshare. Changes ONLY visibility. */
export const updateTextAssetVisibility = (
  workspaceId: string,
  presentationId: string,
  visibility: 'workspace' | 'private',
) =>
  saasApi.patch<TextAssetItem>(
    `${textAssetsBase(workspaceId)}/${encodeURIComponent(presentationId)}/visibility`,
    { visibility },
  )

export const deleteTextAsset = (workspaceId: string, presentationId: string) =>
  saasApi.delete<{ deleted: boolean; id: string }>(`${textAssetsBase(workspaceId)}/${encodeURIComponent(presentationId)}`)

// ---------------------------------------------------------------------
// VISTOERA_TENANT_AUDIO_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1 —
// "音频资产". A thin client for
// `app/api/routes/saas_audio_assets.py` — a read-model aggregation over
// `CanvasVoiceTemplate` + `SiteAsset(media_type="audio")` +
// `CanvasAudioGeneration` provenance (see that route/service's own
// docstrings). Never a second parallel audio store — this file adds no
// list-merging/sorting/pagination logic of its own; the backend already
// returns one final, already-paginated `items` array + `summary` counts.
// ---------------------------------------------------------------------

export type AudioPresentationType =
  | 'VOICE_TEMPLATE'
  | 'SPEECH'
  | 'MUSIC'
  | 'SFX'
  | 'AMBIENCE'
  | 'RECORDING'
  | 'REFERENCE_AUDIO'
  | 'MIXDOWN'

export type AudioAssetSource =
  | 'UPLOAD'
  | 'AI_GENERATED'
  | 'RECORDED'
  | 'PLATFORM_LIBRARY'
  | 'CANVAS'
  | 'VIDEO_PROJECT'
  | 'IMPORTED'
  | 'UNKNOWN'

export type AudioAssetOwnership = 'WORKSPACE_OWNED' | 'PLATFORM_REFERENCE'

/** Presentation types a plain "上传音频" request may create — mirrors
 * `UPLOADABLE_PRESENTATION_TYPES` in `audio_asset_library_service.py`.
 * `VOICE_TEMPLATE` is a hard P0 exclusion (task spec §46/§47) — never
 * offered in the upload type picker. */
export const AUDIO_UPLOADABLE_PRESENTATION_TYPES: readonly AudioPresentationType[] = [
  'MUSIC',
  'SFX',
  'AMBIENCE',
  'RECORDING',
  'REFERENCE_AUDIO',
]

export const AUDIO_ALL_PRESENTATION_TYPES: readonly AudioPresentationType[] = [
  'VOICE_TEMPLATE',
  'SPEECH',
  'MUSIC',
  'SFX',
  'AMBIENCE',
  'RECORDING',
  'REFERENCE_AUDIO',
  'MIXDOWN',
]

export type AudioAssetLibraryView = 'mine' | 'shared' | 'all'

export interface AudioAssetTeamBlock {
  enabled: boolean
  active_member_count: number
  view?: AudioAssetLibraryView
  user_id?: string
  role?: string
  /** @deprecated VistoeraTASK-00580 — use creator_options. */
  members?: Array<{ user_id: string; display_name: string; role: string }>
  creator_options?: Array<{ user_id: string; display_name: string; is_me?: boolean }>
}

export interface AudioAssetItem {
  id: string // "voice_template:<id>" | "asset:<id>" — canonical source dispatch, never a fabricated UUID.
  presentation_type: AudioPresentationType
  name: string
  description: string | null
  source: AudioAssetSource
  ownership: AudioAssetOwnership
  status: string | null
  usable: boolean
  duration_ms: number | null
  has_preview_audio: boolean
  playback_url: string | null
  provider: string | null
  created_at: string | null
  updated_at: string | null
  size_bytes: number | null
  filename: string | null
  can_delete: boolean
  usage_count: number | null
  tags: string[]
  // VOICE_TEMPLATE-only extras (see `_collect_voice_template_items`).
  project_id?: string
  canvas_node_id?: string
  voice_brief?: string | null
  preview_duration_ms?: number | null
  reference_audio_count?: number
  execution_availability?: string
  execution_availability_reason?: string | null
  // VistoeraTASK-00217 — Voice Template Parameter Memory, embedded so a
  // template switch never needs a separate round trip to resolve the
  // newly-selected template's remembered Pitch/Speed.
  generation_preference?: VoiceTemplateGenerationPreference | null
  // VistoeraTASK-00555 — team Workspace list enrichment (absent in personal Workspaces).
  created_by_user_id?: string | null
  visibility?: string | null
  creator?: { display_name: string; is_me?: boolean } | null
  can_manage?: boolean
  collection_id?: string | null
}

/** VistoeraTASK-00217 — remembered Pitch/Speed for ONE Voice Template. */
export interface VoiceTemplateGenerationPreference {
  pitch: number | null
  speed: number | null
  updated_at?: string | null
}

export interface AudioAssetSummary {
  all: number
  voice_template: number
  speech: number
  music: number
  sfx: number
  ambience: number
  recording: number
  reference_audio: number
  mixdown: number
}

export interface AudioAssetListResult {
  team?: AudioAssetTeamBlock
  items: AudioAssetItem[]
  total: number
  page: number
  page_size: number
  summary: AudioAssetSummary
}

export type AudioAssetSort = 'recent' | 'oldest' | 'name' | 'duration'

const audioAssetsBase = (workspaceId: string) => `/workspaces/${workspaceId}/audio-assets`

/** VistoeraTASK-00557 — team library scope for Canvas Audio Picker (ignored when personal). */
export type AudioPickerTeamParams = ImagePickerTeamParams

export const listAudioAssets = (
  workspaceId: string,
  params?: {
    q?: string
    type?: AudioPresentationType | 'all'
    source?: AudioAssetSource | 'all'
    ownership?: AudioAssetOwnership | 'all'
    tag?: string
    sort?: AudioAssetSort
    page?: number
    page_size?: number
    view?: AudioAssetLibraryView
  } & AudioPickerTeamParams,
) => saasApi.get<AudioAssetListResult>(audioAssetsBase(workspaceId), { params })

export const listAudioFolders = (workspaceId: string, signal?: AbortSignal) =>
  saasApi.get<ImageFoldersResponse>(`${audioAssetsBase(workspaceId)}/collections`, {
    params: { view: 'all' },
    signal,
  })

export const createAudioFolder = (workspaceId: string, name: string, parentId: string | null) =>
  saasApi.post<ImageFolder>(`${audioAssetsBase(workspaceId)}/collections`, {
    name,
    parent_id: parentId,
  })

export const renameAudioFolder = (workspaceId: string, folderId: string, name: string) =>
  saasApi.patch<ImageFolder>(`${audioAssetsBase(workspaceId)}/collections/${folderId}`, { name })

export const deleteAudioFolder = (workspaceId: string, folderId: string) =>
  saasApi.delete<{ deleted: boolean }>(`${audioAssetsBase(workspaceId)}/collections/${folderId}`)

export const getAudioAssetSummary = (workspaceId: string) =>
  saasApi.get<AudioAssetSummary>(`${audioAssetsBase(workspaceId)}/summary`)

export const uploadAudioAsset = (
  workspaceId: string,
  file: File,
  presentationType: Exclude<AudioPresentationType, 'VOICE_TEMPLATE' | 'SPEECH' | 'MIXDOWN'>,
  title?: string,
  onProgress?: (percent: number) => void,
  options?: { collection_id?: string | null },
) => {
  const form = new FormData()
  form.append('file', file)
  return saasApi.post<{ id: string; asset_id: string }>(`${audioAssetsBase(workspaceId)}/upload`, form, {
    params: {
      presentation_type: presentationType,
      ...(title ? { title } : {}),
      ...(options?.collection_id ? { collection_id: options.collection_id } : {}),
    },
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
      ? (evt) => {
          if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100))
        }
      : undefined,
    // VistoeraTASK-00220/00221 — same root-cause fix as `uploadVideoAsset`
    // above, same derivation. BEFORE: no override -> silent global 30s
    // default. Real backend limit for THIS exact upload is 150MB
    // (`audio_asset_library_service._MAX_UPLOAD_BYTES`). AFTER:
    // 1_200_000ms (20 min) = 150MB at the same ~125KB/s floor used for
    // video above (half the size, half the time, same throughput floor).
    // Every existing caller (AudioAssetPicker, AudioUploadModal, Canvas
    // OS File Drop) inherits this same real fix.
    timeout: 1_200_000,
  })
}

export const deleteAudioAsset = (workspaceId: string, id: string) =>
  saasApi.delete<{ id: string; deleted: boolean }>(`${audioAssetsBase(workspaceId)}/${encodeURIComponent(id)}`)

export interface AudioAssetBatchDeleteResult {
  deleted: string[]
  failed: Array<{ id: string; code: string; message: string }>
  deleted_count: number
  failed_count: number
}

export const batchDeleteAudioAssets = (workspaceId: string, ids: string[]) =>
  saasApi.post<AudioAssetBatchDeleteResult>(`${audioAssetsBase(workspaceId)}/batch-delete`, { ids })

/** Rename — plain-media items only (MUSIC/SFX/AMBIENCE/RECORDING/
 * REFERENCE_AUDIO/MIXDOWN); the backend rejects VOICE_TEMPLATE/SPEECH
 * (see `audio_asset_library_service.rename_audio_asset` docstring for
 * why those two are excluded on purpose, not merely unwired). */
export const renameAudioAsset = (workspaceId: string, id: string, name: string) =>
  saasApi.patch<{ id: string; name: string }>(`${audioAssetsBase(workspaceId)}/${encodeURIComponent(id)}`, { name })

export interface AudioEditorContext {
  asset_id: string
  name: string
  format_label: string
  content_type: string
  duration_ms: number
  sample_rate_hz: number | null
  filename: string | null
}

export interface AudioWaveformPeaksResult {
  asset_id: string
  buckets: number
  peaks: number[]
  cached: boolean
}

export const fetchAudioEditorContext = (workspaceId: string, assetId: string) =>
  saasApi.get<AudioEditorContext>(`${audioAssetsBase(workspaceId)}/by-asset/${encodeURIComponent(assetId)}/editor-context`)

export const fetchAudioWaveformPeaks = (workspaceId: string, assetId: string) =>
  saasApi.get<AudioWaveformPeaksResult>(`${audioAssetsBase(workspaceId)}/by-asset/${encodeURIComponent(assetId)}/waveform-peaks`)

/** VistoeraTASK-00171 §八 — accept-phase response: `status: 'processing'`
 * always, since the real ffmpeg work is scheduled server-side via
 * `BackgroundTasks` and hasn't run yet when this resolves. `asset_id` is
 * the FINAL identity — never re-created on success (§十). */
export interface AudioTrimAcceptResult {
  id: string
  asset_id: string
  status: 'processing'
  name: string
  duration_ms: number | null
  presentation_type?: string | null
}

export const trimAudioAsset = (
  workspaceId: string,
  assetId: string,
  body: { trim_start_ms: number; trim_end_ms: number },
) =>
  saasApi.post<AudioTrimAcceptResult>(`${audioAssetsBase(workspaceId)}/by-asset/${encodeURIComponent(assetId)}/trim`, body)

export interface AudioTrimStatusResult {
  asset_id: string
  status: 'processing' | 'ready' | 'failed'
  name: string
  duration_ms: number | null
  presentation_type?: string | null
  error_message?: string | null
}

/** Polled by Canvas while a sibling node is pending, and on hydrate to
 * reconcile a still-pending node against real backend state (§十 refresh
 * recovery). */
export const fetchAudioTrimStatus = (workspaceId: string, assetId: string) =>
  saasApi.get<AudioTrimStatusResult>(`${audioAssetsBase(workspaceId)}/by-asset/${encodeURIComponent(assetId)}/trim-status`)

export const retryAudioTrim = (workspaceId: string, assetId: string) =>
  saasApi.post<{ asset_id: string; status: 'processing' }>(
    `${audioAssetsBase(workspaceId)}/by-asset/${encodeURIComponent(assetId)}/retry-trim`,
    {},
  )

// ==================================================================
// Competitive Content Analysis — VISTOERA_COMPETITIVE_CONTENT_ANALYSIS_V1
// ==================================================================
// Mirrors backend/app/saas/models/competitive_content.py +
// backend/app/api/routes/saas_competitive_content.py exactly — see those
// modules' own docstrings for the Reference/Capture/Analysis layering
// this contract implements. Never a video URL of our own: "查看原内容"
// always opens `source_url` on the original platform.

export type ReferenceContentPlatform =
  | 'douyin'
  | 'xiaohongshu'
  | 'weixin_channels'
  | 'bilibili'
  | 'youtube'
  | 'kuaishou'
  | 'unknown'

export type ReferenceContentType = 'VIDEO' | 'IMAGE_POST' | 'MIXED'

export type ReferenceContentStatus = 'pending' | 'analyzing' | 'ready' | 'partial' | 'analyzed' | 'failed'

export type ReferenceLibraryView = 'mine' | 'shared' | 'all'

export interface ReferenceContentTeamBlock {
  enabled: boolean
  active_member_count: number
  view?: ReferenceLibraryView
  user_id?: string
  role?: string
}

export interface ReferenceContentCreator {
  user_id: string
  display_name: string
  is_me: boolean
}

export interface ReferenceContentSummary {
  id: string
  workspace_id: string
  created_by_user_id?: string | null
  visibility?: 'workspace' | 'private'
  can_manage?: boolean
  creator?: ReferenceContentCreator
  platform: ReferenceContentPlatform
  platform_label: string
  source_url: string
  canonical_url: string | null
  /** Resolved openable original-platform URL (canonical_url || source_url). */
  external_url?: string | null
  external_content_id: string | null
  content_type: ReferenceContentType
  title: string
  author_name: string | null
  published_at: string | null
  duration_ms: number | null
  cover_image_url: string | null
  metadata: Record<string, unknown>
  status: ReferenceContentStatus
  status_label: string
  status_detail: string | null
  original_link_reachable: boolean | null
  created_at: string
  updated_at: string
  last_analyzed_at: string | null
}

export interface ReferenceContentListResult {
  items: ReferenceContentSummary[]
  total: number
  page: number
  page_size: number
  team?: ReferenceContentTeamBlock
}

export interface CaptureSnapshotOut {
  id: string
  version: number
  status: 'pending' | 'running' | 'succeeded' | 'failed'
  capability_id: string | null
  media_kind: string | null
  media_duration_ms: number | null
  media_width: number | null
  media_height: number | null
  local_media_manifest: Record<string, unknown>
  source_media_available: boolean | null
  /** @deprecated back-compat mirror only — see MediaAnalysisSnapshotOut.media_probe */
  probe: Record<string, unknown>
  error_code: string | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
}

// Media Layer Contract Reconciliation V1 — Layer 2. `component_status` is
// keyed by probe/asr/scene/keyframe/motion/ocr, each one of PENDING/
// RUNNING/SUCCEEDED/FAILED/SKIPPED (spec §6) — never used to expose any
// Worker/tool name to the tenant, only to drive the generic pending/
// partial/ready UI states below.
export interface MediaAnalysisSnapshotOut {
  id: string
  version: number
  status: 'PENDING' | 'RUNNING' | 'PARTIAL' | 'READY' | 'FAILED'
  capability_id: string | null
  content_type: ReferenceContentType | null
  pipeline_version: string
  media_probe: Record<string, unknown>
  motion_analysis: Record<string, unknown>
  ocr_results: Record<string, unknown> | unknown[]
  component_status: Record<string, string>
  error_code: string | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
}

export interface ContentStructureBeat {
  start_ms: number
  end_ms: number
  label: string
  accent_color?: string | null
}

export interface ScriptStructureStage {
  stage: string
  start_ms?: number | null
  end_ms?: number | null
  quote?: string | null
  function: string
  expression?: string | null
  why_effective?: string | null
}

/** Canonical Layer 3 Skill output (`competitive_content_semantic_analysis_v1`). */
export type SemanticAnalysisV1 = Record<string, unknown>

export interface AnalysisSnapshotOut {
  id: string
  status: 'pending' | 'running' | 'succeeded' | 'failed'
  skill_version: string
  analysis_schema_version: number
  media_analysis_snapshot_id: string | null
  error_code: string | null
  error_message: string | null
  created_at: string
  completed_at: string | null
  summary: string | null
  highlights: string[] | null
  tags: string[] | null
  content_structure: ContentStructureBeat[] | null
  /** Legacy string, or canonical Skill object when semantic_analysis_v1 is used. */
  hook_analysis: string | Record<string, unknown> | null
  script_analysis: ScriptStructureStage[] | Record<string, unknown> | null
  visual_strategy: { observations?: string[] } | Record<string, unknown> | null
  rhythm_analysis: string | Record<string, unknown> | null
  audience_strategy: string | null
  platform_strategy: string | null
  strengths: string[] | null
  weaknesses: string[] | null
  replicable_patterns: string[] | Array<Record<string, unknown>> | null
  creative_takeaways: string[] | Record<string, unknown> | null
  /** Canonical Skill payload when present — preferred over legacy flat keys. */
  semantic_analysis_v1?: SemanticAnalysisV1 | null
}

export interface TranscriptSegmentOut {
  index: number
  start_ms: number
  end_ms: number
  text: string
  speaker: string | null
}

export interface ShotSegmentOut {
  index: number
  start_ms: number
  end_ms: number
  representative_frame_url: string | null
  keyframe_storage_key?: string | null
  /** Read-time projection from MediaAnalysisSnapshot transcript_segments (overlap). */
  transcript_text?: string | null
  shot_scale: string | null
  camera_motion: string | null
  visual_description: string | null
  narrative_function: string | null
}

export type AnalysisStageStatus =
  | 'NOT_STARTED'
  | 'QUEUED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'

export interface AnalysisStageOut {
  id: string
  label: string
  status: AnalysisStageStatus
  completed_at: string | null
  error_code: string | null
  user_safe_message: string | null
}

export interface AnalysisProgressSummary {
  media_completed: number
  media_total: number
  ai_completed: number
  ai_total: number
  ai_running: boolean
  ai_failed: boolean
}

export interface ImagePostPageOut {
  index: number
  image_url: string | null
  width: number | null
  height: number | null
  page_role: string | null
  caption_text: string | null
}

export interface CcaRecoveryContractOut {
  first_failed_stage: string | null
  retry_start_stage: string | null
  recovery_kind: 'capture' | 'media' | 'semantic' | 'none'
  reused_successful_stages: string[]
  invalidated_downstream_stages: string[]
  diagnostic_code: string | null
  retryable: boolean
  recommended_action: string | null
  active_task_id: string | null
  active_task_kind: string | null
  source_recapture_required: boolean
  reference_content_id: string
  original_failed_task_id: string | null
  retry_task_id: string | null
  retry_of_task_id: string | null
  full_rerun_available: boolean
  retry_failed_stages_available: boolean
  active_retry_reused?: boolean
  cca_record_id_unchanged?: boolean
  execution_state?: string | null
  active_task_in_flight?: boolean
}

export interface ReferenceContentDetail extends ReferenceContentSummary {
  capture: CaptureSnapshotOut | null
  media_analysis: MediaAnalysisSnapshotOut | null
  analysis: AnalysisSnapshotOut | null
  analysis_stages?: AnalysisStageOut[]
  analysis_progress?: AnalysisProgressSummary
  recovery?: CcaRecoveryContractOut
  transcript: TranscriptSegmentOut[]
  shots: ShotSegmentOut[]
  image_pages: ImagePostPageOut[]
}

const competitiveContentBase = (workspaceId: string) => `/workspaces/${workspaceId}/competitive-content`

export const listReferenceContents = (
  workspaceId: string,
  params?: {
    platform?: ReferenceContentPlatform
    content_type?: ReferenceContentType
    status?: ReferenceContentStatus
    q?: string
    page?: number
    page_size?: number
    view?: ReferenceLibraryView
  },
) => saasApi.get<ReferenceContentListResult>(competitiveContentBase(workspaceId), { params })

export const createReferenceContent = (workspaceId: string, sourceUrl: string, contentType?: ReferenceContentType) =>
  saasApi.post<ReferenceContentSummary>(competitiveContentBase(workspaceId), {
    source_url: sourceUrl,
    content_type: contentType,
  })

export const detectReferenceContentPlatform = (workspaceId: string, sourceUrl: string) =>
  saasApi.post<{ platform: ReferenceContentPlatform; platform_label: string; source_url?: string }>(
    `${competitiveContentBase(workspaceId)}/detect-platform`,
    { source_url: sourceUrl },
  )

export const getReferenceContentDetail = (workspaceId: string, id: string) =>
  saasApi.get<ReferenceContentDetail>(`${competitiveContentBase(workspaceId)}/${id}`)

export const deleteReferenceContent = (workspaceId: string, id: string) =>
  saasApi.delete(`${competitiveContentBase(workspaceId)}/${id}`)

export const retryReferenceContentCapture = (workspaceId: string, id: string) =>
  saasApi.post<ReferenceContentSummary>(`${competitiveContentBase(workspaceId)}/${id}/retry`)

// Media Layer Contract Reconciliation V1 §5 — re-run Layer 2 only (new
// ASR/scene-detect/OCR pass) against the already-succeeded capture.
export const retryReferenceContentMediaAnalysis = (workspaceId: string, id: string) =>
  saasApi.post<ReferenceContentSummary & { media_analysis: MediaAnalysisSnapshotOut | null }>(
    `${competitiveContentBase(workspaceId)}/${id}/retry-media-analysis`,
  )

// Semantic SaaS Integration V1 — Layer 3 only (never re-capture / re-run media).
export const retryReferenceContentSemanticAnalysis = (workspaceId: string, id: string) =>
  saasApi.post<ReferenceContentSummary & { semantic_harness_run_id?: string | null; media_analysis_snapshot_id?: string | null }>(
    `${competitiveContentBase(workspaceId)}/${id}/retry-semantic-analysis`,
  )

/** Stage-aware in-place recovery — same CCA record, no duplicate list row. */
export const retryReferenceContentFailedStages = (workspaceId: string, id: string) =>
  saasApi.post<ReferenceContentDetail & { recovery: CcaRecoveryContractOut }>(
    `${competitiveContentBase(workspaceId)}/${id}/retry-failed-stages`,
  )

// --------------------------------------------------------------- Content Production Workbench
// VISTOERA_CONTENT_PRODUCTION_WORKBENCH_V1 — tenant AI 创作工作台.

export type ContentProjectStatus = 'draft' | 'active' | 'archived'
export type ContentTargetPlatform = 'douyin' | 'xiaohongshu' | 'bilibili' | 'other'
export type ProductionRunStatus =
  | 'draft'
  | 'plan_generating'
  | 'plan_ready'
  | 'executing'
  | 'awaiting_confirmation'
  | 'completed'
  | 'failed'
export type ProductionPlanStatus = 'empty' | 'ready' | 'failed'
export type ProductionArtifactKind =
  | 'topic'
  | 'script'
  | 'storyboard'
  | 'image'
  | 'video'
  | 'final'
  | 'platform_version'
  | 'other'
export type ProductionArtifactStatus = 'draft' | 'ready' | 'failed'

export interface ProductionConstraints {
  target_audience: string
  content_style: string
  video_duration: string
  core_selling_points: string
  avoid_content: string
}

export interface ProductionContextOut {
  id: string
  project_id: string
  constraints: ProductionConstraints
  product_asset_ids: string[]
  reference_content_ids: string[]
  created_at: string
  updated_at: string
}

export interface VirtualIpLinkSummary {
  id: string
  name: string
  tag: string | null
  bio: string | null
  avatar_url: string | null
  status: string
}

export interface ProductAssetLinkSummary {
  id: string
  title: string
  media_type: string
  status: string
  thumb_url: string | null
}

export interface ReferenceContentLinkSummary {
  id: string
  title: string
  platform: string
  platform_label: string
  /** Present on full ReferenceContent rows; optional on workbench link summaries. */
  content_type?: string
  status: string
  /** Backend field name from `_reference_content_summaries`. */
  cover_image_url: string | null
}

export interface ProductionPlanSection {
  index: number
  title: string
  body: string
}

export interface ProductionPlanPayload {
  sections: ProductionPlanSection[]
}

export interface ProductionPlanOut {
  id: string
  run_id: string
  status: ProductionPlanStatus
  status_label: string
  plan: ProductionPlanPayload
  created_at: string
  updated_at: string
}

export interface ProductionRunOut {
  id: string
  project_id: string
  stage: number
  description: string
  status: ProductionRunStatus
  status_label: string
  agent_runtime_ready: boolean
  readiness_message: string | null
  agent_execution_id?: string | null
  created_at: string
  updated_at: string
}

export interface ProductionArtifactOut {
  id: string
  project_id: string
  run_id: string
  kind: ProductionArtifactKind | string
  title: string
  payload: Record<string, unknown>
  status: ProductionArtifactStatus | string
  created_at: string
  updated_at: string
}

export interface AgentRuntimeReadiness {
  ready: boolean
  message: string
}

export interface ContentProjectCoverCandidate {
  asset_id: string | null
  asset_type: 'site_asset' | 'image_generation_output' | 'video_task' | 'video_asset' | string
  thumbnail_url: string
  role: string
  is_video?: boolean
}

export interface ContentProjectGalleryMetadata {
  subtitle: string
  has_visual_assets: boolean
  status_label: string
  target_platform_label: string
}

export interface ContentProjectSummary {
  id: string
  workspace_id: string
  title: string
  thumbnail_asset_id: string | null
  status: ContentProjectStatus
  status_label: string
  target_platform: ContentTargetPlatform | string
  target_platform_label: string
  current_stage: number
  user_goal: string | null
  virtual_ip_id: string | null
  created_at: string
  updated_at: string
  last_edited_at?: string | null
  cover_candidates?: ContentProjectCoverCandidate[]
  gallery_metadata?: ContentProjectGalleryMetadata
  /** TASK-00556 team sharing: `private` (仅自己) | `workspace` (团队共享). */
  visibility?: ContentProjectVisibility
  created_by_user_id?: string | null
  creator?: ContentProjectCreator | null
  is_mine?: boolean
  permissions?: ContentProjectPermissions
  access_mode?: ContentProjectAccessMode
}

export type ContentProjectVisibility = 'private' | 'workspace'
export type ContentProjectAccessMode = 'EDITABLE' | 'READ_ONLY'
export type ContentProjectView = 'mine' | 'shared' | 'all'
export type ContentProjectSort = 'created_desc' | 'created_asc' | 'updated_desc'

export interface ContentProjectCreator {
  user_id: string
  display_name: string
  /** Only present on the Owner-only member-projects listing. */
  email?: string
}

/** Server-evaluated permissions (re-checked on every request). */
export interface ContentProjectPermissions {
  can_view: boolean
  can_edit: boolean
  can_copy: boolean
  can_manage_visibility: boolean
  access_mode: ContentProjectAccessMode
}

export interface ContentProjectTeamBlock {
  enabled: boolean
  active_member_count: number
  view?: ContentProjectView
  user_id?: string
  role?: string
}

export interface ContentProjectCopyReport {
  source_project_id: string
  restricted_asset_count: number
  restricted_assets: Array<{ node_id: string; reason: string }>
}

export interface ContentProjectDetail extends ContentProjectSummary {
  copy_report?: ContentProjectCopyReport
  context: ProductionContextOut
  virtual_ip: VirtualIpLinkSummary | null
  product_assets: ProductAssetLinkSummary[]
  reference_contents: ReferenceContentLinkSummary[]
  latest_plan: ProductionPlanOut | null
  runs: ProductionRunOut[]
  artifacts: ProductionArtifactOut[]
  agent_runtime: AgentRuntimeReadiness
}

export interface ContentProjectListResult {
  items: ContentProjectSummary[]
  total: number
  page: number
  page_size: number
  /** Present for every viewer-aware list; `enabled` only when the Workspace
   * has >= 2 ACTIVE members (drives the 我的项目 / 团队共享 / 全部 tabs). */
  team?: ContentProjectTeamBlock
}

export interface ContentProjectMemberOption {
  user_id: string
  display_name: string
  email: string | null
  role: string | null
  status: 'active' | 'departed'
  is_self: boolean
  project_count: number
}

export interface ContentProjectMemberOptionsResult {
  members: ContentProjectMemberOption[]
  unknown_project_count: number
}

export interface ContentProjectSaveDraftBody {
  title?: string
  target_platform?: string
  current_stage?: number
  user_goal?: string | null
  virtual_ip_id?: string | null
  clear_virtual_ip?: boolean
  constraints?: ProductionConstraints
  product_asset_ids?: string[]
  reference_content_ids?: string[]
  thumbnail_asset_id?: string | null
  clear_thumbnail?: boolean
}

export interface ContentProductionActionResult {
  ok?: boolean
  accepted?: boolean
  message?: string | null
  run?: ProductionRunOut
  agent_runtime?: AgentRuntimeReadiness
}

const contentProductionBase = (workspaceId: string) => `/workspaces/${workspaceId}/content-production`

export const listContentProjects = (
  workspaceId: string,
  params?: { q?: string; page?: number; page_size?: number; view?: ContentProjectView; sort?: ContentProjectSort },
) => saasApi.get<ContentProjectListResult>(contentProductionBase(workspaceId), { params })

/** TASK-00556 — creator-only share toggle (仅自己 <-> 团队共享). */
export const setContentProjectVisibility = (
  workspaceId: string,
  projectId: string,
  visibility: ContentProjectVisibility,
) =>
  saasApi.patch<ContentProjectSummary>(
    `${contentProductionBase(workspaceId)}/${encodeURIComponent(projectId)}/visibility`,
    { visibility },
  )

/** TASK-00556 — Workspace-OWNER-only management listing (403 otherwise). */
export const listContentProjectMemberProjects = (
  workspaceId: string,
  params?: { creator_id?: string; q?: string; sort?: ContentProjectSort; page?: number; page_size?: number },
) => saasApi.get<ContentProjectListResult>(`${contentProductionBase(workspaceId)}/member-projects`, { params })

export const listContentProjectMemberOptions = (workspaceId: string) =>
  saasApi.get<ContentProjectMemberOptionsResult>(`${contentProductionBase(workspaceId)}/member-projects/members`)

export const createContentProject = (workspaceId: string, title?: string) =>
  saasApi.post<ContentProjectDetail>(contentProductionBase(workspaceId), title ? { title } : {})

export const getContentProject = (workspaceId: string, projectId: string) =>
  saasApi.get<ContentProjectDetail>(`${contentProductionBase(workspaceId)}/${encodeURIComponent(projectId)}`)

export const saveContentProjectDraft = (
  workspaceId: string,
  projectId: string,
  body: ContentProjectSaveDraftBody,
) =>
  saasApi.patch<ContentProjectDetail>(
    `${contentProductionBase(workspaceId)}/${encodeURIComponent(projectId)}`,
    body,
  )

// VISTOERA_CONTENT_PROJECT_DIRECT_CANVAS_AND_FULLSCREEN_CANVAS_SHELL_V1
// §9/§10 — hard delete (see `content_production_service.delete_project`'s
// own docstring for why); 204 No Content, so no response body type.
export const deleteContentProject = (workspaceId: string, projectId: string) =>
  saasApi.delete<void>(`${contentProductionBase(workspaceId)}/${encodeURIComponent(projectId)}`)

export interface ContentProjectBatchDeleteResult {
  deleted_ids: string[]
  deleted_count: number
}

export const batchDeleteContentProjects = (workspaceId: string, projectIds: string[]) =>
  saasApi.post<ContentProjectBatchDeleteResult>(`${contentProductionBase(workspaceId)}/batch-delete`, {
    project_ids: projectIds,
  })

/** VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_INTERACTION_V2 §41
 * "复制项目" — clones the project + its full Canvas graph server-side
 * (new node/edge ids, same underlying `SiteAsset` references) and
 * returns the SAME `ContentProjectDetail` shape `createContentProject`
 * does, so the caller can read `.data.id` and navigate straight to the
 * new project's Canvas. */
export const duplicateContentProject = (workspaceId: string, projectId: string) =>
  saasApi.post<ContentProjectDetail>(`${contentProductionBase(workspaceId)}/${encodeURIComponent(projectId)}/duplicate`)

export const generateContentProductionPlan = (workspaceId: string, projectId: string) =>
  saasApi.post<ContentProductionActionResult>(
    `${contentProductionBase(workspaceId)}/${encodeURIComponent(projectId)}/generate-plan`,
  )

export const startContentProduction = (workspaceId: string, projectId: string) =>
  saasApi.post<ContentProductionActionResult>(
    `${contentProductionBase(workspaceId)}/${encodeURIComponent(projectId)}/start-creation`,
  )

export const listContentProductionVirtualIpOptions = (workspaceId: string) =>
  saasApi.get<{ items: VirtualIpLinkSummary[] }>(`${contentProductionBase(workspaceId)}/virtual-ip-options`)

// VISTOERA_PRODUCTION_CANVAS_V1_FOUNDATION_AND_INTERACTIVE_SHELL — Canvas
// is a spatial VIEW nested under an owned `ContentProject` (same base as
// the functions above), never a sibling top-level resource. Types mirror
// `app.api.routes.saas_production_canvas`'s Pydantic models 1:1.
const productionCanvasBase = (workspaceId: string, projectId: string) =>
  `${contentProductionBase(workspaceId)}/${encodeURIComponent(projectId)}/canvas`

export const getProductionCanvas = (workspaceId: string, projectId: string) =>
  saasApi.get<ProductionCanvasDto>(productionCanvasBase(workspaceId, projectId))

export const saveProductionCanvas = (
  workspaceId: string,
  projectId: string,
  body: { nodes: CanvasNodeDto[]; edges: CanvasEdgeDto[]; viewport: CanvasViewportDto },
) => saasApi.put<ProductionCanvasDto>(productionCanvasBase(workspaceId, projectId), body)

export const seedProductionCanvasSample = (workspaceId: string, projectId: string) =>
  saasApi.post<ProductionCanvasDto>(`${productionCanvasBase(workspaceId, projectId)}/seed-sample`)

// VISTOERA_CANVAS_AS_PRIMARY_HOME_AND_GLOBAL_ACCOUNT_ENTRY_V1 — resolves
// (idempotently creating, on first-ever visit) THE ONE canonical Home
// Canvas project id for this workspace, and ensures its 4 Starter Nodes
// exist. Never itself a Canvas read — the caller must still navigate to
// `getProductionCanvas`'s own canonical route with the returned id.
export interface HomeCanvasResolution {
  project_id: string
  created_project: boolean
  seeded_starter_nodes: boolean
}

export const getHomeCanvas = (workspaceId: string) =>
  saasApi.get<HomeCanvasResolution>(`/workspaces/${encodeURIComponent(workspaceId)}/home-canvas`)

// VISTOERA_CANVAS_VOICE_TEMPLATE_GENERATOR_NODE_V1
export type CanvasVoiceTemplateSourceType = 'AI_GENERATED' | 'REFERENCE' | 'CLONED'

export interface CanvasVoiceTemplateAudioAsset {
  asset_id: string
  filename: string | null
  mime_type: string
  size_bytes: number
  duration_ms: number | null
  media_type: string
  playback_url: string
  status: string
}

export interface CanvasVoiceTemplateDto {
  type: 'voice_template'
  template_id: string
  name: string
  source_type: CanvasVoiceTemplateSourceType | string
  creation_mode: string
  voice_brief: string | null
  preview_text: string | null
  reference_audio_asset_ids: string[]
  reference_audio_assets: CanvasVoiceTemplateAudioAsset[]
  voice_rights_confirmed: boolean
  voice_rights_confirmed_at: string | null
  provider: string | null
  provider_voice_id: string | null
  preview_audio_asset_id: string | null
  preview_audio: CanvasVoiceTemplateAudioAsset | null
  voice_profile: Record<string, unknown>
  status: string
  error_code: string | null
  error_message: string | null
  metadata: Record<string, unknown>
}

export interface GenerateCanvasVoiceTemplatePayload {
  source_type?: CanvasVoiceTemplateSourceType | string
  voice_brief?: string | null
  preview_text?: string | null
  template_name?: string | null
  reference_audio_asset_ids?: string[] | null
  voice_rights_confirmed?: boolean | null
}

const canvasVoiceTemplateBase = (workspaceId: string, projectId: string, nodeId: string) =>
  `${productionCanvasBase(workspaceId, projectId)}/nodes/${encodeURIComponent(nodeId)}/voice-template`

export const getCanvasVoiceTemplate = (
  workspaceId: string,
  projectId: string,
  nodeId: string,
  templateId?: string | null,
) =>
  saasApi.get<CanvasVoiceTemplateDto>(canvasVoiceTemplateBase(workspaceId, projectId, nodeId), {
    params: templateId ? { template_id: templateId } : undefined,
  })

export interface CanvasVoiceTemplateRuntimeCapabilitiesDto {
  voice_provider_ready: boolean
  voice_clone_runtime_enabled: boolean
  voice_clone_blocker_code?: string | null
  provider: string | null
}

/** VistoeraTASK-00219 — canonical Voice Template Export URL (a Vistoera
 * manifest, never the Preview Audio bytes). Optional `templateId` mirrors
 * `getCanvasVoiceTemplate`'s own resolution contract: omit it to resolve
 * "the most recently updated template for this node" server-side — the
 * same fallback already relied on elsewhere in this file. */
export const canvasVoiceTemplateExportUrl = (
  workspaceId: string,
  projectId: string,
  nodeId: string,
  templateId?: string | null,
): string => {
  // `canvasVoiceTemplateBase` returns a path meant for the `saasApi` axios
  // instance, whose own `baseURL` already carries the `/api/saas` prefix —
  // NOT for a raw cross-origin `resolveSaasMediaUrl` build, which needs
  // that prefix spelled out explicitly (same fix already applied once to
  // `canvasVoiceTemplatePlaybackUrl` in voiceTemplateProvider.ts; caught
  // here via real Preview E2E, not by the type checker, since both are
  // plain strings).
  const base = `/api/saas${canvasVoiceTemplateBase(workspaceId, projectId, nodeId)}/export`
  const path = templateId ? `${base}?template_id=${encodeURIComponent(templateId)}` : base
  return resolveSaasMediaUrl(path) as string
}

export const getCanvasVoiceTemplateRuntimeCapabilities = (
  workspaceId: string,
  projectId: string,
  nodeId: string,
) =>
  saasApi.get<CanvasVoiceTemplateRuntimeCapabilitiesDto>(
    `${canvasVoiceTemplateBase(workspaceId, projectId, nodeId)}/runtime-capabilities`,
  )

export const generateCanvasVoiceTemplate = (
  workspaceId: string,
  projectId: string,
  nodeId: string,
  payload: GenerateCanvasVoiceTemplatePayload,
) =>
  saasApi.post<CanvasVoiceTemplateDto>(`${canvasVoiceTemplateBase(workspaceId, projectId, nodeId)}/generate`, payload, {
    timeout: 300000,
  })

export const uploadCanvasVoiceTemplateReferenceAudio = (
  workspaceId: string,
  projectId: string,
  nodeId: string,
  file: File,
  durationMs?: number | null,
  captureMethod?: 'UPLOAD' | 'MICROPHONE' | null,
) => {
  const form = new FormData()
  form.append('file', file)
  if (durationMs != null) form.append('duration_ms', String(durationMs))
  if (captureMethod) form.append('capture_method', captureMethod)
  return saasApi.post<CanvasVoiceTemplateAudioAsset>(
    `${canvasVoiceTemplateBase(workspaceId, projectId, nodeId)}/reference-audio`,
    form,
    { timeout: 120000 },
  )
}

// VISTOERA_CANVAS_GENERATE_AUDIO_NODE_V1
export interface CanvasSystemVoiceTemplateDto {
  type: 'system_voice_template'
  system_voice_key: string
  provider: string
  provider_voice_id: string
  language: string
  gender: string
  style_tags: string[]
  name: string
  description: string
  preview_text: string
}

export interface CanvasVoiceTemplateListItemDto {
  type: 'voice_template'
  template_id: string
  name: string
  source_type: string
  voice_brief: string | null
  provider: string | null
  provider_voice_id: string | null
  preview_audio_asset_id: string | null
  preview_audio: CanvasVoiceTemplateAudioAsset | null
  canvas_node_id: string
  project_id: string
  status?: string
  created_at?: string | null
  updated_at: string | null
  usable?: boolean
  selectable?: boolean
  executable?: boolean
  creation_status?: string
  execution_availability?: string
  execution_availability_reason?: string | null
  generation_preference?: VoiceTemplateGenerationPreference | null
}

export interface CanvasVoiceTemplateListResult {
  items: CanvasVoiceTemplateListItemDto[]
  count: number
  total: number
  page: number
  page_size: number
}

export interface CanvasAudioGenerationDto {
  type: 'audio'
  artifact_id: string
  audio_asset_id: string | null
  voice_template_id: string | null
  system_voice_key: string | null
  text_snapshot: string
  duration_ms: number | null
  mime_type: string | null
  provider: string | null
  model: string | null
  provider_voice_id: string | null
  audio: CanvasVoiceTemplateAudioAsset | null
  status: string
  error_code: string | null
  error_message: string | null
  generation_metadata: Record<string, unknown>
  // VistoeraTASK-00217 — Generation Provenance top-level convenience
  // fields (also duplicated inside `generation_metadata`).
  pitch?: number | null
  speed?: number | null
}

export interface GenerateCanvasAudioPayload {
  text: string
  voice_template_id?: string | null
  system_voice_key?: string | null
  // VistoeraTASK-00217 — optional; the backend normalizes/clamps against
  // the real active Provider's own capability (never trust this raw
  // value client-side either — Canvas only ever sends what its OWN
  // capability-driven Slider bounds already allowed).
  pitch?: number | null
  speed?: number | null
}

/** VistoeraTASK-00217 — ONE Provider parameter's real Canvas-facing
 * capability bounds (mirrors the backend's `VoiceGenerationParameterCapability.to_dict()`). */
export interface AudioGenerationParameterCapabilityField {
  supported: boolean
  min: number | null
  max: number | null
  step: number | null
  default: number | null
  unit: string | null
}

export interface AudioGenerationParameterCapabilityResponse {
  provider: string | null
  ready: boolean
  pitch: AudioGenerationParameterCapabilityField
  speed: AudioGenerationParameterCapabilityField
}

const canvasAudioGenerationBase = (workspaceId: string, projectId: string, nodeId: string) =>
  `${productionCanvasBase(workspaceId, projectId)}/nodes/${encodeURIComponent(nodeId)}/audio-generation`

export const listCanvasSystemVoices = (workspaceId: string, locale?: string) =>
  saasApi.get<{ items: CanvasSystemVoiceTemplateDto[]; count: number }>(
    `${contentProductionBase(workspaceId)}/voice-templates/system`,
    { params: locale ? { locale } : undefined },
  )

export const fetchVoiceTemplateExecutionReadiness = (
  workspaceId: string,
  templateId: string,
) =>
  saasApi.get<{
    template_id: string
    creation_status: string
    execution_availability: string
    execution_availability_reason: string | null
    selectable: boolean
    executable: boolean
    usable: boolean
  }>(`${contentProductionBase(workspaceId)}/voice-templates/${encodeURIComponent(templateId)}/execution-readiness`)

export const listCanvasMyVoiceTemplates = (
  workspaceId: string,
  projectId: string,
  params?: {
    q?: string
    sort?: 'recent' | 'oldest' | 'name'
    page?: number
    page_size?: number
    include_unusable?: boolean
  },
) =>
  saasApi.get<CanvasVoiceTemplateListResult>(
    `${contentProductionBase(workspaceId)}/${encodeURIComponent(projectId)}/voice-templates`,
    { params },
  )

export const previewCanvasSystemVoice = (workspaceId: string, systemVoiceKey: string, locale?: string) =>
  saasApi.post<CanvasVoiceTemplateAudioAsset>(
    `${contentProductionBase(workspaceId)}/voice-templates/system/${encodeURIComponent(systemVoiceKey)}/preview`,
    {},
    { params: locale ? { locale } : undefined, timeout: 120000 },
  )

export const getCanvasAudioGeneration = (
  workspaceId: string,
  projectId: string,
  nodeId: string,
  artifactId?: string | null,
) =>
  saasApi.get<CanvasAudioGenerationDto>(canvasAudioGenerationBase(workspaceId, projectId, nodeId), {
    params: artifactId ? { artifact_id: artifactId } : undefined,
  })

export const generateCanvasAudio = (
  workspaceId: string,
  projectId: string,
  nodeId: string,
  payload: GenerateCanvasAudioPayload,
) =>
  saasApi.post<CanvasAudioGenerationDto>(
    `${canvasAudioGenerationBase(workspaceId, projectId, nodeId)}/generate`,
    payload,
    { timeout: 300000 },
  )

// --- VistoeraTASK-00217 — Capability-driven Pitch/Speed --------------

export const fetchAudioGenerationParameterCapability = (workspaceId: string) =>
  saasApi.get<AudioGenerationParameterCapabilityResponse>(
    `${contentProductionBase(workspaceId)}/audio-generation/parameter-capability`,
  )

export const fetchVoiceTemplateGenerationPreference = (workspaceId: string, templateId: string) =>
  saasApi.get<{ generation_preference: VoiceTemplateGenerationPreference | null }>(
    `${contentProductionBase(workspaceId)}/voice-templates/${encodeURIComponent(templateId)}/generation-preference`,
  )

export const saveVoiceTemplateGenerationPreference = (
  workspaceId: string,
  templateId: string,
  payload: { pitch: number | null; speed: number | null },
) =>
  saasApi.put<{ generation_preference: VoiceTemplateGenerationPreference | null }>(
    `${contentProductionBase(workspaceId)}/voice-templates/${encodeURIComponent(templateId)}/generation-preference`,
    payload,
  )

// ---------------------------------------------------------------------
// VISTOERA_PC3_MOTION_RECONSTRUCTION_WORKER_AND_CANVAS_NODE_V1
// ---------------------------------------------------------------------

export interface MotionAssetDto {
  motion_asset_id: string
  workspace_id: string
  site_id: string
  motion_type: 'MOTION_2D' | 'MOTION_3D' | 'MOTION_RETARGETED'
  status: string
  schema_version: string
  duration_ms: number
  fps: number
  frame_count: number
  joint_count: number
  skeleton_schema: string
  coordinate_space: string
  label?: string | null
  preview?: Record<string, unknown> | null
}

export interface MotionReconstructionDto {
  reconstruction_id: string
  canvas_node_id: string
  input_video_asset_id: string | null
  input_motion_asset_id: string | null
  output_motion_asset_id: string | null
  job_id: string | null
  quality_mode: string
  status: string
  error_code?: string | null
  error_message?: string | null
  output_motion?: MotionAssetDto | null
  available_tracks?: Array<{
    track_id: string
    frames_present: number
    coverage: number
    mean_confidence: number
    representative_frame_index?: number
    representative_timestamp_ms?: number
  }>
  selected_track_id?: string | null
}

export interface SubmitMotionReconstructionPayload {
  input_video_asset_id: string
  quality_mode?: string
  selected_track_id?: string
}

export interface SelectMotionTrackPayload {
  selected_track_id: string
}

export const getMotionAsset = (workspaceId: string, motionAssetId: string) =>
  saasApi.get<MotionAssetDto>(`/workspaces/${workspaceId}/motion-assets/${motionAssetId}`)

export const getMotionAssetPreview = (workspaceId: string, motionAssetId: string) =>
  saasApi.get<Record<string, unknown>>(`/workspaces/${workspaceId}/motion-assets/${motionAssetId}/preview`)

export const submitMotionReconstruction = (
  workspaceId: string,
  projectId: string,
  nodeId: string,
  payload: SubmitMotionReconstructionPayload,
) =>
  saasApi.post<MotionReconstructionDto>(
    `/workspaces/${workspaceId}/content-production/${projectId}/canvas/nodes/${nodeId}/motion-reconstruction`,
    payload,
  )

export const getMotionReconstruction = (workspaceId: string, reconstructionId: string) =>
  saasApi.get<MotionReconstructionDto>(
    `/workspaces/${workspaceId}/content-production/motion-reconstructions/${reconstructionId}`,
  )

export const selectMotionReconstructionTrack = (
  workspaceId: string,
  reconstructionId: string,
  payload: SelectMotionTrackPayload,
) =>
  saasApi.post<MotionReconstructionDto>(
    `/workspaces/${workspaceId}/content-production/motion-reconstructions/${reconstructionId}/track-selection`,
    payload,
  )

// ---------------------------------------------------------------------
// VISTOERA_VIDEO_DIRECTOR_PROPOSAL_SKILL_AND_THREE_OPTION_SELECTION_P0
// Canvas-reusable Skill client — never provider-specific (/chatgpt/...).
// ---------------------------------------------------------------------

export interface VideoDirectorShotV1 {
  shot_index: number
  duration_seconds: number
  shot_type: string
  framing: string
  camera_movement: string
  subject_action: string
  environment_action?: string | null
  visual_description: string
  transition?: string | null
  audio_note?: string | null
  reference_usage?: string | null
}

export interface VideoDirectorProposalV1 {
  proposal_id: string
  title: string
  one_line_concept: string
  creative_direction: string
  why_it_fits: string
  narrative_structure: string
  opening_hook: string
  ending_image: string
  pacing: string
  visual_style: string
  camera_language: string
  subject_motion: string
  environment_motion: string
  audio_direction: string
  estimated_shot_count: number
  estimated_duration: number
  generation_strategy: string
  risks_or_notes?: string | null
  style_tags?: string[]
  shots: VideoDirectorShotV1[]
}

export interface VideoDirectorProposalBatchV1 {
  batch_id: string
  skill_id: string
  skill_name: string
  skill_version: string
  skill_run_id: string | null
  status: string
  diversity_status: string | null
  proposals: VideoDirectorProposalV1[] | null
  error_code: string | null
  error_message: string | null
  created_at: string | null
  completed_at: string | null
  input_fingerprint: string
}

export interface VideoDirectorSessionState {
  session_id: string
  workspace_id: string
  status: string
  selection_stale: boolean
  skill_id: string
  skill_name: string
  skill_version: string
  creation_mode: string
  user_intent: string
  input_fingerprint: string
  selected_batch_id: string | null
  selected_proposal_id: string | null
  selected_proposal: VideoDirectorProposalV1 | null
  selected_proposal_at: string | null
  batches: VideoDirectorProposalBatchV1[]
  active_run_id: string | null
  error_code: string | null
  error_message: string | null
  project?: VideoDirectorProjectSummary
}

export type VideoDirectorProjectStage =
  | 'IDEA'
  | 'DIRECTOR_PROPOSAL'
  | 'STORYBOARD'
  | 'KEYFRAME_DESIGN'
  | 'VIDEO_GENERATION'
  | 'RESULT'

export interface VideoDirectorProjectSummary {
  project_id: string
  session_id: string
  title: string
  creation_mode: string
  duration_seconds: number
  aspect_ratio: string
  project_stage: VideoDirectorProjectStage
  project_stage_label: string
  current_step: number
  last_viewed_step: number | null
  project_status: string
  thumbnail_role: string
  source_session_id: string | null
  forked_from_stage: string | null
  updated_at: string | null
  created_at: string | null
}

export interface VideoDirectorProjectListResponse {
  items: VideoDirectorProjectSummary[]
  total: number
  page: number
  page_size: number
}

export interface VideoDirectorInputPayload {
  user_intent: string
  creation_mode?: string
  duration_seconds: number
  aspect_ratio: string
  resolution?: string
  fps?: number
  target_platform?: string
  style_preferences?: string[]
  avoidances?: string[]
  virtual_ip_snapshot?: Record<string, unknown>
  reference_assets?: Array<{
    asset_id: string
    media_type: 'image' | 'video'
    role: string
    filename?: string
  }>
  generation_constraints?: string[]
  session_id?: string
}

const videoDirectorBase = (workspaceId: string) => `/workspaces/${workspaceId}/video-director`

export const listVideoDirectorProjects = (
  workspaceId: string,
  params?: { page?: number; page_size?: number; q?: string },
) =>
  saasApi.get<VideoDirectorProjectListResponse>(`${videoDirectorBase(workspaceId)}/sessions`, { params })

export const createBlankVideoDirectorProject = (workspaceId: string) =>
  saasApi.post<VideoDirectorSessionState>(`${videoDirectorBase(workspaceId)}/sessions/blank`)

export const forkVideoDirectorProject = (
  workspaceId: string,
  sessionId: string,
  data: { forked_from_stage: string; fork_metadata?: Record<string, unknown> },
) =>
  saasApi.post<VideoDirectorSessionState>(
    `${videoDirectorBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/fork`,
    data,
  )

export const patchVideoDirectorProject = (
  workspaceId: string,
  sessionId: string,
  data: {
    title?: string
    current_step?: number
    last_viewed_step?: number
    project_status?: string
  },
) =>
  saasApi.patch<VideoDirectorSessionState>(
    `${videoDirectorBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}`,
    data,
  )

export const createVideoDirectorSession = (workspaceId: string, data: VideoDirectorInputPayload) =>
  saasApi.post<VideoDirectorSessionState>(`${videoDirectorBase(workspaceId)}/sessions`, data)

export const getVideoDirectorSession = (workspaceId: string, sessionId: string) =>
  saasApi.get<VideoDirectorSessionState>(`${videoDirectorBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}`)

export const startVideoDirectorProposals = (
  workspaceId: string,
  sessionId: string,
  data: VideoDirectorInputPayload,
) =>
  saasApi.post<VideoDirectorSessionState>(
    `${videoDirectorBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/proposals`,
    data,
  )

export const regenerateVideoDirectorProposals = (
  workspaceId: string,
  sessionId: string,
  data: VideoDirectorInputPayload,
) =>
  saasApi.post<VideoDirectorSessionState>(
    `${videoDirectorBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/proposals/regenerate`,
    data,
  )

export const selectVideoDirectorProposal = (
  workspaceId: string,
  sessionId: string,
  data: { batch_id: string; proposal_id: string },
) =>
  saasApi.put<VideoDirectorSessionState>(
    `${videoDirectorBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/proposals/select`,
    data,
  )

// ---------------------------------------------------------------------
// VIDEO_SCRIPT_STORYBOARD — Step 2 canvas-reusable Skill client
// ---------------------------------------------------------------------

export interface StoryboardKeyframeRequirementV1 {
  role: 'START' | 'MIDDLE' | 'END'
  description: string
}

export interface StoryboardShotV1 {
  shot_id: string
  shot_number: number
  start_time: number
  end_time: number
  duration_seconds: number
  purpose: string
  visual_description: string
  subject: string
  subject_action: string
  environment: string
  shot_size: string
  camera_angle: string
  camera_position: string
  camera_movement: string
  composition: string
  lighting: string
  transition_in: string
  transition_out: string
  continuity_requirements: string
  audio_cue: string
  keyframe_requirements: StoryboardKeyframeRequirementV1[]
  generation_strategy: string
}

export interface VideoScriptStoryboardV1 {
  schema_version: string
  title: string
  creative_summary: string
  total_duration_seconds: number
  aspect_ratio: string
  narrative_script: string
  continuity_notes: string
  audio_direction: string
  generation_notes: string
  shots: StoryboardShotV1[]
}

export interface VideoStoryboardBatchV1 {
  batch_id: string
  skill_id: string
  skill_name: string
  skill_version: string
  skill_run_id: string | null
  director_batch_id: string
  selected_proposal_id: string
  status: string
  alignment_status: string | null
  storyboard: VideoScriptStoryboardV1 | null
  error_code: string | null
  error_message: string | null
  created_at: string | null
  completed_at: string | null
}

export interface VideoStoryboardSessionState {
  session_id: string
  workspace_id: string
  status: string
  storyboard_stale: boolean
  director_selection_stale: boolean
  skill_id: string
  skill_name: string
  skill_version: string
  selected_director_proposal: VideoDirectorProposalV1 | null
  selected_director_proposal_id: string | null
  selected_director_batch_id: string | null
  confirmed_storyboard_batch_id: string | null
  confirmed_storyboard_at: string | null
  confirmed_storyboard: VideoScriptStoryboardV1 | null
  batches: VideoStoryboardBatchV1[]
  active_run_id: string | null
  error_code: string | null
  error_message: string | null
}

export interface VideoStoryboardInputPayload {
  user_intent?: string
  duration_seconds?: number
  aspect_ratio?: string
  resolution?: string
  quality_tier?: string
  reference_assets?: VideoDirectorInputPayload['reference_assets']
  virtual_ip_snapshot?: Record<string, unknown>
  creation_mode?: string
}

const videoStoryboardBase = (workspaceId: string) => `/workspaces/${workspaceId}/video-storyboard`

export const getVideoStoryboardSession = (workspaceId: string, sessionId: string) =>
  saasApi.get<VideoStoryboardSessionState>(
    `${videoStoryboardBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}`,
  )

export const generateVideoStoryboard = (
  workspaceId: string,
  sessionId: string,
  data?: VideoStoryboardInputPayload,
) =>
  saasApi.post<VideoStoryboardSessionState>(
    `${videoStoryboardBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/generate`,
    data ?? {},
  )

export const regenerateVideoStoryboard = (
  workspaceId: string,
  sessionId: string,
  data?: VideoStoryboardInputPayload,
) =>
  saasApi.post<VideoStoryboardSessionState>(
    `${videoStoryboardBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/regenerate`,
    data ?? {},
  )

export const confirmVideoStoryboard = (
  workspaceId: string,
  sessionId: string,
  data: { batch_id: string },
) =>
  saasApi.put<VideoStoryboardSessionState>(
    `${videoStoryboardBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/confirm`,
    data,
  )

// ---------------------------------------------------------------------
// VIDEO_KEYFRAME_DESIGN — Step 3 canvas-reusable Skill client
// ---------------------------------------------------------------------

export type KeyframeRole = 'START' | 'MIDDLE' | 'END'

export interface KeyframeDesignSpecV1 {
  keyframe_id: string
  shot_id: string
  role: KeyframeRole
  time_position: number
  purpose: string
  visual_description: string
  subject_state: string
  subject_pose_or_action: string
  environment_state: string
  camera_position: string
  camera_angle: string
  shot_size: string
  composition: string
  lighting: string
  continuity_from_previous: string
  continuity_to_next: string
  reference_asset_ids: string[]
  identity_constraints: string
  negative_constraints: string
  generation_prompt_semantic: string
  generation_strategy: string
  status: string
}

/** One row of `saas_video_keyframe_assets` — design spec + generation
 * task lifecycle, kept separate from the design spec itself (task spec
 * §41: "Design Spec 和 Generated Asset 需要独立追踪"). */
export interface VideoKeyframeAssetV1 {
  keyframe_id: string
  shot_id: string
  role: KeyframeRole
  spec: KeyframeDesignSpecV1
  depends_on: string[]
  status:
    | 'not_generated'
    | 'queued'
    | 'running'
    | 'succeeded'
    | 'failed'
    | 'blocked_by_dependency'
    | 'needs_regeneration'
  generation_operation: 'CREATE_NEW' | 'IMAGE_TO_IMAGE' | null
  task_id: string | null
  output_asset_id: string | null
  error_message: string | null
  created_at: string | null
  generated_at: string | null
}

export interface VideoKeyframeGlobalVisualBibleV1 {
  subject_identity: string
  appearance_constraints: string
  wardrobe_or_material: string
  environment_identity: string
  color_and_lighting: string
  visual_style: string
  camera_language: string
  continuity_rules: string
}

export interface VideoKeyframeShotContinuityLinkV1 {
  from_keyframe_id: string
  to_keyframe_id: string
  continuity_type: string
}

export interface VideoKeyframeDesignBatchV1 {
  design_batch_id: string
  skill_id: string
  skill_name: string
  skill_version: string
  skill_run_id: string | null
  storyboard_batch_id: string
  status: string
  global_visual_bible: VideoKeyframeGlobalVisualBibleV1 | null
  shot_continuity_links: VideoKeyframeShotContinuityLinkV1[]
  warnings: string[]
  error_code: string | null
  error_message: string | null
  provider: string | null
  created_at: string | null
  completed_at: string | null
  input_fingerprint: string | null
  keyframes: VideoKeyframeAssetV1[]
}

export interface VideoKeyframeSessionState {
  session_id: string
  workspace_id: string
  status: string
  storyboard_stale: boolean
  keyframe_design_stale: boolean
  all_required_valid: boolean
  step4_unlocked: boolean
  skill_id: string
  skill_name: string
  skill_version: string
  confirmed_keyframe_batch_id: string | null
  confirmed_keyframe_at: string | null
  latest_keyframe_batch_id: string | null
  batches: VideoKeyframeDesignBatchV1[]
  active_batch: VideoKeyframeDesignBatchV1 | null
  error_code: string | null
  error_message: string | null
}

const videoKeyframesBase = (workspaceId: string) => `/workspaces/${workspaceId}/video-keyframes`

export const getVideoKeyframeSession = (workspaceId: string, sessionId: string) =>
  saasApi.get<VideoKeyframeSessionState>(
    `${videoKeyframesBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}`,
  )

export const designVideoKeyframes = (workspaceId: string, sessionId: string) =>
  saasApi.post<VideoKeyframeSessionState>(
    `${videoKeyframesBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/design`,
  )

export const redesignVideoKeyframes = (workspaceId: string, sessionId: string) =>
  saasApi.post<VideoKeyframeSessionState>(
    `${videoKeyframesBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/redesign`,
  )

export const generateVideoKeyframe = (
  workspaceId: string,
  sessionId: string,
  designBatchId: string,
  keyframeId: string,
) =>
  saasApi.post<VideoKeyframeSessionState>(
    `${videoKeyframesBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/batches/${encodeURIComponent(designBatchId)}/keyframes/${encodeURIComponent(keyframeId)}/generate`,
  )

export const regenerateVideoKeyframe = (
  workspaceId: string,
  sessionId: string,
  designBatchId: string,
  keyframeId: string,
) =>
  saasApi.post<VideoKeyframeSessionState>(
    `${videoKeyframesBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/batches/${encodeURIComponent(designBatchId)}/keyframes/${encodeURIComponent(keyframeId)}/regenerate`,
  )

export const generateRequiredVideoKeyframes = (
  workspaceId: string,
  sessionId: string,
  designBatchId: string,
) =>
  saasApi.post<VideoKeyframeSessionState>(
    `${videoKeyframesBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/batches/${encodeURIComponent(designBatchId)}/generate-required`,
  )

export const updateVideoKeyframeSpec = (
  workspaceId: string,
  sessionId: string,
  designBatchId: string,
  keyframeId: string,
  fields: Record<string, unknown>,
) =>
  saasApi.put<VideoKeyframeSessionState>(
    `${videoKeyframesBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/batches/${encodeURIComponent(designBatchId)}/keyframes/${encodeURIComponent(keyframeId)}/spec`,
    { fields },
  )

export const confirmVideoKeyframeDesign = (
  workspaceId: string,
  sessionId: string,
  designBatchId: string,
) =>
  saasApi.put<VideoKeyframeSessionState>(
    `${videoKeyframesBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/confirm`,
    { design_batch_id: designBatchId },
  )

// ---------------------------------------------------------------------
// VIDEO_GENERATION_STEP4_EXECUTION_PIPELINE_P0 — Step 4: shot-level video
// generation execution. Deterministic orchestration only (task spec §5):
// this consumes the already-confirmed Storyboard + Keyframe Design output,
// it never re-derives shot semantics client-side. Playback/download reuse
// the exact canonical `videoGenTaskOutputUrl(workspaceId, taskId)` byte
// endpoint the classic Video Generation page already uses (task spec §八:
// no parallel worker/delivery path invented for Step 4).

export type ShotGenerationStrategy = 'IMAGE_TO_VIDEO' | 'START_END_FRAME_VIDEO'

export type ShotGenerationStatus =
  | 'pending'
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'stale'
  | 'needs_regeneration'

export interface ShotGenerationShotV1 {
  shot_id: string
  shot_number: number
  generation_strategy: ShotGenerationStrategy
  start_time: number
  end_time: number
  duration_seconds: number
  aspect_ratio: string
  required_roles: KeyframeRole[]
  source_keyframe_asset_ids: Record<string, string>
  source_site_asset_ids: Record<string, string>
  prompt_semantic: string | null
  status: ShotGenerationStatus
  task_id: string | null
  output_asset_id: string | null
  provider: string | null
  model: string | null
  error_message: string | null
  stale_reason: string | null
  attempt_count: number
  started_at: string | null
  generated_at: string | null
}

export interface ShotGenerationBatchV1 {
  batch_id: string
  storyboard_batch_id: string
  keyframe_batch_id: string
  input_fingerprint: string
  aspect_ratio: string
  quality_tier: string
  total_duration_seconds: number
  shot_count: number
  created_at: string | null
}

export interface ShotGenerationSessionState {
  session_id: string
  workspace_id: string
  gate_ok: boolean
  gate_reason: string | null
  storyboard_stale: boolean
  keyframe_design_stale: boolean
  aspect_ratio: string | null
  quality_tier: string | null
  total_duration_seconds: number
  shot_count: number
  batch: ShotGenerationBatchV1 | null
  shots: ShotGenerationShotV1[]
}

const videoShotGenerationBase = (workspaceId: string) => `/workspaces/${workspaceId}/video-shot-generation`

export const getVideoShotGenerationSession = (workspaceId: string, sessionId: string) =>
  saasApi.get<ShotGenerationSessionState>(
    `${videoShotGenerationBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}`,
  )

export const generateVideoShot = (workspaceId: string, sessionId: string, shotId: string) =>
  saasApi.post<ShotGenerationSessionState>(
    `${videoShotGenerationBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/shots/${encodeURIComponent(shotId)}/generate`,
  )

export const regenerateVideoShot = (workspaceId: string, sessionId: string, shotId: string) =>
  saasApi.post<ShotGenerationSessionState>(
    `${videoShotGenerationBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/shots/${encodeURIComponent(shotId)}/regenerate`,
  )

export const generateAllVideoShots = (workspaceId: string, sessionId: string) =>
  saasApi.post<ShotGenerationSessionState>(
    `${videoShotGenerationBase(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/generate-all`,
  )

// ---------------------------------------------------------------------
// VISTOERA_CANVAS_PROJECT_START_CREATIVE_BRIEF_AND_IDEATION_BRANCHING_
// NODE_V1 — `project_start` Canvas node's "生成创意方向" / candidate
// "发散" quick action. Thin client for `saas_creative_ideation.py`; the
// Brief itself is plain Canvas node `data.fields` (autosaved through the
// normal full-replace Canvas save, never through this client).
// ---------------------------------------------------------------------

export const CREATIVE_IDEATION_CAPABILITY = 'creative.ideation'

export interface CreativeIdeationAssetRef {
  asset_id?: string | null
  asset_type: string
  display_name: string
}

export interface CreativeIdeationBriefPayload {
  user_intent: string
  content_goal?: string | null
  content_goal_source?: string | null
  target_duration_seconds?: number | null
  duration_source?: string | null
  assets?: CreativeIdeationAssetRef[] | null
  must_include?: string[] | null
  must_avoid?: string[] | null
  audience?: string | null
  platform?: string | null
  tone?: string | null
  references?: string[] | null
}

export interface CreativeIdeationCandidate {
  candidate_id: string
  title: string
  premise: string
  hook: string
  tone: string
  story_direction: string
  product_role: string | null
  character_role: string | null
  estimated_duration: number
  tags: string[]
  rationale: string
}

export type CreativeIdeationRunStatus = 'SUBMITTING' | 'QUEUED' | 'GENERATING' | 'SUCCESS' | 'ERROR'

export interface CreativeIdeationRunState {
  run_id: string
  canvas_node_id: string
  run_kind: 'IDEATION' | 'EXPAND'
  parent_candidate_node_id: string | null
  root_run_id: string | null
  generation_depth: number
  status: CreativeIdeationRunStatus
  skill_key: string
  skill_version: string
  skill_selection_source: string
  provider_selection_mode: 'DEFAULT' | 'EXPLICIT'
  provider_option_id: string | null
  resolved_provider_option_id: string | null
  candidate_count_requested: number
  candidates: CreativeIdeationCandidate[] | null
  input_snapshot: Record<string, unknown>
  error_code: string | null
  error_message: string | null
  created_at: string | null
  completed_at: string | null
}

export interface GenerateCreativeIdeationPayload {
  canvas_node_id: string
  project_id: string
  brief: CreativeIdeationBriefPayload
  candidate_count?: number
  skill_selection_source?: 'AUTO' | 'EXPLICIT'
  skill_key?: string | null
  provider_selection_mode?: 'DEFAULT' | 'EXPLICIT'
  provider_option_id?: string | null
  idempotency_key?: string | null
}

export interface ExpandCreativeIdeationPayload {
  canvas_node_id: string
  candidate_node_id: string
  project_id: string
  root_canvas_node_id: string
  root_run_id?: string | null
  generation_depth: number
  brief: CreativeIdeationBriefPayload
  // VISTOERA_GENERATE_CREATIVE_CANONICAL_GENERATION_CARD_UI_DIVERGE_
  // TRIPLET_AND_SINGLE_EXPANSION_V3 §22 — Diverge is now also valid on a
  // node with NO current result yet ("all three go into generating from
  // scratch"), so there is no parent candidate to snapshot; `{}` is sent
  // in that case (backend's `parent_candidate: dict[str, Any]` already
  // accepts any dict, and only forwards it to the Skill when truthy).
  parent_candidate: Partial<CreativeIdeationCandidate>
  expand_instruction?: string | null
  candidate_count?: number
  skill_selection_source?: 'AUTO' | 'EXPLICIT'
  skill_key?: string | null
  provider_selection_mode?: 'DEFAULT' | 'EXPLICIT'
  provider_option_id?: string | null
  parent_run_id?: string | null
  idempotency_key?: string | null
}

function creativeIdeationBase(workspaceId: string): string {
  return `/workspaces/${workspaceId}/creative-ideation`
}

export const generateCreativeIdeation = (workspaceId: string, payload: GenerateCreativeIdeationPayload) =>
  saasApi
    .post<CreativeIdeationRunState>(`${creativeIdeationBase(workspaceId)}/generate`, payload)
    .then((res) => res.data)

export const expandCreativeIdeation = (workspaceId: string, payload: ExpandCreativeIdeationPayload) =>
  saasApi
    .post<CreativeIdeationRunState>(`${creativeIdeationBase(workspaceId)}/expand`, payload)
    .then((res) => res.data)

export const getCreativeIdeationRun = (workspaceId: string, runId: string) =>
  saasApi
    .get<CreativeIdeationRunState>(`${creativeIdeationBase(workspaceId)}/runs/${encodeURIComponent(runId)}`)
    .then((res) => res.data)

// ---------------------------------------------------------------------
// VISTOERA_CANVAS_PROJECT_START_TO_GENERATE_CREATIVE_UNIFIED_GENERATION_
// NODE_V2 §11 — Generation History. Thin client over
// `svc.list_node_history` (no new backend table — every past Generate/
// Regenerate/Diverge dispatch for a Canvas node IS its own history row).
// ---------------------------------------------------------------------

export interface CreativeIdeationHistoryEntry {
  run_id: string
  run_kind: 'IDEATION' | 'EXPAND'
  status: CreativeIdeationRunStatus
  skill_key: string
  skill_version: string
  provider_selection_mode: 'DEFAULT' | 'EXPLICIT'
  resolved_provider_option_id: string | null
  candidate_count_requested: number
  title: string | null
  premise: string | null
  error_message: string | null
  created_at: string | null
  completed_at: string | null
}

export const getCreativeIdeationNodeHistory = (workspaceId: string, canvasNodeId: string, opts?: { runKind?: 'IDEATION' | 'EXPAND'; limit?: number }) =>
  saasApi
    .get<{ canvas_node_id: string; history: CreativeIdeationHistoryEntry[] }>(
      `${creativeIdeationBase(workspaceId)}/nodes/${encodeURIComponent(canvasNodeId)}/history`,
      { params: { run_kind: opts?.runKind, limit: opts?.limit } },
    )
    .then((res) => res.data.history)

export type CreativeCandidateFeedbackEventType = 'SELECTED' | 'REJECTED' | 'EXPANDED' | 'REGENERATED'

export const recordCreativeCandidateEvent = (
  workspaceId: string,
  payload: {
    run_id?: string | null
    candidate_id?: string | null
    event_type: CreativeCandidateFeedbackEventType
    metadata?: Record<string, unknown> | null
  },
) =>
  saasApi
    .post<{ recorded: boolean }>(`${creativeIdeationBase(workspaceId)}/candidate-events`, payload)
    .then((res) => res.data)

// ---------------------------------------------------------------------
// VISTOERA_CANVAS_SCRIPT_GENERATION_NODE_AND_CREATIVE_ASSET_CONTEXT_
// LINKING_V1 — `script_generation` Canvas node's "生成剧本" / "受控发散"
// quick action. Thin client for `saas_script_generation.py`; the node's
// own fields (Primary Creative Context snapshot / Supporting Assets /
// content type / script settings / current Script Result / Generation
// History) are plain Canvas node `data.fields`, autosaved through the
// normal full-replace Canvas save, never through this client.
// ---------------------------------------------------------------------

export const SCRIPT_GENERATION_CAPABILITY = 'script.generate'

/** Primary Creative Context snapshot — the Structured Context Asset a
 * `creative_candidate` node's own result becomes once dragged into a
 * `script_generation` node. Deliberately reuses `CreativeIdeationCandidate`'s
 * OWN field shape (never a parallel `CreativeIdea`/`CreativeArtifact`
 * schema) plus provenance. */
export interface ScriptGenerationCreativeContext {
  candidate_id?: string | null
  title: string
  premise: string
  hook?: string | null
  tone?: string | null
  story_direction?: string | null
  product_role?: string | null
  character_role?: string | null
  estimated_duration?: number | null
  tags?: string[] | null
  rationale?: string | null
}

export interface ScriptGenerationSupportingAssetRef {
  asset_id?: string | null
  asset_type: string
  display_name: string
  bound_entity_name?: string | null
  bound_entity_type?: string | null
  bound_entity_id?: string | null
  relationship?: string | null
  binding_confidence?: string | null
  binding_source?: string | null
  related_entity_name?: string | null
}

export interface ScriptEntityAssetBinding {
  entity_type: string
  entity_name: string
  entity_id?: string | null
  asset_id: string
  asset_display_name?: string | null
  relationship: string
  confidence?: string | null
  source?: string | null
  related_entity_name?: string | null
}

export interface ScriptDialogueLine {
  character: string
  line: string
}

export interface ScriptScene {
  scene_number: number
  setting: string
  duration_sec: number
  dramatic_purpose: string
  action: string
  dialogue: ScriptDialogueLine[]
  voiceover?: string | null
  product_presence?: string | null
  transition_hint?: string | null
}

export interface ScriptArtifact {
  script_id: string
  title: string
  logline: string
  synopsis: string
  direction_summary?: string | null
  target_duration_sec: number
  characters: string[]
  /** Core continuity products / props (NOT characters). */
  key_objects?: string[]
  /** Character/product ↔ upstream image identity links. */
  entity_asset_bindings?: ScriptEntityAssetBinding[] | null
  scenes: ScriptScene[]
  ending: string
  key_product_moments: string[]
  tone: string
  pacing: string
  tags?: string[] | null
}

export interface ScriptGenerationInputPayload {
  creative_context: ScriptGenerationCreativeContext
  supporting_assets?: ScriptGenerationSupportingAssetRef[] | null
  entity_asset_bindings?: ScriptEntityAssetBinding[] | null
  content_type?: string | null
  script_structure?: string | null
  generation_requirements?: string[] | null
  additional_requirements?: string | null
  target_duration_seconds?: number | null
}

export type ScriptGenerationRunStatus = 'SUBMITTING' | 'QUEUED' | 'GENERATING' | 'SUCCESS' | 'ERROR'

export interface ScriptGenerationRunState {
  run_id: string
  canvas_node_id: string
  run_kind: 'GENERATE' | 'DIVERGE'
  source_creative_node_id: string | null
  source_creative_run_id: string | null
  source_creative_candidate_id: string | null
  status: ScriptGenerationRunStatus
  skill_key: string
  skill_version: string
  skill_selection_source: string
  provider_selection_mode: 'DEFAULT' | 'EXPLICIT'
  provider_option_id: string | null
  resolved_provider_option_id: string | null
  script_count_requested: number
  scripts: ScriptArtifact[] | null
  input_snapshot: Record<string, unknown>
  error_code: string | null
  error_message: string | null
  created_at: string | null
  completed_at: string | null
}

export interface GenerateScriptGenerationPayload {
  canvas_node_id: string
  project_id: string
  input: ScriptGenerationInputPayload
  skill_selection_source?: 'AUTO' | 'EXPLICIT'
  skill_key?: string | null
  provider_selection_mode?: 'DEFAULT' | 'EXPLICIT'
  provider_option_id?: string | null
  idempotency_key?: string | null
  source_creative_node_id?: string | null
  source_creative_run_id?: string | null
  source_creative_candidate_id?: string | null
  source_creative_fingerprint?: string | null
}

export interface DivergeScriptGenerationPayload extends GenerateScriptGenerationPayload {
  parent_run_id?: string | null
}

function scriptGenerationBase(workspaceId: string): string {
  return `/workspaces/${workspaceId}/script-generation`
}

export const generateScriptGeneration = (workspaceId: string, payload: GenerateScriptGenerationPayload) =>
  saasApi
    .post<ScriptGenerationRunState>(`${scriptGenerationBase(workspaceId)}/generate`, payload)
    .then((res) => res.data)

export const divergeScriptGeneration = (workspaceId: string, payload: DivergeScriptGenerationPayload) =>
  saasApi
    .post<ScriptGenerationRunState>(`${scriptGenerationBase(workspaceId)}/diverge`, payload)
    .then((res) => res.data)

export const getScriptGenerationRun = (workspaceId: string, runId: string) =>
  saasApi
    .get<ScriptGenerationRunState>(`${scriptGenerationBase(workspaceId)}/runs/${encodeURIComponent(runId)}`)
    .then((res) => res.data)

export type ScriptGenerationFeedbackEventType =
  | 'SELECTED'
  | 'REJECTED'
  | 'EDITED'
  | 'REGENERATED'
  | 'DIVERGED'
  | 'DUPLICATED'
  | 'PUBLISHED'

export const recordScriptGenerationEvent = (
  workspaceId: string,
  payload: {
    run_id?: string | null
    script_id?: string | null
    event_type: ScriptGenerationFeedbackEventType
    metadata?: Record<string, unknown> | null
  },
) =>
  saasApi
    .post<{ recorded: boolean }>(`${scriptGenerationBase(workspaceId)}/script-events`, payload)
    .then((res) => res.data)

// ---------------------------------------------------------------------
// VISTOERA_CANVAS_STORYBOARD_GENERATION_NODE_SCRIPT_CONTEXT_AND_SHOT_
// ARTIFACT_V1 — `storyboard_generation` Canvas node.
// ---------------------------------------------------------------------

export const STORYBOARD_GENERATION_CAPABILITY = 'storyboard.generate'

export interface StoryboardGenerationScriptContext {
  script_artifact_id?: string | null
  script_id?: string | null
  title: string
  logline?: string | null
  synopsis: string
  target_duration_sec?: number | null
  characters?: string[] | null
  key_objects?: string[] | null
  key_product_moments?: string[] | null
  tags?: string[] | null
  direction_summary?: string | null
  scenes?: ScriptScene[] | null
  ending?: string | null
  tone?: string | null
  pacing?: string | null
  source_run_id?: string | null
  fingerprint?: string | null
  source_node_id?: string | null
  version?: number | null
}

export interface StoryboardGenerationSupportingAssetRef {
  asset_id?: string | null
  asset_type: string
  display_name: string
}

export type ShotCameraIntent =
  | 'STATIC'
  | 'PUSH_IN'
  | 'PULL_OUT'
  | 'PAN_LEFT'
  | 'PAN_RIGHT'
  | 'TILT'
  | 'TRACKING'
  | 'ORBIT'
  | 'HANDHELD'
  | 'DOLLY'
  | 'CUSTOM'

export interface ShotCharacterRef {
  character_id: string
  name?: string
  asset_id?: string | null
}

export interface ShotDialogueLine {
  character_id: string
  text: string
  start_time_sec?: number | null
}

export interface ShotCharacterAction {
  character_id: string
  action: string
}

export interface ShotKeyframeIntent {
  position: 'START' | 'MID' | 'END'
  time_sec: number
  description: string
}

export interface ShotContinuity {
  previous_shot_id?: string | null
  same_character_appearance?: boolean | null
  same_scene?: boolean | null
  same_product?: boolean | null
}

export interface ShotArtifact {
  shot_id: string
  shot_index: number
  start_time_sec: number
  end_time_sec: number
  duration_sec: number
  title: string
  description: string
  shot_size: string
  camera_intent: ShotCameraIntent
  camera_description?: string | null
  characters: ShotCharacterRef[]
  scene: string | { scene_id?: string | null; name: string; asset_id?: string | null }
  objects: Array<{ object_id?: string | null; name: string; asset_id?: string | null }>
  products: Array<{ product_id?: string | null; name: string; asset_id?: string | null }>
  dialogue: ShotDialogueLine[]
  character_actions: ShotCharacterAction[]
  keyframe_intents: ShotKeyframeIntent[]
  continuity: ShotContinuity
  source_script_scene_id?: string | null
  source_script_artifact_id?: string | null
  source_script_version?: number | null
  thumbnail_url?: string | null
}

export interface StoryboardArtifact {
  storyboard_artifact_id: string
  title: string
  synopsis: string
  direction_summary?: string | null
  total_duration_sec: number
  aspect_ratio: string
  /** Production target clarity (e.g. 720P / 1080P). Optional for older
   * artifacts; Video Shot cards inherit + lock when present. */
  quality_tier?: string | null
  style: string
  shots: ShotArtifact[]
}

export interface StoryboardGenerationInputPayload {
  script_context: StoryboardGenerationScriptContext
  supporting_assets?: StoryboardGenerationSupportingAssetRef[] | null
  shot_count?: number | null
  total_duration_sec?: number | null
  style_reference?: string | null
  camera_language?: string | null
  aspect_ratio?: string | null
  additional_instructions?: string | null
}

export type StoryboardGenerationRunStatus = 'SUBMITTING' | 'QUEUED' | 'GENERATING' | 'SUCCESS' | 'ERROR'

export interface StoryboardGenerationRunState {
  run_id: string
  canvas_node_id: string
  run_kind: 'GENERATE' | 'DIVERGE'
  source_script_node_id: string | null
  source_script_run_id: string | null
  source_script_artifact_id: string | null
  source_script_version?: string | null
  status: StoryboardGenerationRunStatus
  skill_key: string
  skill_version: string
  skill_selection_source: string
  provider_selection_mode: 'DEFAULT' | 'EXPLICIT'
  provider_option_id: string | null
  resolved_provider_option_id: string | null
  storyboard_count_requested: number
  storyboards: StoryboardArtifact[] | null
  input_snapshot: Record<string, unknown>
  error_code: string | null
  error_message: string | null
  created_at: string | null
  completed_at: string | null
}

export interface GenerateStoryboardGenerationPayload {
  canvas_node_id: string
  project_id: string
  input: StoryboardGenerationInputPayload
  skill_selection_source?: 'AUTO' | 'EXPLICIT'
  skill_key?: string | null
  provider_selection_mode?: 'DEFAULT' | 'EXPLICIT'
  provider_option_id?: string | null
  idempotency_key?: string | null
  source_script_node_id?: string | null
  source_script_run_id?: string | null
  source_script_artifact_id?: string | null
  source_script_version?: string | null
  source_script_fingerprint?: string | null
}

export interface DivergeStoryboardGenerationPayload extends GenerateStoryboardGenerationPayload {
  parent_run_id?: string | null
}

function storyboardGenerationBase(workspaceId: string): string {
  return `/workspaces/${workspaceId}/storyboard-generation`
}

export const generateStoryboardGeneration = (workspaceId: string, payload: GenerateStoryboardGenerationPayload) =>
  saasApi
    .post<StoryboardGenerationRunState>(`${storyboardGenerationBase(workspaceId)}/generate`, payload)
    .then((res) => res.data)

export const divergeStoryboardGeneration = (workspaceId: string, payload: DivergeStoryboardGenerationPayload) =>
  saasApi
    .post<StoryboardGenerationRunState>(`${storyboardGenerationBase(workspaceId)}/diverge`, payload)
    .then((res) => res.data)

export const getStoryboardGenerationRun = (workspaceId: string, runId: string) =>
  saasApi
    .get<StoryboardGenerationRunState>(`${storyboardGenerationBase(workspaceId)}/runs/${encodeURIComponent(runId)}`)
    .then((res) => res.data)

// ---------------------------------------------------------------------
// Generate Text (`generate_text`) — Canonical Generation Card client.
// Capability / skill / run APIs; Provider Options use TEXT_GENERATION_CAPABILITY.
// Never hardcode ChatGPT Web / pc3 worker ids in the frontend.
// ---------------------------------------------------------------------

export const TEXT_GENERATION_CAPABILITY = 'text.generation'

export interface TextGenerationSkillDescriptor {
  /** Backend catalog uses skill_id as skill_key alias for historic clients. */
  skill_id?: string
  skill_key?: string
  skill_definition_id?: string
  skill_version_id?: string
  version?: string
  skill_version?: string
  harness_version?: string
  display_name: string
  description?: string | null
  /** Normalized by client; must include image for V1 image→text path. */
  accepted_input_types: string[]
  /** Optional quick chips — only render when present; never hardcode fashion chips. */
  suggested_instructions?: string[] | null
  /** Informational only — never used to dedupe the skill list. */
  platforms?: string[] | null
  output_type?: string | null
  supports_diverge?: boolean
  is_default?: boolean
  ownership_type?: string | null
  ownership_label?: string | null
  sort_order?: number
}

export interface TextGenerationSkillsResponse {
  skills: TextGenerationSkillDescriptor[]
}

export interface TextGenerationInputAssetRef {
  asset_id?: string | null
  asset_type: string
  display_name?: string | null
  source_node_id?: string | null
  role?: string | null
  context?: string | null
}

export interface TextGenerationInputPayload {
  input_assets: TextGenerationInputAssetRef[]
  language?: string | null
  content_length?: string | null
  output_format?: string | null
  additional_instruction?: string | null
  scoped_context?: Record<string, unknown> | null
}

export type TextGenerationRunStatus =
  | 'SUBMITTING'
  | 'QUEUED'
  | 'GENERATING'
  | 'SUCCESS'
  | 'ERROR'

export interface TextGenerationResultPayload {
  text: string
  format?: string | null
  language?: string | null
  title?: string | null
  result_id?: string | null
  schema_version?: string | null
}

export interface TextGenerationRunState {
  run_id: string
  canvas_node_id: string
  run_kind?: string
  status: TextGenerationRunStatus
  skill_key: string
  skill_version: string
  skill_definition_id?: string | null
  skill_version_id?: string | null
  skill_selection_source: string
  provider_selection_mode: 'DEFAULT' | 'EXPLICIT' | string
  provider_option_id: string | null
  resolved_provider_option_id?: string | null
  output_count_requested?: number
  language?: string | null
  output_format?: string | null
  result_text?: string | null
  result?: TextGenerationResultPayload | null
  input_snapshot?: Record<string, unknown>
  error_code: string | null
  error_message: string | null
  failure_stage?: string | null
  harness_run_id?: string | null
  created_at: string | null
  completed_at: string | null
}

export interface CreateTextGenerationRunPayload {
  canvas_node_id: string
  project_id: string
  input: TextGenerationInputPayload
  skill_selection_source?: 'AUTO' | 'EXPLICIT'
  skill_key?: string | null
  skill_version?: string | null
  provider_selection_mode?: 'DEFAULT' | 'EXPLICIT'
  provider_option_id?: string | null
  idempotency_key?: string | null
}

function textGenerationBase(workspaceId: string): string {
  return `/workspaces/${workspaceId}/text-generation`
}

export const listTextGenerationSkills = (workspaceId: string, locale?: string) =>
  saasApi
    .get<TextGenerationSkillsResponse>(`${textGenerationBase(workspaceId)}/skills`, {
      params: locale ? { locale } : undefined,
    })
    .then((res) => res.data)

export const createTextGenerationRun = (workspaceId: string, payload: CreateTextGenerationRunPayload) =>
  saasApi
    .post<TextGenerationRunState>(`${textGenerationBase(workspaceId)}/generate`, payload)
    .then((res) => res.data)

export const getTextGenerationRun = (workspaceId: string, runId: string) =>
  saasApi
    .get<TextGenerationRunState>(`${textGenerationBase(workspaceId)}/runs/${encodeURIComponent(runId)}`)
    .then((res) => res.data)
